import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    FaUsers,
    FaFileContract,
    FaUserCheck,
    FaHandHoldingUsd,
    FaChartLine,
    FaPoundSign,
    FaMoneyBillWave,
    FaCalendarAlt,
    FaCheckCircle,
    FaTimesCircle,
    FaClock,
    FaFileDownload,
    FaTrashAlt,
    FaChevronDown,
    FaChevronLeft,
    FaChevronRight,
    FaPaperPlane,
    FaSearch,
    FaWrench,
    FaCircleNotch,
    FaExclamationTriangle,
    FaSyncAlt,
    FaFileExcel,
    FaUpload,
    FaUndo,
    FaClipboardCheck,
    FaCalculator,
    FaPlay,
    FaStopCircle,
    FaIdCard,
    FaCodeBranch,
    FaLink,
} from 'react-icons/fa'
import styles from './styles.module.scss'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import api from '../../../helpers/apiRequester'
import { useAuthUserData } from '../../../authUserProvider'

interface Claim {
    funder_case_ref: string
    introducer_case_ref: string
    loan_value: number
    quantum_individual: number
    defendant: string
    cfa_date: string
    client_status: string
    allocated: string
    submitted: string
    api_transferred: string
}

interface SplitPreviewClient {
    funder_client_id: number
    full_name: string
    email_address: string
    total_value: number
    claims_count: number
    lenders: string[]
    introducer?: string
}

interface SplitPreviewResponse {
    success: boolean
    batch_id: string
    canSplit: boolean
    reason?: string | null
    total_clients: number
    total_claims: number
    total_book_value: number
    lenders: string[]
    clients: SplitPreviewClient[]
    message?: string
}

const REPROCESS_DOCUMENTS = [
    { key: 'cfa', label: 'CFA (Conditional Fee Agreement)' },
    { key: 'loa', label: 'LOA (Letter of Authority)' },
    { key: 'loc', label: 'LOC (Letter of Complaint)' },
    { key: 'aof', label: 'AOF (Assignment of Funds)' },
    { key: 'trInstructionLetter', label: 'TR Instruction Letter' },
    { key: 'trForensicsReport', label: 'TR Forensics Report' },
    { key: 'esignCertificate', label: 'E-Sign Certificate' },
] as const

const ALL_REPROCESS_DOC_KEYS = REPROCESS_DOCUMENTS.map((d) => d.key)

type BundleEmailAofVariant = 'standard' | 'neradac'

const S3_ONLY_DOC_LABELS = ['Credit Report', 'AML Document', 'ATE Certificate']

interface Client {
    funder_client_id: number
    title: string
    first_name: string
    last_name: string
    full_name: string
    email_address: string
    phone_number: string
    dob: string
    address_line_1: string
    address_city: string
    address_post_code: string
    introducer: string
    total_value: number
    claims_count: number
    claims: Claim[]
}

interface Pagination {
    page: number
    limit: number
    total_clients: number
    total_claims: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
}

interface PageSummary {
    clients_on_page: number
    claims_on_page: number
    book_value_on_page: number
}

interface BatchSummary {
    total_clients: number
    total_claims: number
    total_book_value: number
    transferred_clients?: number
    transferred_claims?: number
}

interface FundingBatchDetailsResponse {
    success: boolean
    batch_id: string
    clients: Client[]
    pagination: Pagination
    page_summary: PageSummary
    batch_summary: BatchSummary
    failed_transfer_csv_url?: string | null
    ate_requested?: boolean
    ate_requested_date?: string
    ate_returned?: boolean
    submitted?: boolean | number
    api_transferred?: boolean | number
    config?: {
        commission_rate?: number | string | null
        stat_interest?: number | string | null
        max_clients?: number | string | null
        min_client_value?: number | string | null
        calculation_date?: string | null
        ate_requested?: 'Yes' | null
        ate_requested_date?: string | null
        ate_returned?: 'Yes' | null
        ate_requested_csv_link?: string | null
        ate_returned_csv_link?: string | null
        ate_skipped?: 'Yes' | null
    }
}

interface ProgressLogEntry {
    type: string
    stage: string
    percent: number
    message: string
    data?: any
    timestamp: Date
}

// Temple "import policies" return sheet vs the DB (POST /verify-ate-import).
type AteIssueKind =
    | 'ate_policy_id'
    | 'ate_policy_number'
    | 'ate_inception_date'
    | 'missing_sheet_row'
    | 'sheet_value_missing'

interface AteVerifyIssue {
    kind: AteIssueKind
    field?: string
    funderClientId: string
    claimRef?: string
    claimRefs?: string[]
    expected: string | null
    actual: string | null
    repairable: boolean
}

interface AteReturnFile {
    key: string
    canonical: boolean
    uploadedAt: string | null
    fileUrl: string
    rowCount?: number
    originalFileName?: string
}

interface AteSupplementaryRequest {
    key: string
    fileName?: string
    requestedAt: string | null
    clientCount: number | null
    claimCount: number | null
    fileUrl: string
}

interface AteVerifyReport {
    source: 's3' | 'ftp'
    backfilledS3: boolean
    filesFound: number
    files: AteReturnFile[]
    supplementaryRequests: AteSupplementaryRequest[]
    sheetRowCount: number
    batchClientCount: number
    batchRowCount: number
    matchedCount: number
    populatedWithoutSheetRow: number
    otherBatchRowCount: number
    issueCount: number
    repairable: { clientCount: number; issueCount: number }
    unrepairable: { clientCount: number; claimCount: number }
    ok: boolean
    issues: AteVerifyIssue[]
    repaired?: { references: number; claims: number; clients: number }
}

const ATE_ISSUE_LABELS: Record<AteIssueKind, string> = {
    ate_policy_id: 'Policy ID (funder_references)',
    ate_policy_number: 'Policy number (funder_claims)',
    ate_inception_date: 'Inception date (funder_claims)',
    missing_sheet_row: 'Not on Temple sheet',
    sheet_value_missing: 'Blank on Temple sheet',
}

interface AuditClaimPreview {
    funder_case_ref: string
    introducer_case_ref: string
    law_firm_secondary_reference: string
    loan_value: number
    quantum_individual: number
    defendant: string
    cfa_date: string
    client_status: string
}

interface AuditClientPreview {
    funder_client_id: number
    title: string
    first_name: string
    last_name: string
    email_address: string
    dob: string
    claims: AuditClaimPreview[]
    claimsCount: number
    totalValue: number
}

interface AuditPartialClaim extends AuditClaimPreview {
    funder_client_id: number
    clientName: string
}

interface AuditSummary {
    totalBatchClaims: number
    totalActiveClaims: number
    auditedClaims: number
    unauditedClaims: number
    alreadyRemoved: number
    clientsToFullyRemove: number
    partialClaimsToRemove: number
    auditFileReferences: number
    addressColumnsDetected?: string[]
    addressChanges?: number
    addressUnmatchedRows?: number
}

interface AuditAddressFieldChange {
    field: string
    label: string
    current: string | null
    proposed: string
    conflictingValues: string[]
}

interface AuditAddressChange {
    funder_client_id: number
    clientName: string
    email_address: string | null
    sourceRef: string
    currentAddress: Record<string, string | null>
    changes: AuditAddressFieldChange[]
    hasConflicts: boolean
}

interface AuditAddressUpdate {
    funder_client_id: number
    fields: Record<string, string>
}

interface AuditPreview {
    summary: AuditSummary
    clientsToFullyRemove: AuditClientPreview[]
    partialClaimsToRemove: AuditPartialClaim[]
    allCaseRefsToRemove: string[]
    addressChanges?: AuditAddressChange[]
    allAddressUpdates?: AuditAddressUpdate[]
}

interface RemovedClaim extends AuditClaimPreview {
    audit_removed_date: string
}

interface RemovedClient {
    funder_client_id: number
    title: string
    first_name: string
    last_name: string
    email_address: string
    dob: string
    claims: RemovedClaim[]
    claimsCount: number
    totalValue: number
}

interface PurgeSkippedClient {
    funderClientId: number
    name: string | null
    email: string | null
    introducer?: string | null
    reason: string
}

interface ResetMembershipPreview {
    batchId: string
    introducer: string
    processed: string | null
    ateRequested: boolean
    ateReturned: boolean
    current: { references: number; withoutPack: number; transferred: number; clients: number }
    flagged: { count: number; byReason: { reason: string; count: number }[] }
    missing: { claims: number; clients: number; withoutPack: number }
    orphaned: {
        claims: number
        clients: number
        recoverableClients: number
        recoverableClaims: number
        unrecoverable: { funderClientId: number; claims: number; reason: string }[]
    }
    after: { references: number; withoutPack: number }
}

interface PurgePreview {
    clientCount: number
    claimCount: number
    funding: {
        funder_references: number
        funder_claims: number
        funder_clients: number
    }
    introducerTableCounts: Record<string, {
        clients: number
        leads: number
        lead_states: number
        signature_links: number
        documents: number
    }>
    skippedPartialClients: {
        funderClientId: number
        name: string | null
        email: string | null
        removedClaims: number
        activeClaims: number
    }[]
    skippedIntroducerClients: PurgeSkippedClient[]
    unsupportedIntroducers: string[]
}

interface EmailCsvPurgePreview {
    success: boolean
    message?: string
    preview?: boolean
    requestedEmails?: number
    matchedClients?: number
    claimCount?: number
    transferredClaimCount?: number
    bundleKeyCount?: number
    unmatchedCount?: number
    unmatchedEmails?: string[]
    sharedFunderClients?: number
    trinityUnchanged?: boolean
    clients?: {
        funderClientId: number
        email: string
        name: string | null
        claims: number
        transferredClaims: number
        funderClientShared: boolean
    }[]
    deletionResults?: Record<string, number>
    s3?: { attempted?: number; deleted?: number; failed?: { key: string; error: string }[] }
    recalculation?: { remainingClaims?: number; clientsUpdated?: number }
    perEmail?: { email: string; status: string; claims?: number; transferredClaims?: number }[]
}

interface RelinkIntroducerIdsRow {
    email: string
    status: string
    introducer?: string | null
    funderClientId?: number | null
    currentIntroducerClientId?: number | null
    targetIntroducerClientId?: number | null
    name?: string | null
    error?: string
}

interface RelinkIntroducerIdsPreview {
    success: boolean
    message?: string
    preview?: boolean
    requestedEmails?: number
    willRelinkCount?: number
    alreadyLinkedCount?: number
    skippedCount?: number
    relinkedCount?: number
    failedCount?: number
    fundingMonitorUnchanged?: boolean
    counts?: Record<string, number>
    perEmail?: RelinkIntroducerIdsRow[]
}

const RELINK_STATUS_LABELS: Record<string, string> = {
    will_relink: 'Will relink',
    relinked: 'Relinked',
    already_linked: 'Already linked',
    not_in_batch: 'Not in this batch',
    not_in_introducer: 'Not found in introducer schema',
    duplicate_email: 'Duplicate email in introducer schema',
    ambiguous_funding: 'Ambiguous funding IDs',
    target_id_taken: 'Target ID already in use',
    unsupported_introducer: 'Introducer not supported',
    failed: 'Failed',
}

type ProcessedState = 'Initial' | 'Started' | 'Complete' | 'Complete with errors'

interface BatchDiagnostics {
    processed: ProcessedState
    submissionCsvUrl: string | null
    submissionExcelUrl: string | null
    failureReportUrl: string | null
    orphanedClients: number
    processedRecords?: number
    totalRecords?: number
}

interface FailureBreakdownCase {
    case_ref: string
    docName: string | null
    error: string
}

interface FailureBreakdownCategory {
    category: string
    count: number
    cases: FailureBreakdownCase[]
}

interface FailureBreakdown {
    reportAvailable: boolean
    totalRecords: number
    successCount: number
    failedCount: number
    categories: FailureBreakdownCategory[]
}

export const FundingBatchDetailsView = () => {
    const { batchId } = useParams<{ batchId: string }>()
    const navigate = useNavigate()
    const authUser = useAuthUserData()
    const isScoreUser = authUser?.['custom:organisation'] === 'Score'
    const [batchDetails, setBatchDetails] = useState<FundingBatchDetailsResponse | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [processing, setProcessing] = useState<boolean>(false)
    const [downloadingCSV, setDownloadingCSV] = useState<boolean>(false)
    const [downloadingAtePolicies, setDownloadingAtePolicies] = useState<boolean>(false)
    const [downloadingRemovedExcel, setDownloadingRemovedExcel] = useState<boolean>(false)
    const [requestingATE, setRequestingATE] = useState<boolean>(false)
    const [reRequestingATE, setReRequestingATE] = useState<boolean>(false)
    const [updatingAteSkip, setUpdatingAteSkip] = useState<boolean>(false)
    const [markingAteReturned, setMarkingAteReturned] = useState<boolean>(false)
    const [repairingBatch, setRepairingBatch] = useState<boolean>(false)
    const [repairRemaining, setRepairRemaining] = useState<number | null>(null)
    const [stoppingRepair, setStoppingRepair] = useState<boolean>(false)
    const repairPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [stoppingProcessing, setStoppingProcessing] = useState<boolean>(false)
    const allocationPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [skippingRepair, setSkippingRepair] = useState<boolean>(false)
    const [regeneratingSheet, setRegeneratingSheet] = useState<boolean>(false)
    const [collectingAteCertificates, setCollectingAteCertificates] = useState<boolean>(false)
    const [ateRequested, setAteRequested] = useState<boolean>(false)
    // Temple return sheet verification / repair / re-request
    const [ateVerifyReport, setAteVerifyReport] = useState<AteVerifyReport | null>(null)
    const [verifyingAteImport, setVerifyingAteImport] = useState<boolean>(false)
    const [repairingAteImport, setRepairingAteImport] = useState<boolean>(false)
    const [reRequestingMissingAte, setReRequestingMissingAte] = useState<boolean>(false)
    const [downloadingTempleCsv, setDownloadingTempleCsv] = useState<boolean>(false)
    const [ateReturnFiles, setAteReturnFiles] = useState<AteReturnFile[]>([])
    const [ateSupplementaryRequests, setAteSupplementaryRequests] = useState<AteSupplementaryRequest[]>([])
    const [ateIssuesExpanded, setAteIssuesExpanded] = useState<boolean>(false)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [limit] = useState<number>(25)
    const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set())
    const [progress, setProgress] = useState<{
        percent: number
        message: string
        stage: string
    } | null>(null)
    const [progressLog, setProgressLog] = useState<ProgressLogEntry[]>([])
    const [isProgressLogExpanded, setIsProgressLogExpanded] = useState<boolean>(false)
    const [processingResult, setProcessingResult] = useState<any>(null)
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
    const [deleteConfirmText, setDeleteConfirmText] = useState<string>('')
    const [isDeleting, setIsDeleting] = useState<boolean>(false)
    const [actionsDropdownOpen, setActionsDropdownOpen] = useState<boolean>(false)
    const [showTransferModal, setShowTransferModal] = useState<boolean>(false)
    type TransferMode = 'percentage' | 'count' | 'all' | 'remaining'
    const [transferMode, setTransferMode] = useState<TransferMode>('percentage')
    const [transferPercentage, setTransferPercentage] = useState<number>(20)
    const [transferClientCount, setTransferClientCount] = useState<number>(10)
    const [isTransferring, setIsTransferring] = useState<boolean>(false)
    const [transferResultModal, setTransferResultModal] = useState<{
        show: boolean
        success: boolean
        message: string
        transferredCount?: number
        failedCount?: number
        failedCsvPresignedUrl?: string | null
    }>({ show: false, success: false, message: '' })
    type SplitMode = 'auto' | 'manual'
    const [showSplitModal, setShowSplitModal] = useState<boolean>(false)
    const [splitMode, setSplitMode] = useState<SplitMode>('auto')
    const [splitClientCount, setSplitClientCount] = useState<number>(1)
    const [splitSelectedIds, setSplitSelectedIds] = useState<Set<number>>(new Set())
    const [splitPreview, setSplitPreview] = useState<SplitPreviewResponse | null>(null)
    const [splitPreviewLoading, setSplitPreviewLoading] = useState<boolean>(false)
    const [isSplitting, setIsSplitting] = useState<boolean>(false)
    const [splitLenderFilter, setSplitLenderFilter] = useState<string>('')
    const [splitMinValue, setSplitMinValue] = useState<string>('')
    const [splitMaxValue, setSplitMaxValue] = useState<string>('')
    const [splitMinClaims, setSplitMinClaims] = useState<string>('')
    const [splitMaxClaims, setSplitMaxClaims] = useState<string>('')
    const [splitSearch, setSplitSearch] = useState<string>('')
    const actionsDropdownRef = useRef<HTMLDivElement>(null)
    const [clientSearchQuery, setClientSearchQuery] = useState<string>('')
    const [clientSearchResults, setClientSearchResults] = useState<
        { client_id: number; name: string; email_address: string; dob: string; batch_group: string; funder_case_refs: string | null; law_firm_secondary_refs: string | null; funder_claim_ids: string | null }[]
    >([])
    const [clientSearchLoading, setClientSearchLoading] = useState<boolean>(false)
    const [clientSearchDropdownOpen, setClientSearchDropdownOpen] = useState<boolean>(false)
    const clientSearchRef = useRef<HTMLDivElement>(null)
    const [batchDiagnostics, setBatchDiagnostics] = useState<BatchDiagnostics | null>(null)
    const [diagnosticsLoading, setDiagnosticsLoading] = useState<boolean>(false)
    const [amlSummary, setAmlSummary] = useState<{
        applicable: boolean
        totalRecords: number
        eligibleCount: number
        amlCount: number
    } | null>(null)
    const [amlSummaryLoading, setAmlSummaryLoading] = useState<boolean>(false)
    const [downloadingAmlReport, setDownloadingAmlReport] = useState<boolean>(false)
    const [failureBreakdown, setFailureBreakdown] = useState<FailureBreakdown | null>(null)
    const [failureBreakdownLoading, setFailureBreakdownLoading] = useState<boolean>(false)
    const [expandedFailureCategories, setExpandedFailureCategories] = useState<Set<string>>(new Set())
    const [batchProcessingOpen, setBatchProcessingOpen] = useState<boolean>(true)
    const [ateStatusOpen, setAteStatusOpen] = useState<boolean>(true)
    const [batchSummaryOpen, setBatchSummaryOpen] = useState<boolean>(true)
    const [clientsSectionOpen, setClientsSectionOpen] = useState<boolean>(true)
    const [timelineCarouselIndex, setTimelineCarouselIndex] = useState<number>(0)

    const [auditSectionOpen, setAuditSectionOpen] = useState<boolean>(false)
    const [auditFile, setAuditFile] = useState<File | null>(null)
    const [auditChecking, setAuditChecking] = useState<boolean>(false)
    const [auditPreview, setAuditPreview] = useState<AuditPreview | null>(null)
    const [auditRemoving, setAuditRemoving] = useState<boolean>(false)
    const [auditResult, setAuditResult] = useState<{ success: boolean; message: string } | null>(null)
    const [removedSectionOpen, setRemovedSectionOpen] = useState<boolean>(false)
    const [removedClients, setRemovedClients] = useState<RemovedClient[]>([])
    const [removedTotalClaims, setRemovedTotalClaims] = useState<number>(0)
    const [removedSearchQuery, setRemovedSearchQuery] = useState<string>('')
    const [removedLoading, setRemovedLoading] = useState<boolean>(false)
    const [removedPage, setRemovedPage] = useState<number>(1)
    const [removedPagination, setRemovedPagination] = useState<{
        page: number; limit: number; totalClients: number; totalPages: number; hasNext: boolean; hasPrev: boolean
    } | null>(null)
    const removedLimit = 25
    const [restoringRefs, setRestoringRefs] = useState<Set<string>>(new Set())
    const [removalReasons, setRemovalReasons] = useState<{ reason: string; count: number }[]>([])
    const [selectedRevertReason, setSelectedRevertReason] = useState<string>('')
    const [revertingByType, setRevertingByType] = useState<boolean>(false)
    const [expandedAuditClients, setExpandedAuditClients] = useState<Set<number>>(new Set())
    const [auditClientsListOpen, setAuditClientsListOpen] = useState<boolean>(false)
    const [auditPartialListOpen, setAuditPartialListOpen] = useState<boolean>(false)
    const [auditAddressListOpen, setAuditAddressListOpen] = useState<boolean>(false)
    const [auditApplyAddressUpdates, setAuditApplyAddressUpdates] = useState<boolean>(true)
    const [expandedRemovedClients, setExpandedRemovedClients] = useState<Set<number>>(new Set())
    const auditFileInputRef = useRef<HTMLInputElement>(null)
    const [resettingTransfer, setResettingTransfer] = useState<boolean>(false)
    const [showPurgeModal, setShowPurgeModal] = useState<boolean>(false)
    const [purgePreview, setPurgePreview] = useState<PurgePreview | null>(null)
    const [purgePreviewLoading, setPurgePreviewLoading] = useState<boolean>(false)
    const [purgeConfirmText, setPurgeConfirmText] = useState<string>('')
    const [isPurging, setIsPurging] = useState<boolean>(false)
    const [showResetMembershipModal, setShowResetMembershipModal] = useState<boolean>(false)
    const [resetMembershipPreview, setResetMembershipPreview] = useState<ResetMembershipPreview | null>(null)
    const [resetMembershipPreviewLoading, setResetMembershipPreviewLoading] = useState<boolean>(false)
    const [resetMembershipConfirmText, setResetMembershipConfirmText] = useState<string>('')
    const [isResettingMembership, setIsResettingMembership] = useState<boolean>(false)

    const [showAllocateModal, setShowAllocateModal] = useState<boolean>(false)
    const [allocateResume, setAllocateResume] = useState<boolean>(false)
    const [allocateDocKeys, setAllocateDocKeys] = useState<string[]>([...ALL_REPROCESS_DOC_KEYS])
    const [showReprocessModal, setShowReprocessModal] = useState<boolean>(false)
    const [reprocessDocKeys, setReprocessDocKeys] = useState<string[]>([...ALL_REPROCESS_DOC_KEYS])
    const [sampleSelectedClaim, setSampleSelectedClaim] = useState<{
        key: string
        label: string
        introducer: string
        introducer_case_ref: string
    } | null>(null)
    const [sampleSearchQuery, setSampleSearchQuery] = useState<string>('')
    const [sampleSearchResults, setSampleSearchResults] = useState<
        { key: string; label: string; introducer: string; introducer_case_ref: string }[]
    >([])
    const [sampleSearchLoading, setSampleSearchLoading] = useState<boolean>(false)
    const [sampleSearchDropdownOpen, setSampleSearchDropdownOpen] = useState<boolean>(false)
    const sampleSearchRef = useRef<HTMLDivElement>(null)
    const [sampleRunning, setSampleRunning] = useState<boolean>(false)
    const [sampleResult, setSampleResult] = useState<{
        bundleUrl?: string | null
        case_ref?: string
        documentSources?: { docName: string; source: 'regenerated' | 'downloaded' }[]
        message?: string
    } | null>(null)

    const [bundleEmailSectionOpen, setBundleEmailSectionOpen] = useState<boolean>(false)
    const [bundleEmailFile, setBundleEmailFile] = useState<File | null>(null)
    const [bundleEmailDocKeys, setBundleEmailDocKeys] = useState<string[]>([...ALL_REPROCESS_DOC_KEYS])
    const [bundleEmailAofVariant, setBundleEmailAofVariant] = useState<BundleEmailAofVariant>('standard')
    const [regeneratingBundles, setRegeneratingBundles] = useState<boolean>(false)
    const [regenBundlesResult, setRegenBundlesResult] = useState<{
        success: boolean
        message: string
        emailsProvided?: number
        emailsMatched?: number
        emailsNotFound?: string[]
        bundlesRegenerated?: number
        bundlesFailed?: number
        failures?: { caseRef?: string; funderCaseRef?: string; email?: string; error?: string }[]
        regenerateDocs?: string[]
    } | null>(null)
    const bundleEmailFileInputRef = useRef<HTMLInputElement>(null)

    const [emailPurgeSectionOpen, setEmailPurgeSectionOpen] = useState<boolean>(false)
    const [emailPurgeFile, setEmailPurgeFile] = useState<File | null>(null)
    const [emailPurgePreview, setEmailPurgePreview] = useState<EmailCsvPurgePreview | null>(null)
    const [emailPurgeResult, setEmailPurgeResult] = useState<EmailCsvPurgePreview | null>(null)
    const [emailPurgePreviewLoading, setEmailPurgePreviewLoading] = useState<boolean>(false)
    const [showEmailPurgeModal, setShowEmailPurgeModal] = useState<boolean>(false)
    const [emailPurgeConfirmText, setEmailPurgeConfirmText] = useState<string>('')
    const [isEmailPurging, setIsEmailPurging] = useState<boolean>(false)
    const emailPurgeFileInputRef = useRef<HTMLInputElement>(null)

    const [relinkIdsSectionOpen, setRelinkIdsSectionOpen] = useState<boolean>(false)
    const [relinkIdsFile, setRelinkIdsFile] = useState<File | null>(null)
    const [relinkIdsPreview, setRelinkIdsPreview] = useState<RelinkIntroducerIdsPreview | null>(null)
    const [relinkIdsResult, setRelinkIdsResult] = useState<RelinkIntroducerIdsPreview | null>(null)
    const [relinkIdsPreviewLoading, setRelinkIdsPreviewLoading] = useState<boolean>(false)
    const [showRelinkIdsModal, setShowRelinkIdsModal] = useState<boolean>(false)
    const [relinkIdsConfirmText, setRelinkIdsConfirmText] = useState<string>('')
    const [isRelinkingIds, setIsRelinkingIds] = useState<boolean>(false)
    const relinkIdsFileInputRef = useRef<HTMLInputElement>(null)

    const [postAuditOpen, setPostAuditOpen] = useState<boolean>(false)
    const [maintenanceToolsOpen, setMaintenanceToolsOpen] = useState<boolean>(false)
    const [alterationType, setAlterationType] = useState<string>('tr-credit-report-mismatch')
    const [alterationFile, setAlterationFile] = useState<File | null>(null)
    const [alterationPreview, setAlterationPreview] = useState<any>(null)
    const [alterationPreviewing, setAlterationPreviewing] = useState<boolean>(false)
    const [alterationProcessing, setAlterationProcessing] = useState<boolean>(false)
    const [alterationResult, setAlterationResult] = useState<any>(null)
    const [alterationReprocessing, setAlterationReprocessing] = useState<boolean>(false)
    const [alterationReprocessResult, setAlterationReprocessResult] = useState<any>(null)
    const [alterationExporting, setAlterationExporting] = useState<boolean>(false)
    const alterationFileInputRef = useRef<HTMLInputElement>(null)

    const getTimelineVisibleCount = useCallback(() => {
        if (typeof window === 'undefined') return 6
        const w = window.innerWidth
        if (w <= 620) return 1
        if (w <= 1024) return 3
        if (w <= 1150) return 5
        return 6
    }, [])
    const [timelineVisibleCount, setTimelineVisibleCount] = useState<number>(getTimelineVisibleCount)
    useEffect(() => {
        const updateVisible = () => setTimelineVisibleCount(getTimelineVisibleCount())
        updateVisible()
        window.addEventListener('resize', updateVisible)
        return () => window.removeEventListener('resize', updateVisible)
    }, [getTimelineVisibleCount])

    const handleSessionExpired = useCallback(
        (error: any): boolean => {
            if (error?.response?.status === 401 || error?.status === 401) {
                Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return true
            }
            return false
        },
        [navigate]
    )

    const fetchBatchDetails = useCallback(async () => {
        try {
            setLoading(true)

            // Fetch batch details
            const detailsResponse = await api({
                url: '/funding-batch-details',
                data: {
                    batchId: batchId,
                    page: currentPage,
                    limit: limit,
                },
            })

            // Fetch funding batches to get ATE status from config
            const batchesResponse = await api({
                url: '/funding-batches',
                data: {},
            })

            if (detailsResponse.data && detailsResponse.data.success) {
                let batchDetailsData = detailsResponse.data

                // Get ATE status and processing status from funding-batches endpoint if available
                if (batchesResponse.data && batchesResponse.data.success && batchesResponse.data.batches) {
                    const batchFromList = batchesResponse.data.batches.find((b: any) => b.batch_id === batchId)

                    if (batchFromList) {
                        // Merge ATE status, processing status, CSV links, and config from batches endpoint
                        batchDetailsData = {
                            ...batchDetailsData,
                            submitted: batchFromList.submitted,
                            api_transferred: batchFromList.api_transferred,
                            ate_requested: batchFromList.config?.ate_requested === 'Yes',
                            ate_requested_date: batchFromList.config?.ate_requested_date,
                            ate_returned: batchFromList.config?.ate_returned === 'Yes',
                            config: {
                                commission_rate: batchFromList.config?.commission_rate,
                                stat_interest: batchFromList.config?.stat_interest,
                                max_clients: batchFromList.config?.max_clients,
                                min_client_value: batchFromList.config?.min_client_value,
                                calculation_date: batchFromList.config?.calculation_date,
                                ate_requested: batchFromList.config?.ate_requested,
                                ate_requested_date: batchFromList.config?.ate_requested_date,
                                ate_returned: batchFromList.config?.ate_returned,
                                ate_requested_csv_link: batchFromList.config?.ate_requested_csv_link,
                                ate_returned_csv_link: batchFromList.config?.ate_returned_csv_link,
                                ate_skipped: batchFromList.config?.ate_skipped,
                            },
                        }
                    }
                }

                setBatchDetails(batchDetailsData)
                // Check if ATE has been requested
                if (batchDetailsData.ate_requested !== undefined) {
                    setAteRequested(batchDetailsData.ate_requested)
                }
            } else {
                Report.failure('Error', detailsResponse.data?.message || 'Failed to fetch batch details', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error fetching batch details:', error)
            Report.failure('Error', error.message || 'Failed to fetch batch details', 'OKAY')
        } finally {
            setLoading(false)
        }
    }, [batchId, currentPage, limit, handleSessionExpired])

    useEffect(() => {
        if (batchId) {
            fetchBatchDetails()
        }
    }, [batchId, fetchBatchDetails])

    const fetchBatchDiagnostics = useCallback(async () => {
        if (!batchId) return
        setDiagnosticsLoading(true)
        try {
            const response = await api({
                url: '/batch-diagnostics',
                data: { batchGroup: batchId },
            })
            if (response?.data?.success && response.data.processed != null) {
                setBatchDiagnostics({
                    processed: response.data.processed as ProcessedState,
                    submissionCsvUrl: response.data.submissionCsvUrl ?? null,
                    submissionExcelUrl: response.data.submissionExcelUrl ?? null,
                    failureReportUrl: response.data.failureReportUrl ?? null,
                    orphanedClients: response.data.orphanedClients ?? 0,
                    processedRecords: response.data.processedRecords ?? undefined,
                    totalRecords: response.data.totalRecords ?? undefined,
                })
            } else {
                setBatchDiagnostics({
                    processed: 'Initial',
                    submissionCsvUrl: null,
                    submissionExcelUrl: null,
                    failureReportUrl: null,
                    orphanedClients: 0,
                })
            }
        } catch (err) {
            if (handleSessionExpired(err as any)) return
            console.error('Error fetching batch diagnostics:', err)
        } finally {
            setDiagnosticsLoading(false)
        }
    }, [batchId, handleSessionExpired])

    useEffect(() => {
        if (batchId && batchDetails) {
            fetchBatchDiagnostics()
        }
    }, [batchId, batchDetails?.batch_id, fetchBatchDiagnostics])

    // Temple return sheets already in S3 for this batch (cheap HEAD/LIST — no
    // FTP on page load). Drives the Downloads card; the full compare is on demand.
    const fetchAteReturnFiles = useCallback(async () => {
        if (!batchId) return
        try {
            const response = await api({
                url: '/verify-ate-import',
                data: { batchId, locateOnly: true, allowFtp: false },
            })
            if (response?.data?.success) {
                setAteReturnFiles(response.data.files ?? [])
                setAteSupplementaryRequests(response.data.supplementaryRequests ?? [])
            }
        } catch (err) {
            if (handleSessionExpired(err as any)) return
            console.error('Error locating Temple return files:', err)
        }
    }, [batchId, handleSessionExpired])

    useEffect(() => {
        if (batchId && batchDetails) {
            fetchAteReturnFiles()
        }
    }, [batchId, batchDetails?.batch_id, fetchAteReturnFiles])

    // AML identity documents (fairclaim): count of cases in the batch with an
    // attached AML report in S3. Not applicable for other introducers - the
    // backend answers instantly then and the card stays hidden.
    const fetchAmlSummary = useCallback(async () => {
        if (!batchId) return
        setAmlSummaryLoading(true)
        try {
            const response = await api({
                url: '/funding-batch-aml-summary',
                data: { batchGroup: batchId },
            })
            if (response?.data?.success) {
                setAmlSummary({
                    applicable: response.data.applicable ?? false,
                    totalRecords: response.data.totalRecords ?? 0,
                    eligibleCount: response.data.eligibleCount ?? 0,
                    amlCount: response.data.amlCount ?? 0,
                })
            }
        } catch (err) {
            if (handleSessionExpired(err as any)) return
            console.error('Error fetching AML document summary:', err)
        } finally {
            setAmlSummaryLoading(false)
        }
    }, [batchId, handleSessionExpired])

    useEffect(() => {
        if (batchId && batchDetails) {
            fetchAmlSummary()
        }
    }, [batchId, batchDetails?.batch_id, fetchAmlSummary])

    const handleDownloadAmlReport = async () => {
        if (!batchId) return
        setDownloadingAmlReport(true)
        try {
            const response = await api({
                url: '/funding-batch-aml-report',
                data: { batchGroup: batchId },
            })
            const data = response?.data
            if (data?.success && data.downloadUrl) {
                window.open(data.downloadUrl, '_blank', 'noopener,noreferrer')
            } else {
                Report.failure(
                    'AML report',
                    data?.message || 'Failed to generate the AML claims report.',
                    'OKAY'
                )
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error downloading AML report:', error)
            Report.failure(
                'AML report error',
                error?.response?.data?.message || error?.message || 'Failed to download the AML claims report',
                'OKAY'
            )
        } finally {
            setDownloadingAmlReport(false)
        }
    }

    const fetchFailureBreakdown = useCallback(async () => {
        if (!batchId) return
        setFailureBreakdownLoading(true)
        try {
            const response = await api({
                url: '/batch-failure-breakdown',
                data: { batchGroup: batchId },
            })
            if (response?.data?.success) {
                setFailureBreakdown({
                    reportAvailable: response.data.reportAvailable ?? false,
                    totalRecords: response.data.totalRecords ?? 0,
                    successCount: response.data.successCount ?? 0,
                    failedCount: response.data.failedCount ?? 0,
                    categories: response.data.categories ?? [],
                })
            }
        } catch (err) {
            if (handleSessionExpired(err as any)) return
            console.error('Error fetching batch failure breakdown:', err)
        } finally {
            setFailureBreakdownLoading(false)
        }
    }, [batchId, handleSessionExpired])

    // Refresh the failure breakdown whenever diagnostics report the batch as
    // Complete with errors (including after a repair attempt re-fetches diagnostics)
    useEffect(() => {
        if (batchDiagnostics?.processed === 'Complete with errors') {
            fetchFailureBreakdown()
        }
    }, [batchDiagnostics, fetchFailureBreakdown])

    // On page load, re-attach to a background run already in progress (all
    // bundle runs execute server-side as detached processes and survive
    // navigation/refresh). Repair runs drive the repair UI; allocation and
    // re-process runs drive the processing overlay.
    useEffect(() => {
        if (!batchId) return
        let cancelled = false
        const checkExistingRun = async () => {
            try {
                const response = await api({
                    url: '/bundle-batch-status',
                    data: { batchGroup: batchId },
                })
                const data = response?.data
                if (cancelled || !data?.success || data.state !== 'running') return
                if (data.mode === 'repair') {
                    setRepairingBatch(true)
                    setRepairRemaining(data.brokenRemaining ?? null)
                    repairPollTimerRef.current = setTimeout(pollRepairStatus, 5000)
                } else {
                    setProcessing(true)
                    setProgress({
                        percent: 1,
                        message: 'Re-attached to the processing run already in progress...',
                        stage: 'documents',
                    })
                    allocationPollTimerRef.current = setTimeout(pollBundleRunStatus, 2000)
                }
            } catch {
                // No run info available — nothing to attach to
            }
        }
        checkExistingRun()
        return () => {
            cancelled = true
            if (repairPollTimerRef.current) {
                clearTimeout(repairPollTimerRef.current)
                repairPollTimerRef.current = null
            }
            if (allocationPollTimerRef.current) {
                clearTimeout(allocationPollTimerRef.current)
                allocationPollTimerRef.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchId])

    const toggleFailureCategory = (category: string) => {
        setExpandedFailureCategories((prev) => {
            const next = new Set(prev)
            if (next.has(category)) {
                next.delete(category)
            } else {
                next.add(category)
            }
            return next
        })
    }

    const handleRequestATE = async () => {
        if (!batchId) return

        setRequestingATE(true)
        try {
            const response = await api({
                url: '/request-ate',
                data: {
                    batchId: batchId,
                },
            })

            if (response.data && response.data.success) {
                // Show the message from the response
                const message = response.data.message || 'ATE requested successfully'
                Report.success('Success', message, 'OKAY')
                // Immediately update ATE status to show progress
                setAteRequested(true)
                // Update batch details with the new ATE status
                if (batchDetails) {
                    setBatchDetails({
                        ...batchDetails,
                        ate_requested: true,
                        ate_requested_date: new Date().toISOString(),
                    })
                }
                // Refresh batch details to get updated ATE status from server
                await fetchBatchDetails()
            } else {
                Report.failure('Request Error', response.data?.message || 'Failed to request ATE', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error requesting ATE:', error)
            Report.failure('Request Error', error.message || 'Failed to request ATE', 'OKAY')
        } finally {
            setRequestingATE(false)
        }
    }

    const handleReRequestATE = () => {
        if (!batchId) return

        Confirm.show(
            'Re-request ATE',
            'This will delete all existing ATE data for this batch (certificates, policy numbers, provider info) and regenerate the ATE submission sheet. This cannot be undone. Continue?',
            'Yes, Re-request',
            'Cancel',
            async () => {
                setReRequestingATE(true)
                try {
                    const response = await api({
                        url: '/re-request-ate',
                        data: { batchId },
                    })

                    if (response.data && response.data.success) {
                        Report.success('Success', response.data.message || 'ATE data cleared and re-requested', 'OKAY')
                        setAteRequested(true)
                        if (batchDetails) {
                            setBatchDetails({
                                ...batchDetails,
                                ate_requested: true,
                                ate_requested_date: new Date().toISOString(),
                                ate_returned: false,
                            })
                        }
                        await fetchBatchDetails()
                    } else {
                        Report.failure('Error', response.data?.message || 'Failed to re-request ATE', 'OKAY')
                    }
                } catch (error: any) {
                    if (handleSessionExpired(error)) return
                    console.error('Error re-requesting ATE:', error)
                    Report.failure('Error', error.message || 'Failed to re-request ATE', 'OKAY')
                } finally {
                    setReRequestingATE(false)
                }
            }
        )
    }

    /**
     * ATE is an optional stage. Skipping it lets the batch be processed and
     * submitted without certificates; the ATE run can happen later, followed by
     * a re-process to pull the certificates into the existing bundles.
     */
    const handleSetAteSkipped = (skipped: boolean) => {
        if (!batchId) return

        const confirmTitle = skipped ? 'Skip ATE stage' : 'Require ATE stage'
        const confirmMessage = skipped
            ? 'The batch will be processed without ATE certificates — document bundles will be built without them. You can request ATE later and re-process the batch to include the certificates. Continue?'
            : 'ATE certificates will be required again, so processing will fail for any claim without one. Continue?'

        Confirm.show(confirmTitle, confirmMessage, skipped ? 'Yes, skip ATE' : 'Yes, require ATE', 'Cancel', async () => {
            setUpdatingAteSkip(true)
            try {
                const response = await api({
                    url: '/skip-ate',
                    data: { batchId, skipped },
                })

                if (response.data && response.data.success) {
                    Report.success('Success', response.data.message || 'ATE stage updated', 'OKAY')
                    await fetchBatchDetails()
                } else {
                    Report.failure('Error', response.data?.message || 'Failed to update the ATE stage', 'OKAY')
                }
            } catch (error: any) {
                if (handleSessionExpired(error)) return
                console.error('Error updating ATE skip state:', error)
                Report.failure(
                    'Error',
                    error?.response?.data?.message || error?.message || 'Failed to update the ATE stage',
                    'OKAY'
                )
            } finally {
                setUpdatingAteSkip(false)
            }
        })
    }

    /**
     * Temple's collection run only flags ATE as returned when every row on the
     * return sheet has a certificate number and every PDF arrived. When a few
     * clients come back blank the batch is stuck even though the rest of the
     * certificates are in S3. This previews the gap (clients with no policy
     * number) and, on confirmation, marks the stage returned so ATE insertion
     * and the funder transfer can proceed for everyone else.
     */
    const handleMarkAteReturned = async () => {
        if (!batchId) return

        setMarkingAteReturned(true)
        let preview: any = null
        try {
            const response = await api({ url: '/mark-ate-returned', data: { batchId } })
            if (!response.data?.success) {
                Report.failure('Mark ATE as returned', response.data?.message || 'Could not check the batch', 'OKAY')
                return
            }
            preview = response.data
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure(
                'Mark ATE as returned',
                error?.response?.data?.message || error?.message || 'Could not check the batch',
                'OKAY'
            )
            return
        } finally {
            setMarkingAteReturned(false)
        }

        const missing: number = preview.clientsWithoutPolicy ?? 0
        const total: number = preview.totalClients ?? 0
        const ids: string[] = (preview.clientsWithoutPolicyList ?? []).map((c: any) => String(c.funderClientId))
        const idSample = ids.slice(0, 15).join(', ') + (ids.length > 15 ? `, … (+${ids.length - 15} more)` : '')

        const gapText =
            missing > 0
                ? `${missing} of ${total} clients have no ATE policy number (${idSample}). Their cases will fail ATE insertion until Temple returns their certificates; everyone else will be processed.`
                : `All ${total} clients have an ATE policy number.`
        const nextText = preview.ateSkipped
            ? 'This unlocks "Add ATE to existing bundles" and the funder transfer.'
            : 'This unlocks processing with the returned certificates and the funder transfer.'

        Confirm.show(
            'Mark ATE as returned',
            `${gapText} ${nextText} Continue?`,
            'Yes, mark as returned',
            'Cancel',
            async () => {
                setMarkingAteReturned(true)
                try {
                    const response = await api({ url: '/mark-ate-returned', data: { batchId, confirm: true } })
                    if (response.data?.success) {
                        Report.success('ATE marked as returned', response.data.message || 'ATE stage marked as returned', 'OKAY')
                        await fetchBatchDetails()
                    } else {
                        Report.failure('Mark ATE as returned', response.data?.message || 'Failed to mark ATE as returned', 'OKAY')
                    }
                } catch (error: any) {
                    if (handleSessionExpired(error)) return
                    console.error('Error marking ATE as returned:', error)
                    Report.failure(
                        'Mark ATE as returned',
                        error?.response?.data?.message || error?.message || 'Failed to mark ATE as returned',
                        'OKAY'
                    )
                } finally {
                    setMarkingAteReturned(false)
                }
            }
        )
    }

    const stopRepairPolling = () => {
        if (repairPollTimerRef.current) {
            clearTimeout(repairPollTimerRef.current)
            repairPollTimerRef.current = null
        }
    }

    // Repair now runs as a detached background process on the server; this
    // polls its status until it reaches a terminal state.
    const pollRepairStatus = async () => {
        if (!batchId) return
        try {
            const response = await api({
                url: '/repair-batch-status',
                data: { batchGroup: batchId },
            })
            const data = response?.data
            if (data?.success) {
                setRepairRemaining(data.brokenRemaining ?? null)

                if (data.state === 'running') {
                    repairPollTimerRef.current = setTimeout(pollRepairStatus, 5000)
                    return
                }

                setRepairingBatch(false)
                setRepairRemaining(null)
                const result = data.status?.result
                if (data.state === 'complete') {
                    Report.success('Repair complete', result?.message || 'Batch fully repaired.', 'OKAY')
                } else if (data.state === 'stopped') {
                    Report.success(
                        'Repair stopped',
                        result?.message ||
                            'The repair run was stopped. Completed repairs are saved — run Repair batch again to continue.',
                        'OKAY'
                    )
                } else if (data.state === 'failed') {
                    Report.failure(
                        'Repair incomplete',
                        result?.message ||
                            `${data.brokenRemaining ?? 'Some'} record(s) could not be repaired. Check the failure report for details.`,
                        'OKAY'
                    )
                } else {
                    // stale/idle: the runner process died (crash or server restart).
                    // Repaired records are already saved, so a re-run resumes cheaply.
                    Report.failure(
                        'Repair interrupted',
                        `The repair process stopped unexpectedly with ${data.brokenRemaining ?? '?'} record(s) still broken. Completed repairs are saved — run Repair batch again to continue.`,
                        'OKAY'
                    )
                }
                await fetchBatchDetails()
                await fetchBatchDiagnostics()
                return
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error polling repair status:', error)
        }
        // Transient polling error — keep polling a little less frequently
        repairPollTimerRef.current = setTimeout(pollRepairStatus, 10000)
    }

    const handleStopRepair = async () => {
        if (!batchId) return
        setStoppingRepair(true)
        try {
            const response = await api({
                url: '/repair-batch-stop',
                data: { batchGroup: batchId },
            })
            const data = response?.data
            if (data?.success) {
                stopRepairPolling()
                setRepairingBatch(false)
                setRepairRemaining(null)
                Report.success(
                    data.stopped ? 'Repair stopped' : 'No repair running',
                    data.message || 'Repair run stopped.',
                    'OKAY'
                )
                await fetchBatchDiagnostics()
            } else {
                Report.failure('Stop failed', data?.message || 'Failed to stop the repair run.', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error stopping repair:', error)
            Report.failure(
                'Stop error',
                error?.response?.data?.message || error?.message || 'Failed to stop the repair run',
                'OKAY'
            )
        } finally {
            setStoppingRepair(false)
        }
    }

    const handleRepairBatch = async () => {
        if (!batchId) return

        setRepairingBatch(true)
        setActionsDropdownOpen(false)
        try {
            const response = await api({
                url: '/repair-batch',
                data: { batchGroup: batchId },
            })

            const data = response?.data
            if (data?.success && data?.started) {
                setRepairRemaining(data.totalToRepair ?? null)
                Report.success(
                    'Repair started',
                    data.message ||
                        `Repair is running in the background for ${data.totalToRepair ?? '?'} record(s). Progress will update on this page.`,
                    'OKAY'
                )
                stopRepairPolling()
                repairPollTimerRef.current = setTimeout(pollRepairStatus, 5000)
            } else {
                setRepairingBatch(false)
                Report.failure('Repair error', data?.message || 'Failed to start batch repair.', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error repairing batch:', error)
            const alreadyRunning = error?.response?.status === 409
            if (alreadyRunning) {
                // A run is already in flight (e.g. started from another tab) — attach to it.
                Report.success(
                    'Repair already running',
                    error?.response?.data?.message || 'A repair run is already in progress for this batch.',
                    'OKAY'
                )
                stopRepairPolling()
                repairPollTimerRef.current = setTimeout(pollRepairStatus, 5000)
                return
            }
            setRepairingBatch(false)
            Report.failure(
                'Repair error',
                error?.response?.data?.message || error?.message || 'Failed to start batch repair',
                'OKAY'
            )
        }
    }

    const handleBundleEmailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null
        setBundleEmailFile(file)
        setRegenBundlesResult(null)
    }

    const fileToBase64 = async (file: File) => {
        const buffer = await file.arrayBuffer()
        return btoa(new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''))
    }

    const handleEmailPurgeFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null
        setEmailPurgeFile(file)
        setEmailPurgePreview(null)
        setEmailPurgeResult(null)
    }

    const handleEmailPurgePreview = async () => {
        if (!batchId || !emailPurgeFile) return
        setEmailPurgePreviewLoading(true)
        setEmailPurgeResult(null)
        try {
            const base64 = await fileToBase64(emailPurgeFile)
            const response = await api({
                url: '/funding-batch-purge-by-email-csv',
                data: { batchId, fileData: base64, preview: true },
            })
            const data = response.data as EmailCsvPurgePreview
            if (data?.success) {
                setEmailPurgePreview(data)
                setEmailPurgeConfirmText('')
                setShowEmailPurgeModal(true)
            } else {
                Report.failure('Preview Error', data?.message || 'Failed to build CSV purge preview', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure(
                'Preview Error',
                error?.response?.data?.message || error.message || 'Failed to build CSV purge preview',
                'OKAY'
            )
        } finally {
            setEmailPurgePreviewLoading(false)
        }
    }

    const handleCloseEmailPurgeModal = () => {
        if (isEmailPurging) return
        setShowEmailPurgeModal(false)
        setEmailPurgeConfirmText('')
    }

    const handleEmailPurgeConfirm = async () => {
        if (!batchId || !emailPurgeFile || emailPurgeConfirmText.toLowerCase() !== 'delete') return
        setIsEmailPurging(true)
        try {
            const base64 = await fileToBase64(emailPurgeFile)
            const response = await api({
                url: '/funding-batch-purge-by-email-csv',
                data: { batchId, fileData: base64, confirm: 'DELETE' },
            })
            const data = response.data as EmailCsvPurgePreview
            if (data?.success) {
                setEmailPurgeResult(data)
                setShowEmailPurgeModal(false)
                setEmailPurgeConfirmText('')
                Report.success(
                    'Clients removed',
                    data.message ||
                        `Removed ${data.matchedClients ?? 0} client(s) and ${data.claimCount ?? 0} claim(s) from this batch.`,
                    'OKAY'
                )
                await fetchBatchDetails()
                fetchBatchDiagnostics()
                fetchRemovedRecords()
            } else {
                Report.failure('Delete Error', data?.message || 'Failed to remove clients from this batch', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure(
                'Delete Error',
                error?.response?.data?.message || error.message || 'Failed to remove clients from this batch',
                'OKAY'
            )
        } finally {
            setIsEmailPurging(false)
            if (emailPurgeFileInputRef.current) emailPurgeFileInputRef.current.value = ''
        }
    }

    const handleRelinkIdsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null
        setRelinkIdsFile(file)
        setRelinkIdsPreview(null)
        setRelinkIdsResult(null)
    }

    const handleRelinkIdsPreview = async () => {
        if (!batchId || !relinkIdsFile) return
        setRelinkIdsPreviewLoading(true)
        setRelinkIdsResult(null)
        try {
            const base64 = await fileToBase64(relinkIdsFile)
            const response = await api({
                url: '/funding-batch-relink-introducer-ids-by-email-csv',
                data: { batchId, fileData: base64, preview: true },
            })
            const data = response.data as RelinkIntroducerIdsPreview
            if (data?.success) {
                setRelinkIdsPreview(data)
                setRelinkIdsConfirmText('')
                setShowRelinkIdsModal(true)
            } else {
                Report.failure('Preview Error', data?.message || 'Failed to build client ID relink preview', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure(
                'Preview Error',
                error?.response?.data?.message || error.message || 'Failed to build client ID relink preview',
                'OKAY'
            )
        } finally {
            setRelinkIdsPreviewLoading(false)
        }
    }

    const handleCloseRelinkIdsModal = () => {
        if (isRelinkingIds) return
        setShowRelinkIdsModal(false)
        setRelinkIdsConfirmText('')
    }

    const handleRelinkIdsConfirm = async () => {
        if (!batchId || !relinkIdsFile || relinkIdsConfirmText.toLowerCase() !== 'relink') return
        setIsRelinkingIds(true)
        try {
            const base64 = await fileToBase64(relinkIdsFile)
            const response = await api({
                url: '/funding-batch-relink-introducer-ids-by-email-csv',
                data: { batchId, fileData: base64, confirm: 'RELINK' },
            })
            const data = response.data as RelinkIntroducerIdsPreview
            setRelinkIdsResult(data)
            setShowRelinkIdsModal(false)
            setRelinkIdsConfirmText('')
            if (data?.success) {
                Report.success(
                    'Client IDs relinked',
                    data.message ||
                        `Relinked ${data.relinkedCount ?? data.willRelinkCount ?? 0} introducer client ID(s). Funding-monitor was not changed.`,
                    'OKAY'
                )
            } else {
                Report.failure(
                    'Relink Error',
                    data?.message || 'Failed to relink introducer client IDs',
                    'OKAY'
                )
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure(
                'Relink Error',
                error?.response?.data?.message || error.message || 'Failed to relink introducer client IDs',
                'OKAY'
            )
        } finally {
            setIsRelinkingIds(false)
            if (relinkIdsFileInputRef.current) relinkIdsFileInputRef.current.value = ''
        }
    }

    const handleRegenerateBundlesByEmail = async () => {
        if (!batchId || !bundleEmailFile) return
        setRegeneratingBundles(true)
        setRegenBundlesResult(null)
        try {
            const buffer = await bundleEmailFile.arrayBuffer()
            const base64 = btoa(
                new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
            )
            const response = await api({
                url: '/funding-batch-regenerate-bundles-by-email-csv',
                data: {
                    batchId,
                    fileData: base64,
                    regenerateDocs: bundleEmailDocKeys,
                    aofTemplateVariant: bundleEmailAofVariant,
                },
            })
            const data = response?.data
            setRegenBundlesResult(data)
            if (data?.success) {
                Report.success('Bundles regenerated', data.message || 'Document bundles regenerated in place.', 'OKAY')
            } else {
                Report.failure('Regeneration incomplete', data?.message || 'Failed to regenerate document bundles', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure(
                'Regeneration error',
                error?.response?.data?.message || error?.message || 'Failed to regenerate document bundles',
                'OKAY'
            )
        } finally {
            setRegeneratingBundles(false)
            if (bundleEmailFileInputRef.current) bundleEmailFileInputRef.current.value = ''
        }
    }

    const toggleBundleEmailDoc = (key: string) => {
        setBundleEmailDocKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        )
    }

    const handleSkipRepair = async () => {
        if (!batchId) return

        setSkippingRepair(true)
        setActionsDropdownOpen(false)
        try {
            const response = await api({
                url: '/skip-repair',
                data: { batchGroup: batchId },
            })

            const data = response?.data
            if (data?.success) {
                const msg =
                    data.message ||
                    `Removed ${data.removedCount ?? 0} claim(s) without document pack URL. Batch now has ${data.recordCount ?? 0} claim(s).`
                Report.success('Skip repair complete', msg, 'OKAY')
                setBatchDiagnostics({
                    processed: 'Complete',
                    submissionCsvUrl: data.funder_submission_csv_link ?? null,
                    submissionExcelUrl: data.funder_submission_excel_link ?? null,
                    failureReportUrl: null,
                    orphanedClients: batchDiagnostics?.orphanedClients ?? 0,
                })
                await fetchBatchDetails()
                // Do not refetch diagnostics here: keep the submission links from the skip-repair response
                // so the submission sheet buttons show immediately (refetch can return stale or overwrite).
            } else {
                Report.failure('Skip repair failed', data?.message || 'Failed to skip repair', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error skipping repair:', error)
            Report.failure(
                'Skip repair error',
                error?.response?.data?.message || error?.message || 'Failed to run skip repair',
                'OKAY'
            )
        } finally {
            setSkippingRepair(false)
        }
    }

    const handleRegenerateSubmissionSheet = async () => {
        if (!batchId) return

        setRegeneratingSheet(true)
        setActionsDropdownOpen(false)
        try {
            const response = await api({
                url: '/regenerate-submission-sheet',
                data: { batchGroup: batchId },
            })

            const data = response?.data
            if (data?.success) {
                Report.success(
                    'Submission sheet regenerated',
                    data.message || 'The submission sheet has been regenerated with the latest data.',
                    'OKAY'
                )
                await fetchBatchDetails()
                await fetchBatchDiagnostics()
            } else {
                Report.failure(
                    'Regeneration failed',
                    data?.message || 'Failed to regenerate submission sheet.',
                    'OKAY'
                )
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error regenerating submission sheet:', error)
            Report.failure(
                'Regeneration error',
                error?.response?.data?.message || error?.message || 'Failed to regenerate submission sheet',
                'OKAY'
            )
        } finally {
            setRegeneratingSheet(false)
        }
    }

    const handleCollectAteCertificates = async () => {
        setCollectingAteCertificates(true)
        try {
            const response = await api({
                method: 'POST',
                url: '/collect-ate-certificates',
                data: {},
            })
            const isAccepted = response?.status === 202
            const data = response?.data
            if (isAccepted && data?.success) {
                Report.success(
                    'ATE collection started',
                    data.message ||
                        'ATE certificate collection is running in the background. This may take several minutes.',
                    'OKAY'
                )
            } else if (!isAccepted) {
                Report.failure('Error', data?.message || 'Failed to start ATE certificate collection', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error starting ATE certificate collection:', error)
            Report.failure(
                'Error',
                error?.response?.data?.message || error?.message || 'Failed to start ATE certificate collection',
                'OKAY'
            )
        } finally {
            setCollectingAteCertificates(false)
        }
    }

    const applyAteVerifyResponse = (data: any) => {
        const report = data as AteVerifyReport
        setAteVerifyReport(report)
        setAteReturnFiles(report.files ?? [])
        setAteSupplementaryRequests(report.supplementaryRequests ?? [])
        setAteIssuesExpanded((report.issues?.length ?? 0) > 0 && (report.issues?.length ?? 0) <= 10)
    }

    /**
     * Compare the Temple return sheet(s) with funder_references.ate_policy_id
     * and funder_claims.ate_policy_number / ate_inception_date for this batch.
     * Falls back to the Temple FTP server (and backfills S3) if S3 has no copy.
     */
    const handleVerifyAteImport = async () => {
        if (!batchId) return
        setVerifyingAteImport(true)
        try {
            const response = await api({
                url: '/verify-ate-import',
                data: { batchId },
                validateStatus: (s: number) => (s >= 200 && s < 300) || s === 404,
            })
            if (response?.data?.success) {
                applyAteVerifyResponse(response.data)
            } else {
                setAteVerifyReport(null)
                Report.failure('Verify ATE import', response?.data?.message || 'Failed to verify ATE import', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error verifying ATE import:', error)
            Report.failure('Verify ATE import', error?.response?.data?.message || error?.message || 'Failed to verify ATE import', 'OKAY')
        } finally {
            setVerifyingAteImport(false)
        }
    }

    /** Download the actual Temple return CSV (S3 copy; pulled from FTP + saved to S3 if missing). */
    const handleDownloadTempleCsv = async () => {
        if (!batchId) return
        setDownloadingTempleCsv(true)
        try {
            const response = await api({
                url: '/verify-ate-import',
                data: { batchId, locateOnly: true },
            })
            const files: AteReturnFile[] = response?.data?.files ?? []
            if (response?.data?.success && files.length > 0) {
                setAteReturnFiles(files)
                setAteSupplementaryRequests(response.data.supplementaryRequests ?? [])
                const canonical = files.find((f) => f.canonical) ?? files[0]
                window.open(canonical.fileUrl, '_blank')
                if (response.data.source === 'ftp') {
                    Report.info(
                        'Recovered from Temple FTP',
                        'S3 had no copy of this return sheet. It has been pulled from the Temple FTP server and saved to S3 for future use.',
                        'OKAY',
                    )
                }
            } else {
                Report.failure(
                    'Temple import CSV',
                    response?.data?.message || 'No Temple return CSV found for this batch in S3 or on the Temple FTP server',
                    'OKAY',
                )
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error downloading Temple CSV:', error)
            Report.failure('Temple import CSV', error?.response?.data?.message || error?.message || 'Failed to locate the Temple return CSV', 'OKAY')
        } finally {
            setDownloadingTempleCsv(false)
        }
    }

    /** Write the sheet values onto the mismatched rows (this batch only). */
    const handleRepairAteImport = () => {
        if (!batchId || !ateVerifyReport) return
        const { repairable } = ateVerifyReport
        Confirm.show(
            'Repair ATE data from Temple sheet',
            `${repairable.issueCount} value${repairable.issueCount === 1 ? '' : 's'} across ${repairable.clientCount} client${
                repairable.clientCount === 1 ? '' : 's'
            } will be written from the Temple return sheet into funder_references / funder_claims for this batch. Blank sheet cells are never written. Continue?`,
            'Yes, repair',
            'Cancel',
            async () => {
                setRepairingAteImport(true)
                try {
                    const response = await api({ url: '/repair-ate-import', data: { batchId } })
                    if (response?.data) {
                        applyAteVerifyResponse(response.data)
                        if (response.data.success) {
                            Report.success('ATE repair complete', response.data.message || 'Repaired from the Temple sheet', 'OKAY')
                        } else {
                            Report.warning('ATE repair finished with errors', response.data.message || 'Some rows could not be repaired', 'OKAY')
                        }
                        await fetchBatchDetails()
                    }
                } catch (error: any) {
                    if (handleSessionExpired(error)) return
                    console.error('Error repairing ATE import:', error)
                    Report.failure('ATE repair', error?.response?.data?.message || error?.message || 'Failed to repair ATE data', 'OKAY')
                } finally {
                    setRepairingAteImport(false)
                }
            },
        )
    }

    /** Build a supplementary ATE request sheet for the clients Temple never returned. */
    const handleReRequestMissingAte = () => {
        if (!batchId || !ateVerifyReport) return
        const { unrepairable } = ateVerifyReport
        Confirm.show(
            'Re-request missing ATE',
            `A supplementary ATE request sheet will be generated for ${unrepairable.clientCount} client${
                unrepairable.clientCount === 1 ? '' : 's'
            } (${unrepairable.claimCount} claim${unrepairable.claimCount === 1 ? '' : 's'}) that have no ATE data and are not on the Temple return sheet. Send it to Temple, then run "Collect ATE certificates" when their return arrives. Continue?`,
            'Yes, build sheet',
            'Cancel',
            async () => {
                setReRequestingMissingAte(true)
                try {
                    const response = await api({ url: '/re-request-missing-ate', data: { batchId } })
                    if (response?.data?.success && response.data.fileUrl) {
                        window.open(response.data.fileUrl, '_blank')
                        Report.success('Supplementary ATE request ready', response.data.message || 'Sheet generated', 'OKAY')
                        await fetchAteReturnFiles()
                    } else {
                        Report.failure('Re-request missing ATE', response?.data?.message || 'Failed to build the supplementary request', 'OKAY')
                    }
                } catch (error: any) {
                    if (handleSessionExpired(error)) return
                    console.error('Error re-requesting missing ATE:', error)
                    Report.failure(
                        'Re-request missing ATE',
                        error?.response?.data?.message || error?.message || 'Failed to build the supplementary request',
                        'OKAY',
                    )
                } finally {
                    setReRequestingMissingAte(false)
                }
            },
        )
    }

    const handleCloseProgress = () => {
        // Only dismiss completed results. While a run is active the overlay
        // (and Stop control) must stay visible — closing would hide the only
        // obvious way to stop the background process.
        if (processing) return
        stopAllocationPolling()
        setProgress(null)
        setProgressLog([])
        setProcessingResult(null)
        setIsProgressLogExpanded(false)
    }

    const stopAllocationPolling = () => {
        if (allocationPollTimerRef.current) {
            clearTimeout(allocationPollTimerRef.current)
            allocationPollTimerRef.current = null
        }
    }

    const bundleRunModeLabel = (mode: string | null | undefined) =>
        mode === 'ate_insert'
            ? 'Add ATE'
            : mode === 'reprocess'
            ? 'Re-process'
            : mode === 'repair'
            ? 'Repair'
            : 'Processing'

    // Allocation and re-process runs execute as a detached sharded process on
    // the server (same pipeline as batch repair): the run survives page
    // refreshes and API restarts, and this poll drives the progress overlay
    // until the run reaches a terminal state.
    const pollBundleRunStatus = async () => {
        if (!batchId) return
        try {
            const response = await api({
                url: '/bundle-batch-status',
                data: { batchGroup: batchId },
            })
            const data = response?.data
            if (data?.success) {
                if (data.state === 'running') {
                    const completed = data.progress?.completed ?? 0
                    const total = data.progress?.totalToProcess ?? data.status?.totalToProcess ?? 0
                    const succeeded = data.progress?.succeeded ?? 0
                    const failed = data.progress?.failed ?? 0
                    const percent =
                        total > 0 ? Math.min(99, Math.max(1, Math.round((completed / total) * 100))) : 1
                    const message =
                        total > 0
                            ? `${completed}/${total} records processed (${succeeded} succeeded, ${failed} failed)`
                            : 'Run starting...'
                    setProgress({ percent, message, stage: 'documents' })
                    setProgressLog((prev) => {
                        const last = prev[prev.length - 1]
                        if (last && last.message === message) return prev
                        const entry: ProgressLogEntry = {
                            type: failed > 0 ? 'warning' : 'progress',
                            stage: 'documents',
                            percent,
                            message,
                            timestamp: new Date(),
                        }
                        return [...prev, entry]
                    })
                    allocationPollTimerRef.current = setTimeout(pollBundleRunStatus, 5000)
                    return
                }

                // Terminal state reached
                stopAllocationPolling()
                setProcessing(false)
                const result = data.status?.result
                const modeLabel = bundleRunModeLabel(data.mode)

                if (data.state === 'complete') {
                    const total = data.status?.totalToProcess ?? result?.succeededCount ?? 0
                    setProcessingResult({
                        ...result,
                        results: {
                            total,
                            successful: result?.succeededCount ?? total,
                            failed: result?.failureCount ?? 0,
                        },
                    })
                    setProgress({
                        percent: 100,
                        message: result?.message || `${modeLabel} complete`,
                        stage: 'done',
                    })
                    Report.success('Success', result?.message || `${modeLabel} completed successfully.`, 'OKAY')
                } else if (data.state === 'stopped') {
                    setProcessingResult({
                        success: false,
                        message: result?.message || `${modeLabel} run stopped.`,
                    })
                    setProgress({
                        percent: 0,
                        message: result?.message || `${modeLabel} run stopped.`,
                        stage: 'stopped',
                    })
                    Report.success(
                        `${modeLabel} stopped`,
                        result?.message ||
                            'The run was stopped. Completed records are saved — start the run again to continue.',
                        'OKAY'
                    )
                } else if (data.state === 'failed') {
                    setProcessingResult({
                        success: false,
                        message: result?.message || `${modeLabel} finished with failures.`,
                    })
                    setProgress({
                        percent: 100,
                        message: result?.message || `${modeLabel} finished with failures.`,
                        stage: 'error',
                    })
                    Report.failure(
                        `${modeLabel} incomplete`,
                        result?.message ||
                            'Some records could not be processed. Check the failure report for details.',
                        'OKAY'
                    )
                } else {
                    // stale/idle: the runner process died (crash or server restart).
                    // Completed records are already saved, so a re-run resumes cheaply.
                    setProcessingResult({
                        success: false,
                        message: 'The run stopped unexpectedly. Completed records are saved.',
                    })
                    setProgress({
                        percent: 0,
                        message: 'The run stopped unexpectedly. Completed records are saved.',
                        stage: 'error',
                    })
                    Report.failure(
                        `${modeLabel} interrupted`,
                        'The processing run stopped unexpectedly. Completed records are saved — start the run again to continue.',
                        'OKAY'
                    )
                }
                await fetchBatchDetails()
                await fetchBatchDiagnostics()
                return
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error polling bundle run status:', error)
        }
        // Transient polling error — keep polling a little less frequently
        allocationPollTimerRef.current = setTimeout(pollBundleRunStatus, 10000)
    }

    const startBundleRun = async (
        mode: 'allocate' | 'reprocess' | 'ate_insert',
        resume = false,
        regenerateDocs?: string[]
    ) => {
        if (!batchId) return

        setActionsDropdownOpen(false)
        setProcessing(true)
        setProgress(null)
        setProgressLog([])
        setProcessingResult(null)
        setIsProgressLogExpanded(false)
        const modeLabel = bundleRunModeLabel(mode)

        try {
            const response = await api({
                url: '/bundle-batch',
                data: {
                    batchGroup: batchId,
                    mode,
                    ...(resume ? { resume: true } : {}),
                    ...((mode === 'allocate' || mode === 'reprocess') && regenerateDocs != null
                        ? { regenerateDocs }
                        : {}),
                },
            })
            const data = response?.data
            if (data?.success && data?.started) {
                const message =
                    data.message ||
                    `${modeLabel} started in the background for ${data.totalToProcess ?? '?'} record(s).`
                setProgress({ percent: 1, message, stage: 'starting' })
                setProgressLog([{ type: 'info', stage: 'starting', percent: 1, message, timestamp: new Date() }])
                stopAllocationPolling()
                allocationPollTimerRef.current = setTimeout(pollBundleRunStatus, 3000)
            } else {
                setProcessing(false)
                Report.failure(
                    `${modeLabel} error`,
                    data?.message || `Failed to start ${modeLabel.toLowerCase()} run.`,
                    'OKAY'
                )
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            const alreadyRunning = error?.response?.status === 409
            if (alreadyRunning) {
                const runningMode = error?.response?.data?.runningMode
                if (runningMode === 'repair') {
                    // A repair run owns the batch — attach the repair UI instead.
                    setProcessing(false)
                    setRepairingBatch(true)
                    Report.success(
                        'Repair already running',
                        error?.response?.data?.message || 'A repair run is already in progress for this batch.',
                        'OKAY'
                    )
                    stopRepairPolling()
                    repairPollTimerRef.current = setTimeout(pollRepairStatus, 5000)
                    return
                }
                // An allocation/re-process run is already in flight — attach to it.
                Report.success(
                    'Run already in progress',
                    error?.response?.data?.message || 'A processing run is already in progress for this batch.',
                    'OKAY'
                )
                setProgress({ percent: 1, message: 'Attached to the run already in progress...', stage: 'documents' })
                stopAllocationPolling()
                allocationPollTimerRef.current = setTimeout(pollBundleRunStatus, 2000)
                return
            }
            console.error('Error starting bundle run:', error)
            setProcessing(false)
            Report.failure(
                `${modeLabel} error`,
                error?.response?.data?.message || error?.message || `Failed to start ${modeLabel.toLowerCase()} run`,
                'OKAY'
            )
        }
    }

    const handleStopProcessing = async () => {
        if (!batchId) return
        setActionsDropdownOpen(false)
        setStoppingProcessing(true)
        try {
            const response = await api({
                url: '/bundle-batch-stop',
                data: { batchGroup: batchId },
            })
            const data = response?.data
            if (data?.success) {
                stopAllocationPolling()
                setProcessing(false)
                setProgress(null)
                setProgressLog([])
                setProcessingResult({
                    success: false,
                    message: data.message || 'Processing run stopped.',
                })
                Report.success(
                    data.stopped ? 'Run stopped' : 'No run in progress',
                    data.message || 'Processing run stopped.',
                    'OKAY'
                )
                await fetchBatchDiagnostics()
            } else {
                Report.failure('Stop failed', data?.message || 'Failed to stop the processing run.', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error stopping processing run:', error)
            Report.failure(
                'Stop error',
                error?.response?.data?.message || error?.message || 'Failed to stop the processing run',
                'OKAY'
            )
        } finally {
            setStoppingProcessing(false)
        }
    }

    const openAllocateModal = (resume = false) => {
        setActionsDropdownOpen(false)
        setAllocateResume(resume)
        setAllocateDocKeys([...ALL_REPROCESS_DOC_KEYS])
        setShowAllocateModal(true)
    }

    const handleCloseAllocateModal = () => {
        if (processing) return
        setShowAllocateModal(false)
        setAllocateResume(false)
    }

    const toggleAllocateDoc = (key: string) => {
        setAllocateDocKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        )
    }

    const handleConfirmAllocateBatch = () => {
        setShowAllocateModal(false)
        startBundleRun('allocate', allocateResume, allocateDocKeys)
        setAllocateResume(false)
    }

    const handleProcessAllocation = () => openAllocateModal(false)

    /**
     * Resume an interrupted/failed processing run. The backend skips records
     * already completed (submitted with a stored documents pack URL) and only
     * processes the remainder. Doc regenerate/collect choices apply to the
     * remaining records.
     */
    const handleResumeAllocation = () => openAllocateModal(true)

    const sampleClaimOptions = useMemo(() => {
        const clients = batchDetails?.clients ?? []
        const options: { key: string; label: string; introducer: string; introducer_case_ref: string }[] = []
        for (const client of clients) {
            for (const claim of client.claims || []) {
                if (!claim.introducer_case_ref || !client.introducer) continue
                const key = `${client.introducer}::${claim.introducer_case_ref}`
                options.push({
                    key,
                    label: `${claim.introducer_case_ref} · ${client.full_name || client.email_address} (${claim.funder_case_ref || 'no funder ref'})`,
                    introducer: client.introducer,
                    introducer_case_ref: claim.introducer_case_ref,
                })
            }
        }
        return options
    }, [batchDetails?.clients])

    const openReprocessModal = () => {
        setActionsDropdownOpen(false)
        setReprocessDocKeys([...ALL_REPROCESS_DOC_KEYS])
        setSampleResult(null)
        setSampleSearchQuery('')
        setSampleSearchResults([])
        setSampleSearchDropdownOpen(false)
        setSampleSelectedClaim(sampleClaimOptions[0] ?? null)
        setShowReprocessModal(true)
    }

    const handleCloseReprocessModal = () => {
        if (sampleRunning) return
        setShowReprocessModal(false)
        setSampleResult(null)
        setSampleSearchQuery('')
        setSampleSearchResults([])
        setSampleSearchDropdownOpen(false)
    }

    const toggleReprocessDoc = (key: string) => {
        setReprocessDocKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        )
        setSampleResult(null)
    }

    const selectSampleClaim = (claim: {
        key: string
        label: string
        introducer: string
        introducer_case_ref: string
    }) => {
        setSampleSelectedClaim(claim)
        setSampleSearchQuery('')
        setSampleSearchResults([])
        setSampleSearchDropdownOpen(false)
        setSampleResult(null)
    }

    const handleSampleReprocess = async () => {
        if (!batchId || !sampleSelectedClaim) {
            Report.failure('Sample claim required', 'Search for and select a claim from this batch to run a preview.', 'OKAY')
            return
        }

        setSampleRunning(true)
        setSampleResult(null)
        try {
            const response = await api({
                url: '/sample-reprocess-bundle',
                data: {
                    batchId,
                    introducer_case_ref: sampleSelectedClaim.introducer_case_ref,
                    introducer: sampleSelectedClaim.introducer,
                    regenerateDocs: reprocessDocKeys,
                },
            })
            const data = response?.data
            if (data?.success) {
                setSampleResult({
                    bundleUrl: data.bundleUrl,
                    case_ref: data.case_ref,
                    documentSources: data.documentSources || [],
                })
            } else {
                Report.failure(
                    'Sample failed',
                    data?.message || 'Failed to generate sample bundle preview.',
                    'OKAY'
                )
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure(
                'Sample failed',
                error?.response?.data?.message || error?.message || 'Failed to generate sample bundle preview',
                'OKAY'
            )
        } finally {
            setSampleRunning(false)
        }
    }

    const handleConfirmReprocessBatch = () => {
        setShowReprocessModal(false)
        setSampleResult(null)
        startBundleRun('reprocess', false, reprocessDocKeys)
    }

    const handleReprocessAllocation = () => openReprocessModal()
    const handleInsertAteIntoExistingBundles = () => startBundleRun('ate_insert')

    const runRecalculateQuantums = async () => {
        if (!batchId) return

        setActionsDropdownOpen(false)
        setProcessing(true)
        setProgress(null)
        setProgressLog([])
        setProcessingResult(null)
        setIsProgressLogExpanded(false)

        try {
            const accessToken = localStorage.getItem('access')
            if (!accessToken) {
                throw new Error('No access token found')
            }

            const apiUrl = process.env.REACT_APP_API_URL || ''
            const response = await fetch(`${apiUrl}/recalculate-batch-quantums`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: accessToken,
                },
                body: JSON.stringify({
                    allocationGroup: batchId,
                    format: 'stream',
                }),
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            if (!response.body) {
                throw new Error('Response body is null')
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let finalResult: any = null

            while (true) {
                const { done, value } = await reader.read()
                if (done) break

                const lines = decoder.decode(value).split('\n').filter(Boolean)
                for (const line of lines) {
                    try {
                        const update = JSON.parse(line)
                        const logEntry: ProgressLogEntry = {
                            type: update.type || 'info',
                            stage: update.stage || '',
                            percent: update.percent || 0,
                            message: update.message || '',
                            data: update.data,
                            timestamp: new Date(),
                        }
                        setProgressLog((prev) => [...prev, logEntry])

                        if (update.type === 'progress' || update.type === 'info') {
                            setProgress({
                                percent: update.percent || 0,
                                message: update.message || '',
                                stage: update.stage || '',
                            })
                        }

                        if (update.type === 'error') {
                            setProgress({
                                percent: update.percent || 0,
                                message: update.message || 'Error occurred',
                                stage: 'error',
                            })
                            Report.failure('Recalculate Error', update.message || 'An error occurred', 'OKAY')
                        }

                        if (update.type === 'complete') {
                            finalResult = update.data
                            setProcessingResult(finalResult)
                            setProgress({
                                percent: 100,
                                message: update.message || 'Recalculation complete',
                                stage: 'done',
                            })
                            if (finalResult?.success) {
                                const totalMs = finalResult?.timingsMs?.total ?? 0
                                Report.success(
                                    'Quantums Recalculated',
                                    `Updated ${finalResult.updated}/${finalResult.totalClaims} claims across ${finalResult.clientsUpdated} clients in ${totalMs}ms. Press "Regenerate Submission Sheet" to refresh the CSV/Excel.`,
                                    'OKAY'
                                )
                                await fetchBatchDetails()
                            }
                        }
                    } catch (parseError) {
                        console.error('Error parsing JSON line:', parseError, 'Line:', line)
                    }
                }
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error recalculating quantums:', error)
            Report.failure('Recalculate Error', error.message || 'Failed to recalculate quantums', 'OKAY')
            setProgress({
                percent: 0,
                message: error.message || 'Failed to recalculate quantums',
                stage: 'error',
            })
        } finally {
            setProcessing(false)
        }
    }

    const handleRecalculateQuantums = () => {
        if (!batchId) return
        setActionsDropdownOpen(false)
        Confirm.show(
            'Recalculate Quantums',
            'This will re-pull each claim\'s starting balance from the introducer table and overwrite quantum_individual and quantum_client_cumulative on every claim in this batch. Continue?',
            'Recalculate',
            'Cancel',
            () => {
                runRecalculateQuantums()
            },
            () => {},
            {
                titleColor: '#1e40af',
                okButtonBackground: '#1e40af',
            }
        )
    }

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage)
        setExpandedClients(new Set()) // Reset expanded clients when changing pages
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const toggleClientClaims = (clientId: number) => {
        setExpandedClients((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(clientId)) {
                newSet.delete(clientId)
            } else {
                newSet.add(clientId)
            }
            return newSet
        })
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A'
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            })
        } catch {
            return dateString
        }
    }

    const formatCurrency = (value: number) => {
        return `£${value.toLocaleString('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`
    }

    const handleDownloadCSV = async () => {
        if (!batchId || !batchDetails) return

        setDownloadingCSV(true)
        try {
            const response = await api({
                url: '/funding-batch-export',
                data: {
                    batchId: batchId,
                },
            })

            if (response.data && response.data.success && response.data.fileUrl) {
                // Open the presigned S3 URL in a new tab to download the CSV
                window.open(response.data.fileUrl, '_blank')
                Report.success('Success', response.data.message || 'CSV download started', 'OKAY')
            } else {
                Report.failure('Error', response.data?.message || 'Failed to export batch data', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error downloading CSV:', error)
            Report.failure('Export Error', error.message || 'Failed to download CSV', 'OKAY')
        } finally {
            setDownloadingCSV(false)
        }
    }

    const handleDownloadAtePolicies = async () => {
        if (!batchId || !batchDetails) return

        setDownloadingAtePolicies(true)
        try {
            const response = await api({
                url: '/funding-batch-ate-policies-export',
                data: {
                    batchId: batchId,
                },
            })

            if (response.data && response.data.success && response.data.fileUrl) {
                window.open(response.data.fileUrl, '_blank')
                Report.success(
                    'Success',
                    response.data.message || 'ATE policies CSV download started',
                    'OKAY',
                )
            } else {
                Report.failure(
                    'Error',
                    response.data?.message || 'Failed to export ATE policies',
                    'OKAY',
                )
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error downloading ATE policies CSV:', error)
            Report.failure('Export Error', error.message || 'Failed to download ATE policies CSV', 'OKAY')
        } finally {
            setDownloadingAtePolicies(false)
        }
    }

    const handleDownloadRemovedExcel = async () => {
        if (!batchId) return

        setDownloadingRemovedExcel(true)
        try {
            const response = await api({
                url: '/funding-batch-export-removed',
                data: {
                    batchId: batchId,
                },
            })

            if (response.data && response.data.success && response.data.fileUrl) {
                window.open(response.data.fileUrl, '_blank')
                Report.success(
                    'Success',
                    response.data.message || 'Removed clients spreadsheet download started',
                    'OKAY',
                )
            } else {
                Report.failure(
                    'Error',
                    response.data?.message || 'Failed to export removed clients',
                    'OKAY',
                )
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error downloading removed clients spreadsheet:', error)
            Report.failure(
                'Export Error',
                error?.response?.data?.message || error.message || 'Failed to export removed clients',
                'OKAY',
            )
        } finally {
            setDownloadingRemovedExcel(false)
        }
    }

    const handleOpenDeleteModal = () => {
        setDeleteConfirmText('')
        setShowDeleteModal(true)
    }

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false)
        setDeleteConfirmText('')
    }

    const handleDeleteBatch = async () => {
        if (!batchId || deleteConfirmText.toLowerCase() !== 'delete') return

        setIsDeleting(true)
        try {
            const response = await api({
                url: '/funding-batch-delete',
                data: {
                    batchId: batchId,
                },
            })

            if (response.data && response.data.success) {
                const s3Info = response.data.s3Results ? ` S3 files removed: ${response.data.s3Results.deleted}.` : ''
                Report.success(
                    'Batch Deleted',
                    `Successfully deleted batch ${batchId}. ${response.data.totalDeleted} database records removed.${s3Info}`,
                    'OKAY'
                )
                handleCloseDeleteModal()
                // Navigate to the allocate claims page
                navigate('/allocate-claims')
            } else {
                Report.failure('Delete Error', response.data?.message || 'Failed to delete batch', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error deleting batch:', error)
            Report.failure('Delete Error', error.message || 'Failed to delete batch', 'OKAY')
        } finally {
            setIsDeleting(false)
        }
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
                setActionsDropdownOpen(false)
            }
        }
        if (actionsDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [actionsDropdownOpen])

    // Client search: run after 3+ characters, debounced
    useEffect(() => {
        const trimmed = clientSearchQuery.trim()
        if (trimmed.length < 3) {
            setClientSearchResults([])
            setClientSearchDropdownOpen(false)
            return
        }
        const timer = setTimeout(async () => {
            setClientSearchLoading(true)
            try {
                const response = await api({
                    url: '/funding-search-client',
                    data: { searchTerm: trimmed },
                })
                if (response.data?.success && Array.isArray(response.data.clients)) {
                    setClientSearchResults(response.data.clients)
                } else {
                    setClientSearchResults([])
                }
                setClientSearchDropdownOpen(true)
            } catch (error: any) {
                if (handleSessionExpired(error)) return
                setClientSearchResults([])
                setClientSearchDropdownOpen(true)
            } finally {
                setClientSearchLoading(false)
            }
        }, 300)
        return () => clearTimeout(timer)
    }, [clientSearchQuery, handleSessionExpired])

    // Reprocess modal sample claim search (batch-scoped)
    useEffect(() => {
        if (!showReprocessModal || !batchId) return
        const trimmed = sampleSearchQuery.trim()
        if (trimmed.length < 2) {
            setSampleSearchResults([])
            setSampleSearchDropdownOpen(false)
            return
        }
        const timer = setTimeout(async () => {
            setSampleSearchLoading(true)
            try {
                const response = await api({
                    url: '/funding-batch-search-claims',
                    data: { batchId, searchTerm: trimmed },
                })
                const claims = Array.isArray(response.data?.claims) ? response.data.claims : []
                setSampleSearchResults(
                    claims.map((c: any) => ({
                        key: `${c.introducer}::${c.introducer_case_ref}`,
                        label: `${c.introducer_case_ref} · ${c.name || c.email_address || 'Unnamed'}${
                            c.funder_case_ref ? ` (${c.funder_case_ref})` : ''
                        }`,
                        introducer: c.introducer,
                        introducer_case_ref: c.introducer_case_ref,
                    }))
                )
                setSampleSearchDropdownOpen(true)
            } catch (error: any) {
                if (handleSessionExpired(error)) return
                setSampleSearchResults([])
                setSampleSearchDropdownOpen(true)
            } finally {
                setSampleSearchLoading(false)
            }
        }, 300)
        return () => clearTimeout(timer)
    }, [sampleSearchQuery, showReprocessModal, batchId, handleSessionExpired])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sampleSearchRef.current && !sampleSearchRef.current.contains(event.target as Node)) {
                setSampleSearchDropdownOpen(false)
            }
        }
        if (sampleSearchDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [sampleSearchDropdownOpen])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (clientSearchRef.current && !clientSearchRef.current.contains(event.target as Node)) {
                setClientSearchDropdownOpen(false)
            }
        }
        if (clientSearchDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [clientSearchDropdownOpen])

    const handleOpenTransferModal = () => {
        setActionsDropdownOpen(false)
        setTransferMode('percentage')
        setTransferPercentage(20)
        setShowTransferModal(true)
    }

    const handleCloseTransferModal = () => {
        setShowTransferModal(false)
    }

    const resetSplitModalState = () => {
        setSplitMode('auto')
        setSplitClientCount(1)
        setSplitSelectedIds(new Set())
        setSplitLenderFilter('')
        setSplitMinValue('')
        setSplitMaxValue('')
        setSplitMinClaims('')
        setSplitMaxClaims('')
        setSplitSearch('')
        setSplitPreview(null)
    }

    const handleOpenSplitModal = async () => {
        setActionsDropdownOpen(false)
        resetSplitModalState()
        setShowSplitModal(true)
        if (!batchId) return
        setSplitPreviewLoading(true)
        try {
            const response = await api({
                url: '/funding-batch-split-preview',
                method: 'POST',
                data: { batchId },
            })
            if (response.data?.success) {
                setSplitPreview(response.data as SplitPreviewResponse)
                const total = response.data.total_clients || 0
                setSplitClientCount(Math.max(1, Math.min(1, total - 1)))
            } else {
                Report.failure('Split Preview Failed', response.data?.message || 'Could not load clients', 'OKAY')
                setShowSplitModal(false)
            }
        } catch (error: any) {
            Report.failure(
                'Split Preview Failed',
                error?.response?.data?.message || error.message || 'Could not load clients',
                'OKAY'
            )
            setShowSplitModal(false)
        } finally {
            setSplitPreviewLoading(false)
        }
    }

    const handleCloseSplitModal = () => {
        if (isSplitting) return
        setShowSplitModal(false)
        resetSplitModalState()
    }

    const getSplitFilterPayload = () => {
        const filters: {
            lenders?: string[]
            minValue?: number
            maxValue?: number
            minClaims?: number
            maxClaims?: number
        } = {}
        if (splitLenderFilter) filters.lenders = [splitLenderFilter]
        if (splitMinValue !== '' && !Number.isNaN(Number(splitMinValue))) filters.minValue = Number(splitMinValue)
        if (splitMaxValue !== '' && !Number.isNaN(Number(splitMaxValue))) filters.maxValue = Number(splitMaxValue)
        if (splitMinClaims !== '' && !Number.isNaN(Number(splitMinClaims))) filters.minClaims = Number(splitMinClaims)
        if (splitMaxClaims !== '' && !Number.isNaN(Number(splitMaxClaims))) filters.maxClaims = Number(splitMaxClaims)
        return filters
    }

    const filteredSplitClients = useMemo(() => {
        const clients = splitPreview?.clients || []
        const filters = getSplitFilterPayload()
        const q = splitSearch.trim().toLowerCase()
        return clients.filter((c) => {
            if (filters.lenders?.length) {
                const wanted = filters.lenders.map((l) => l.toLowerCase())
                if (!c.lenders.some((l) => wanted.includes(l.toLowerCase()))) return false
            }
            if (filters.minValue != null && c.total_value < filters.minValue) return false
            if (filters.maxValue != null && c.total_value > filters.maxValue) return false
            if (filters.minClaims != null && c.claims_count < filters.minClaims) return false
            if (filters.maxClaims != null && c.claims_count > filters.maxClaims) return false
            if (q) {
                const hay = `${c.full_name} ${c.email_address}`.toLowerCase()
                if (!hay.includes(q)) return false
            }
            return true
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps -- filter deps listed explicitly
    }, [
        splitPreview,
        splitLenderFilter,
        splitMinValue,
        splitMaxValue,
        splitMinClaims,
        splitMaxClaims,
        splitSearch,
    ])

    const splitMovePreview = useMemo(() => {
        const all = splitPreview?.clients || []
        let moving: SplitPreviewClient[] = []
        if (splitMode === 'auto') {
            moving = filteredSplitClients.slice(0, Math.max(0, splitClientCount))
        } else {
            moving = filteredSplitClients.filter((c) => splitSelectedIds.has(c.funder_client_id))
        }
        const movingIds = new Set(moving.map((c) => c.funder_client_id))
        const remaining = all.filter((c) => !movingIds.has(c.funder_client_id))
        const sumValue = (list: SplitPreviewClient[]) =>
            Math.round(list.reduce((s, c) => s + (c.total_value || 0), 0) * 100) / 100
        const sumClaims = (list: SplitPreviewClient[]) => list.reduce((s, c) => s + (c.claims_count || 0), 0)
        return {
            moving,
            remaining,
            movedClients: moving.length,
            movedClaims: sumClaims(moving),
            movedValue: sumValue(moving),
            remainingClients: remaining.length,
            remainingClaims: sumClaims(remaining),
            remainingValue: sumValue(remaining),
        }
    }, [splitPreview, splitMode, filteredSplitClients, splitClientCount, splitSelectedIds])

    const handleConfirmSplit = async () => {
        if (!batchId || !splitPreview?.canSplit) return
        if (splitMovePreview.movedClients < 1 || splitMovePreview.remainingClients < 1) {
            Report.failure(
                'Invalid Split',
                'You must move at least 1 client and leave at least 1 client in the original batch.',
                'OKAY'
            )
            return
        }
        setIsSplitting(true)
        try {
            const filters = getSplitFilterPayload()
            const payload: Record<string, unknown> = {
                batchId,
                mode: splitMode,
                filters,
            }
            if (splitMode === 'auto') {
                payload.clientCount = splitClientCount
            } else {
                payload.clientIds = Array.from(splitSelectedIds)
            }
            const response = await api({
                url: '/funding-batch-split',
                method: 'POST',
                data: payload,
            })
            if (!response.data?.success) {
                Report.failure('Split Failed', response.data?.message || 'Failed to split batch', 'OKAY')
                return
            }
            const newId = response.data.newBatchId as string
            setShowSplitModal(false)
            resetSplitModalState()
            await fetchBatchDetails()
            Confirm.show(
                'Batch Split',
                `${response.data.message || 'Split complete.'}${
                    response.data.submissionRegenerated
                        ? ' Submission sheets regenerated for both batches.'
                        : ''
                }`,
                'View new batch',
                'Stay here',
                () => navigate(`/funding-batch/${newId}`),
                () => undefined
            )
        } catch (error: any) {
            Report.failure(
                'Split Failed',
                error?.response?.data?.message || error.message || 'Failed to split batch',
                'OKAY'
            )
        } finally {
            setIsSplitting(false)
        }
    }

    const handleTransferByApi = async () => {
        if (!batchId) return
        if (transferMode === 'percentage' && (transferPercentage < 1 || transferPercentage > 100)) return
        if (transferMode === 'count' && transferClientCount < 1) return
        setIsTransferring(true)
        try {
            const payload: {
                batchGroup: string
                sendPercentage?: number
                sendClientCount?: number
                sendAllRemaining?: boolean
                returnImmediately?: boolean
            } = {
                batchGroup: batchId,
            }
            if (transferMode === 'remaining') {
                payload.sendAllRemaining = true
                payload.returnImmediately = true
            } else if (transferMode === 'all') {
                payload.sendPercentage = 100
                payload.returnImmediately = true
            } else if (transferMode === 'count') {
                payload.sendClientCount = transferClientCount
                payload.returnImmediately = true
            } else {
                payload.sendPercentage = transferPercentage
                if (transferPercentage >= 100) payload.returnImmediately = true
            }
            const response = await api({
                url: '/funding-api-transfer',
                data: payload,
            })
            const data = response?.data
            const isBackground = response?.status === 202
            if (data?.success) {
                if (isBackground) {
                    Report.success(
                        'Transfer started',
                        data.message ||
                            `Transfer started in background (${
                                data.totalRecords ?? '?'
                            } records). Check server logs for progress.`,
                        'OKAY'
                    )
                    handleCloseTransferModal()
                    setTimeout(() => fetchBatchDetails(), 2000)
                } else {
                    const failedCount = data.failedCount ?? 0
                    if (failedCount > 0) {
                        handleCloseTransferModal()
                        setTransferResultModal({
                            show: true,
                            success: true,
                            message: data.message || 'Transfer completed with some failures.',
                            transferredCount: data.transferredCount,
                            failedCount,
                            failedCsvPresignedUrl: data.failedCsvPresignedUrl ?? null,
                        })
                    } else {
                        Report.success('Transfer complete', data.message, 'OKAY')
                        handleCloseTransferModal()
                        await fetchBatchDetails()
                    }
                }
            } else {
                handleCloseTransferModal()
                setTransferResultModal({
                    show: true,
                    success: false,
                    message: data?.message || 'Failed to transfer cases via API.',
                })
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error transferring by API:', error)
            const errData = error?.response?.data
            handleCloseTransferModal()
            setTransferResultModal({
                show: true,
                success: false,
                message:
                    errData?.message ||
                    error?.message ||
                    'Failed to transfer cases via API. Check your connection and try again.',
            })
        } finally {
            setIsTransferring(false)
        }
    }

    const handleCloseTransferResultModal = () => {
        setTransferResultModal((prev) => ({ ...prev, show: false }))
        fetchBatchDetails()
    }

    const handleAuditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null
        setAuditFile(file)
        setAuditPreview(null)
        setAuditResult(null)
    }

    const handleAuditCheck = async () => {
        if (!batchId || !auditFile) return
        setAuditChecking(true)
        setAuditPreview(null)
        setAuditResult(null)
        try {
            const buffer = await auditFile.arrayBuffer()
            const base64 = btoa(
                new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
            )
            const response = await api({
                url: '/funding-batch-audit-check',
                data: { batchId, fileData: base64 },
            })
            if (response.data?.success) {
                setAuditPreview(response.data as AuditPreview)
                setExpandedAuditClients(new Set())
                setAuditClientsListOpen(false)
                setAuditPartialListOpen(false)
                setAuditAddressListOpen(false)
                setAuditApplyAddressUpdates(true)
            } else {
                Report.failure('Audit Check', response.data?.message || 'Failed to process audit file', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Audit Check Error', error?.response?.data?.message || error.message || 'Failed to process audit file', 'OKAY')
        } finally {
            setAuditChecking(false)
        }
    }

    const auditAddressUpdateCount = auditPreview?.allAddressUpdates?.length || 0
    const auditWillApplyAddressUpdates = auditApplyAddressUpdates && auditAddressUpdateCount > 0
    const auditHasChangesToConfirm = !!auditPreview && (auditPreview.allCaseRefsToRemove.length > 0 || auditWillApplyAddressUpdates)

    const handleAuditConfirmRemoval = async () => {
        if (!batchId || !auditPreview) return
        setAuditRemoving(true)
        try {
            const response = await api({
                url: '/funding-batch-audit-remove',
                data: {
                    batchId,
                    caseRefsToRemove: auditPreview.allCaseRefsToRemove,
                    addressUpdates: auditWillApplyAddressUpdates ? auditPreview.allAddressUpdates : [],
                },
            })
            if (response.data?.success) {
                setAuditResult({ success: true, message: response.data.message })
                setAuditPreview(null)
                setAuditFile(null)
                if (auditFileInputRef.current) auditFileInputRef.current.value = ''
                await fetchBatchDetails()
                fetchRemovedRecords()
            } else {
                setAuditResult({ success: false, message: response.data?.message || 'Removal failed' })
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            setAuditResult({ success: false, message: error?.response?.data?.message || error.message || 'Removal failed' })
        } finally {
            setAuditRemoving(false)
        }
    }

    const handleAuditCancelPreview = () => {
        setAuditPreview(null)
        setAuditResult(null)
    }

    const fetchRemovedRecords = useCallback(async (query?: string, page?: number) => {
        if (!batchId) return
        setRemovedLoading(true)
        try {
            const response = await api({
                url: '/funding-batch-audit-removed',
                data: {
                    batchId,
                    searchQuery: query !== undefined ? query : removedSearchQuery || undefined,
                    page: page || removedPage,
                    limit: removedLimit,
                },
            })
            if (response.data?.success) {
                setRemovedClients(response.data.removedClients || [])
                setRemovedTotalClaims(response.data.totalRemovedClaims ?? 0)
                if (response.data.pagination) {
                    setRemovedPagination(response.data.pagination)
                }
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error fetching removed records:', error)
        } finally {
            setRemovedLoading(false)
        }
    }, [batchId, removedSearchQuery, removedPage, handleSessionExpired])

    useEffect(() => {
        if (removedSectionOpen && batchId) {
            fetchRemovedRecords(undefined, removedPage)
        }
    }, [removedSectionOpen, batchId, removedPage])

    useEffect(() => {
        if (!removedSectionOpen) return
        const timer = setTimeout(() => {
            setRemovedPage(1)
            fetchRemovedRecords(removedSearchQuery, 1)
        }, 300)
        return () => clearTimeout(timer)
    }, [removedSearchQuery])

    const handleRestoreClients = async (caseRefs: string[]) => {
        if (!batchId) return
        setRestoringRefs((prev) => {
            const next = new Set(prev)
            caseRefs.forEach((r) => next.add(r))
            return next
        })
        try {
            const response = await api({
                url: '/funding-batch-audit-restore',
                data: { batchId, caseRefsToRestore: caseRefs },
            })
            if (response.data?.success) {
                Report.success('Restored', response.data.message, 'OKAY')
                await fetchBatchDetails()
                fetchRemovedRecords()
            } else {
                Report.failure('Restore Error', response.data?.message || 'Failed to restore', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Restore Error', error.message || 'Failed to restore', 'OKAY')
        } finally {
            setRestoringRefs((prev) => {
                const next = new Set(prev)
                caseRefs.forEach((r) => next.delete(r))
                return next
            })
        }
    }

    const handleRestoreAll = async () => {
        if (!batchId) return
        setRestoringRefs(new Set(['__all__']))
        try {
            const response = await api({
                url: '/funding-batch-audit-restore',
                data: { batchId, restoreAll: true },
            })
            if (response.data?.success) {
                Report.success('Restored All', response.data.message, 'OKAY')
                await fetchBatchDetails()
                fetchRemovedRecords()
            } else {
                Report.failure('Restore Error', response.data?.message || 'Failed to restore', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Restore Error', error.message || 'Failed to restore', 'OKAY')
        } finally {
            setRestoringRefs(new Set())
        }
    }

    const REASON_LABELS: Record<string, string> = {
        'audit-check': 'Audit Check',
        'loan-start-date-removal': 'Loan Start Date After Oct 2024',
        'various-issues': 'Various Issues',
        'removal-by-client-email': 'Removal by Client Email',
    }

    const fetchRemovalReasons = useCallback(async () => {
        if (!batchId) return
        try {
            const response = await api({
                url: '/funding-batch-audit-removal-reasons',
                data: { batchId },
            })
            if (response.data?.success) {
                setRemovalReasons(response.data.reasons || [])
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            console.error('Error fetching removal reasons:', error)
        }
    }, [batchId, handleSessionExpired])

    useEffect(() => {
        if (removedSectionOpen && batchId) {
            fetchRemovalReasons()
        }
    }, [removedSectionOpen, batchId])

    const handleRevertByType = async () => {
        if (!batchId || !selectedRevertReason) return
        setRevertingByType(true)
        try {
            const response = await api({
                url: '/funding-batch-audit-restore',
                data: { batchId, restoreReason: selectedRevertReason },
            })
            if (response.data?.success) {
                const label = REASON_LABELS[selectedRevertReason] || selectedRevertReason
                Report.success('Reverted', `${response.data.restoredCount} claim(s) restored for "${label}"`, 'OKAY')
                setSelectedRevertReason('')
                await fetchBatchDetails()
                fetchRemovedRecords()
                fetchRemovalReasons()
            } else {
                Report.failure('Revert Error', response.data?.message || 'Failed to revert', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Revert Error', error.message || 'Failed to revert', 'OKAY')
        } finally {
            setRevertingByType(false)
        }
    }

    const handleOpenResetMembershipModal = async () => {
        if (!batchId) return
        setActionsDropdownOpen(false)
        setResetMembershipPreviewLoading(true)
        try {
            const response = await api({
                url: '/funding-batch-reset-membership-preview',
                data: { batchId },
            })
            if (response.data?.success) {
                setResetMembershipPreview(response.data.preview)
                setResetMembershipConfirmText('')
                setShowResetMembershipModal(true)
            } else {
                Report.failure('Preview Error', response.data?.message || 'Failed to build reset preview', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Preview Error', error?.response?.data?.message || error.message || 'Failed to build reset preview', 'OKAY')
        } finally {
            setResetMembershipPreviewLoading(false)
        }
    }

    const handleCloseResetMembershipModal = () => {
        if (isResettingMembership) return
        setShowResetMembershipModal(false)
        setResetMembershipPreview(null)
        setResetMembershipConfirmText('')
    }

    const handleResetMembership = async () => {
        if (!batchId || resetMembershipConfirmText.trim().toLowerCase() !== 'reset') return
        setIsResettingMembership(true)
        try {
            const response = await api({
                url: '/funding-batch-reset-membership',
                data: { batchId, confirm: 'RESET_BATCH' },
            })
            if (response.data?.success) {
                Report.success('Batch Reset', response.data.message, 'OKAY')
                setShowResetMembershipModal(false)
                setResetMembershipPreview(null)
                setResetMembershipConfirmText('')
                await Promise.all([fetchBatchDetails(), fetchBatchDiagnostics()])
                if (removedSectionOpen) {
                    fetchRemovedRecords()
                    fetchRemovalReasons()
                }
            } else {
                Report.failure('Reset Error', response.data?.message || 'Failed to reset batch', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Reset Error', error?.response?.data?.message || error.message || 'Failed to reset batch', 'OKAY')
        } finally {
            setIsResettingMembership(false)
        }
    }

    const handleOpenPurgeModal = async () => {
        if (!batchId) return
        setPurgePreviewLoading(true)
        try {
            const response = await api({
                url: '/funding-batch-purge-removed-preview',
                data: { batchId },
            })
            if (response.data?.success) {
                setPurgePreview(response.data)
                setPurgeConfirmText('')
                setShowPurgeModal(true)
            } else {
                Report.failure('Preview Error', response.data?.message || 'Failed to build delete preview', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Preview Error', error?.response?.data?.message || error.message || 'Failed to build delete preview', 'OKAY')
        } finally {
            setPurgePreviewLoading(false)
        }
    }

    const handleClosePurgeModal = () => {
        if (isPurging) return
        setShowPurgeModal(false)
        setPurgePreview(null)
        setPurgeConfirmText('')
    }

    const handlePurgeRemoved = async () => {
        if (!batchId || purgeConfirmText.toLowerCase() !== 'delete') return
        setIsPurging(true)
        try {
            const response = await api({
                url: '/funding-batch-purge-removed',
                data: { batchId, confirm: 'DELETE' },
            })
            if (response.data?.success) {
                const { clientCount, claimCount, totalDeleted } = response.data
                Report.success(
                    'Permanently Deleted',
                    `${clientCount} client(s) and ${claimCount} claim(s) permanently deleted from the funding batch and introducer database. ${totalDeleted ?? 0} database row(s) removed in total.`,
                    'OKAY'
                )
                setShowPurgeModal(false)
                setPurgePreview(null)
                setPurgeConfirmText('')
                await fetchBatchDetails()
                fetchRemovedRecords()
                fetchRemovalReasons()
            } else {
                Report.failure('Delete Error', response.data?.message || 'Failed to delete removed clients', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Delete Error', error?.response?.data?.message || error.message || 'Failed to delete removed clients', 'OKAY')
        } finally {
            setIsPurging(false)
        }
    }

    const handleResetApiTransfer = async () => {
        if (!batchId) return
        setResettingTransfer(true)
        setActionsDropdownOpen(false)
        try {
            const response = await api({
                url: '/funding-batch-reset-transfer',
                data: { batchId },
            })
            if (response.data?.success) {
                Report.success('Transfer Reset', response.data.message, 'OKAY')
                await fetchBatchDetails()
            } else {
                Report.failure('Reset Error', response.data?.message || 'Failed to reset transfer status', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Reset Error', error.message || 'Failed to reset transfer status', 'OKAY')
        } finally {
            setResettingTransfer(false)
        }
    }

    const toggleAuditClient = (clientId: number) => {
        setExpandedAuditClients((prev) => {
            const next = new Set(prev)
            if (next.has(clientId)) next.delete(clientId)
            else next.add(clientId)
            return next
        })
    }

    const toggleRemovedClient = (clientId: number) => {
        setExpandedRemovedClients((prev) => {
            const next = new Set(prev)
            if (next.has(clientId)) next.delete(clientId)
            else next.add(clientId)
            return next
        })
    }

    const handleAlterationFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null
        setAlterationFile(file)
        setAlterationPreview(null)
        setAlterationResult(null)
        setAlterationReprocessResult(null)
    }

    const handleAlterationPreview = async () => {
        if (!batchId) return
        const requiresFile = alterationType !== 'excessive-claims-removal'
        if (requiresFile && !alterationFile) return
        setAlterationPreviewing(true)
        setAlterationPreview(null)
        setAlterationResult(null)
        setAlterationReprocessResult(null)
        try {
            let base64: string | undefined
            if (requiresFile && alterationFile) {
                const buffer = await alterationFile.arrayBuffer()
                base64 = btoa(
                    new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
                )
            }
            const response = await api({
                url: '/post-audit-alteration-preview',
                data: { batchId, fileData: base64, alterationType },
            })
            if (response.data?.success) {
                setAlterationPreview(response.data)
            } else {
                Report.failure('Preview Error', response.data?.message || 'Failed to preview alterations', 'OKAY')
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Preview Error', error?.response?.data?.message || error.message || 'Failed to preview', 'OKAY')
        } finally {
            setAlterationPreviewing(false)
        }
    }

    const handleAlterationProcess = async () => {
        if (!batchId) return
        const requiresFile = alterationType !== 'excessive-claims-removal'
        if (requiresFile && !alterationFile) return
        setAlterationProcessing(true)
        setAlterationResult(null)
        setAlterationReprocessResult(null)
        try {
            let base64: string | undefined
            if (requiresFile && alterationFile) {
                const buffer = await alterationFile.arrayBuffer()
                base64 = btoa(
                    new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
                )
            }
            const accessToken = window.localStorage.getItem('access') || ''
            const response = await fetch(`${process.env.REACT_APP_API_URL}/post-audit-alteration-process`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: accessToken },
                body: JSON.stringify({ batchId, alterationType, fileData: base64 }),
            })

            if (!response.ok || !response.body) {
                Report.failure('Process Error', 'Failed to process alterations', 'OKAY')
                setAlterationProcessing(false)
                return
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer2 = ''

            while (true) {
                const { done, value } = await reader.read()
                if (done) break
                buffer2 += decoder.decode(value, { stream: true })
                const lines = buffer2.split('\n')
                buffer2 = lines.pop() || ''
                for (const line of lines) {
                    if (!line.trim()) continue
                    try {
                        const msg = JSON.parse(line)
                        if (msg.type === 'complete') {
                            setAlterationResult(msg)
                        }
                    } catch { /* ignore parse errors for partial lines */ }
                }
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Process Error', error.message || 'Failed to process alterations', 'OKAY')
        } finally {
            setAlterationProcessing(false)
        }
    }

    const handleAlterationReprocess = async () => {
        if (!alterationResult?.results) return
        const updatedCases = alterationResult.results
            .filter((r: any) => r.status === 'updated')
            .map((r: any) => ({
                introducer_case_ref: r.introducer_case_ref,
                introducer: alterationPreview?.matched?.find(
                    (m: any) => m.law_firm_secondary_reference === r.ref
                )?.introducer || ''
            }))
            .filter((c: any) => c.introducer_case_ref && c.introducer)

        if (updatedCases.length === 0) {
            Report.info('No Cases', 'No successfully updated cases to reprocess.', 'OKAY')
            return
        }

        setAlterationReprocessing(true)
        setAlterationReprocessResult(null)
        try {
            const accessToken = window.localStorage.getItem('access') || ''
            const response = await fetch(`${process.env.REACT_APP_API_URL}/post-audit-alteration-reprocess`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: accessToken },
                body: JSON.stringify({ cases: updatedCases, batchId }),
            })

            if (!response.ok || !response.body) {
                Report.failure('Reprocess Error', 'Failed to reprocess document bundles', 'OKAY')
                setAlterationReprocessing(false)
                return
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer2 = ''

            while (true) {
                const { done, value } = await reader.read()
                if (done) break
                buffer2 += decoder.decode(value, { stream: true })
                const lines = buffer2.split('\n')
                buffer2 = lines.pop() || ''
                for (const line of lines) {
                    if (!line.trim()) continue
                    try {
                        const msg = JSON.parse(line)
                        if (msg.type === 'complete') {
                            setAlterationReprocessResult(msg)
                        }
                    } catch { /* ignore parse errors */ }
                }
            }
        } catch (error: any) {
            if (handleSessionExpired(error)) return
            Report.failure('Reprocess Error', error.message || 'Failed to reprocess', 'OKAY')
        } finally {
            setAlterationReprocessing(false)
        }
    }

    const handleAlterationExport = async () => {
        if (!batchId || !alterationResult?.results) return
        const updatedRefs = alterationResult.results
            .filter((r: any) => r.status === 'updated')
            .map((r: any) => r.ref)
        if (updatedRefs.length === 0) return

        setAlterationExporting(true)
        try {
            const response = await api({
                url: '/post-audit-alteration-export',
                data: { batchId, refs: updatedRefs },
            })
            if (response.data?.success && response.data.fileData) {
                const byteChars = atob(response.data.fileData)
                const byteNumbers = new Array(byteChars.length)
                for (let i = 0; i < byteChars.length; i++) {
                    byteNumbers[i] = byteChars.charCodeAt(i)
                }
                const blob = new Blob([new Uint8Array(byteNumbers)], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = response.data.fileName || `updated-claims-${batchId}.xlsx`
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(url)
            } else {
                Report.failure('Export Failed', response.data?.message || 'Failed to export updated claims', 'OKAY')
            }
        } catch (error: any) {
            Report.failure('Export Error', error?.response?.data?.message || error.message || 'Failed to export', 'OKAY')
        } finally {
            setAlterationExporting(false)
        }
    }

    const handleQuantumCsvDownload = async () => {
        if (!batchId || !alterationResult?.results) return
        const updatedRefs = alterationResult.results
            .filter((r: any) => r.status === 'updated')
            .map((r: any) => r.ref)
        if (updatedRefs.length === 0) return

        try {
            const response = await api({
                url: '/post-audit-quantum-export',
                data: { batchId, refs: updatedRefs },
            })
            if (!response.data?.success || !response.data.data) {
                Report.failure('Export Failed', response.data?.message || 'Failed to fetch quantum values', 'OKAY')
                return
            }

            const header = 'Law Firm Secondary Reference,Quantum Individual,Quantum Client Cumulative'
            const rows = response.data.data.map((r: any) =>
                `${r.law_firm_secondary_reference},${r.quantum_individual ?? ''},${r.quantum_client_cumulative ?? ''}`
            )
            const csv = [header, ...rows].join('\n')
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `quantum-values-${batchId}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch (error: any) {
            Report.failure('Export Error', error?.response?.data?.message || error.message || 'Failed to export quantum values', 'OKAY')
        }
    }

    const handleAlterationReset = () => {
        setAlterationFile(null)
        setAlterationPreview(null)
        setAlterationResult(null)
        setAlterationReprocessResult(null)
        if (alterationFileInputRef.current) alterationFileInputRef.current.value = ''
    }

    const timelineActiveStepIndex = useMemo(() => {
        if (!batchDetails) return 0
        const ateRequestedDone = ateRequested || !!batchDetails.ate_requested
        const isAteReturnedLocal = batchDetails.config?.ate_returned === 'Yes'
        const isAteSkippedLocal = batchDetails.config?.ate_skipped === 'Yes'
        const ateStageClearedLocal = isAteReturnedLocal || isAteSkippedLocal
        const processedComplete =
            batchDiagnostics?.processed === 'Complete' ||
            batchDiagnostics?.processed === 'Complete with errors'
        const processedStarted = batchDiagnostics?.processed === 'Started'
        const processedStaleLocal = processedStarted && !processing
        const submissionDone =
            batchDiagnostics?.processed === 'Complete' &&
            !!(batchDiagnostics?.submissionExcelUrl || batchDiagnostics?.submissionCsvUrl)
        const apiTransferredProcessed =
            typeof batchDetails.api_transferred === 'boolean'
                ? batchDetails.api_transferred === true
                : (batchDetails.api_transferred ?? 0) > 0
        const isProcessedCompleteLocal = batchDiagnostics?.processed === 'Complete'
        if (!ateRequestedDone && !isAteSkippedLocal) return 1
        if (!ateStageClearedLocal) return 2
        if (processedStaleLocal) return 3
        if (!processedComplete && !processedStarted) return 3
        if (processedComplete && !submissionDone && batchDiagnostics?.processed === 'Complete') return 4
        if (isProcessedCompleteLocal && ateStageClearedLocal && !apiTransferredProcessed) return 5
        return 0
    }, [batchDetails, batchDiagnostics, ateRequested, processing])

    useEffect(() => {
        setTimelineCarouselIndex((prev) => {
            if (prev === 0 && timelineActiveStepIndex > 0) return timelineActiveStepIndex
            return prev
        })
    }, [timelineActiveStepIndex])

    if (loading) {
        return (
            <div className={styles.detailsContainer}>
                <div className={styles.loadingState}>
                    <p>Loading batch details...</p>
                </div>
            </div>
        )
    }

    if (!batchDetails) {
        return (
            <div className={styles.detailsContainer}>
                <div className={styles.noResults}>
                    <p>Batch details not found.</p>
                </div>
            </div>
        )
    }

    // Check if batch has been processed using batch-level status from funding-batches endpoint
    // Handle both boolean and number values (number > 0 means processed)
    const submittedProcessed =
        typeof batchDetails.submitted === 'boolean'
            ? batchDetails.submitted === true
            : (batchDetails.submitted ?? 0) > 0
    const apiTransferredProcessed =
        typeof batchDetails.api_transferred === 'boolean'
            ? batchDetails.api_transferred === true
            : (batchDetails.api_transferred ?? 0) > 0
    const isBatchProcessed = submittedProcessed || apiTransferredProcessed
    // Check config.ate_returned === "Yes" from the config object
    const isAteReturned = batchDetails.config?.ate_returned === 'Yes'
    // ATE is an optional stage: a skipped batch moves on without certificates
    // and can have them added later by an ATE run plus a re-process.
    const isAteSkipped = batchDetails.config?.ate_skipped === 'Yes'
    const ateStageCleared = isAteReturned || isAteSkipped
    const isProcessedComplete = batchDiagnostics?.processed === 'Complete'
    const canTransferByApi = ateStageCleared && isProcessedComplete
    const ateRequestedForSplit =
        !!batchDetails.ate_requested || batchDetails.config?.ate_requested === 'Yes'
    const canSplitBatch =
        !ateRequestedForSplit &&
        !apiTransferredProcessed &&
        (batchDetails.batch_summary?.total_clients ?? 0) >= 2
    // Certificates arrived after the batch was processed without them: the
    // bundles need re-processing to pull the ATE certificate in.
    const needsAteReprocess = isAteReturned && isAteSkipped && isBatchProcessed
    // A previous processing run was interrupted (stale 'Started') or finished
    // with failures - the backend can resume, skipping completed records.
    const canResumeProcessing =
        !processing &&
        (batchDiagnostics?.processed === 'Started' || batchDiagnostics?.processed === 'Complete with errors')

    return (
        <div className={styles.detailsContainer}>
            {/* Progress UI - Moved to top */}
            {(processing || processingResult) && (
                <div className={styles.progressCard}>
                    <div className={styles.progressCardHeader}>
                        <h3>Processing Batch: {batchDetails.batch_id}</h3>
                        <div className={styles.progressHeaderActions}>
                            {processing && (
                                <button
                                    type="button"
                                    className={styles.stopRunButton}
                                    onClick={handleStopProcessing}
                                    disabled={stoppingProcessing}
                                >
                                    {stoppingProcessing ? 'Stopping...' : 'Stop run'}
                                </button>
                            )}
                            {!processing && (
                                <button
                                    type="button"
                                    className={styles.closeButton}
                                    onClick={handleCloseProgress}
                                    aria-label="Close progress"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>

                    {progress && (
                        <div className={styles.progressSection}>
                            <div className={styles.progressHeader}>
                                <span className={styles.progressStage}>{progress.stage}</span>
                                <span className={styles.progressPercent}>{progress.percent}%</span>
                            </div>
                            <div className={styles.progressBar}>
                                <div className={styles.progressBarFill} style={{ width: `${progress.percent}%` }} />
                            </div>
                            {progress.message && <div className={styles.progressMessage}>{progress.message}</div>}
                        </div>
                    )}

                    {progressLog.length > 0 && (
                        <div className={styles.progressLogSection}>
                            <div className={styles.progressLogHeader}>
                                <div className={styles.progressLogTitle}>
                                    <span>Progress Log</span>
                                    <span className={styles.logCount}>({progressLog.length} entries)</span>
                                </div>
                                <button
                                    type="button"
                                    className={styles.expandButton}
                                    onClick={() => setIsProgressLogExpanded(!isProgressLogExpanded)}
                                >
                                    <span className={styles.expandIcon}>{isProgressLogExpanded ? '▼' : '▶'}</span>
                                </button>
                            </div>
                            <div
                                className={`${styles.progressLogContainer} ${
                                    isProgressLogExpanded ? styles.expanded : ''
                                }`}
                            >
                                {isProgressLogExpanded ? (
                                    <div className={styles.progressLog}>
                                        {progressLog.map((entry, idx) => (
                                            <div key={idx} className={styles.progressLogEntry}>
                                                <div className={styles.logHeader}>
                                                    <span
                                                        className={`${styles.logType} ${
                                                            styles[`logType${entry.type}`]
                                                        }`}
                                                    >
                                                        {entry.type.toUpperCase()}
                                                    </span>
                                                    {entry.stage && (
                                                        <span className={styles.logStage}>{entry.stage}</span>
                                                    )}
                                                    {entry.percent !== undefined && (
                                                        <span className={styles.logPercent}>{entry.percent}%</span>
                                                    )}
                                                    {entry.timestamp && (
                                                        <span className={styles.logTime}>
                                                            {new Date(entry.timestamp).toLocaleTimeString()}
                                                        </span>
                                                    )}
                                                </div>
                                                {entry.message && (
                                                    <div className={styles.logMessage}>{entry.message}</div>
                                                )}
                                                {entry.data && Object.keys(entry.data).length > 0 && (
                                                    <div className={styles.logData}>
                                                        {Object.entries(entry.data).map(([key, value]) => (
                                                            <div key={key} className={styles.logDataItem}>
                                                                <strong>{key}:</strong> {String(value)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className={styles.progressLogPreview}>
                                        {progressLog.length > 0 && (
                                            <div className={styles.previewEntry}>
                                                <span
                                                    className={`${styles.previewType} ${
                                                        styles[`logType${progressLog[progressLog.length - 1].type}`]
                                                    }`}
                                                >
                                                    {progressLog[progressLog.length - 1].type.toUpperCase()}
                                                </span>
                                                <span className={styles.previewMessage}>
                                                    {progressLog[progressLog.length - 1].message}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {processingResult && processingResult.success && (
                        <div className={styles.resultSection}>
                            <h4 className={styles.resultTitle}>Processing Complete</h4>
                            <div className={styles.resultGrid}>
                                {processingResult.results && (
                                    <>
                                        <div className={styles.resultItem}>
                                            <span className={styles.resultLabel}>Total Records</span>
                                            <span className={styles.resultValue}>
                                                {processingResult.results.total || 0}
                                            </span>
                                        </div>
                                        <div className={styles.resultItem}>
                                            <span className={styles.resultLabel}>Successful</span>
                                            <span className={styles.resultValue}>
                                                {processingResult.results.successful || 0}
                                            </span>
                                        </div>
                                        <div className={styles.resultItem}>
                                            <span className={styles.resultLabel}>Failed</span>
                                            <span className={styles.resultValue}>
                                                {processingResult.results.failed || 0}
                                            </span>
                                        </div>
                                    </>
                                )}
                                {processingResult.csvUrl && (
                                    <div className={styles.resultItem}>
                                        <span className={styles.resultLabel}>CSV Download</span>
                                        <a
                                            href={processingResult.csvUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.downloadLink}
                                        >
                                            Download CSV
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {processingResult && !processingResult.success && !processing && (
                        <div className={styles.resultSection}>
                            <h4 className={styles.resultTitle}>Run stopped</h4>
                            {processingResult.message && (
                                <p className={styles.progressMessage}>{processingResult.message}</p>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className={styles.detailsCard}>
                <div className={styles.detailsHeader}>
                    <div>
                        <h3>Funding Batch Details</h3>
                        <p className={styles.batchId}>{batchDetails.batch_id}</p>
                    </div>
                    {!isScoreUser && <div className={styles.headerActions} ref={actionsDropdownRef}>
                        <div
                            className={`${styles.actionsDropdown} ${
                                actionsDropdownOpen ? styles.actionsDropdownOpen : ''
                            }`}
                        >
                            <button
                                type="button"
                                className={styles.actionsDropdownTrigger}
                                onClick={() => setActionsDropdownOpen((o) => !o)}
                                aria-expanded={actionsDropdownOpen}
                                aria-haspopup="true"
                            >
                                Actions
                                <FaChevronDown className={styles.actionsDropdownChevron} />
                            </button>
                            {actionsDropdownOpen && (
                                <div className={styles.actionsDropdownMenu}>
                                    {processing && (
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleStopProcessing}
                                        disabled={stoppingProcessing}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaStopCircle />
                                            {stoppingProcessing ? 'Stopping...' : 'Stop run'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Stop the background re-process / allocate / ATE run. Completed records are kept; start again to continue.
                                        </span>
                                    </button>
                                    )}
                                    {repairingBatch && (
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={() => {
                                            setActionsDropdownOpen(false)
                                            handleStopRepair()
                                        }}
                                        disabled={stoppingRepair}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaStopCircle />
                                            {stoppingRepair ? 'Stopping...' : 'Stop repair'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Stop the background repair run. Completed repairs are kept.
                                        </span>
                                    </button>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleDownloadCSV}
                                        disabled={downloadingCSV || !batchDetails}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaFileDownload />
                                            {downloadingCSV ? 'Downloading...' : 'Download CSV'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Export batch clients and claims to CSV for submission or review.
                                        </span>
                                    </button>
                                    {!isBatchProcessed && ateStageCleared && (
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleProcessAllocation}
                                        disabled={processing}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            {processing ? 'Processing...' : 'Process Allocation'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Choose which documents to regenerate vs collect from S3, then run funding
                                            allocation. Calculates values and prepares batch for submission.
                                            {isAteSkipped && !isAteReturned
                                                ? ' ATE is skipped for this batch, so bundles are built without ATE certificates.'
                                                : ''}
                                        </span>
                                    </button>
                                    )}
                                    {canResumeProcessing && (
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleResumeAllocation}
                                        disabled={processing}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaPlay />
                                            {processing ? 'Processing...' : 'Resume Processing'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Continue an interrupted or failed run. Choose regenerate vs collect for
                                            remaining records; completed records are skipped.
                                        </span>
                                    </button>
                                    )}
                                    {!!batchDiagnostics?.submissionExcelUrl && needsAteReprocess && (
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleInsertAteIntoExistingBundles}
                                        disabled={processing}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaIdCard />
                                            {processing ? 'Adding ATE...' : 'Add ATE to Existing Bundles'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Append the returned ATE certificate to each existing bundle. No CFA, LOA,
                                            AOF, TR report or other document is regenerated.
                                        </span>
                                    </button>
                                    )}
                                    {!!batchDiagnostics?.submissionExcelUrl && (
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleReprocessAllocation}
                                        disabled={processing}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaSyncAlt />
                                            {processing ? 'Re-processing...' : 'Re-process Batch'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Choose which documents to regenerate vs collect from S3, optionally preview one claim, then re-generate bundles. Original download links remain valid.
                                        </span>
                                    </button>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleRecalculateQuantums}
                                        disabled={processing}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaCalculator />
                                            {processing ? 'Recalculating...' : 'Recalculate Quantums'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Re-pull each claim's starting balance from the introducer table and recompute quantum_individual and quantum_client_cumulative using this batch's commission %, stat interest % and calculation date.
                                        </span>
                                    </button>
                                    {isBatchProcessed && (
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleRegenerateSubmissionSheet}
                                        disabled={regeneratingSheet}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaFileExcel />
                                            {regeneratingSheet ? 'Regenerating...' : 'Regenerate Submission Sheet'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Rebuild the submission CSV and Excel files using the latest batch data. New download links will be saved.
                                        </span>
                                    </button>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleOpenSplitModal}
                                        disabled={isSplitting || !canSplitBatch || !batchDetails}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaCodeBranch />
                                            {isSplitting ? 'Splitting...' : 'Split Batch'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Move a subset of clients into a new funding batch. Bundles stay intact; only batch membership changes.
                                            <span className={styles.actionsDropdownChecklist}>
                                                {!ateRequestedForSplit ? '✓' : '○'} ATE not requested
                                                {' · '}
                                                {!apiTransferredProcessed ? '✓' : '○'} API transfer not started
                                            </span>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleOpenTransferModal}
                                        disabled={isTransferring || !canTransferByApi || !batchDetails}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaPaperPlane />
                                            {isTransferring ? 'Transferring...' : 'Transfer by API'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Send a percentage of batch clients (and their claims) to Trinity via API.
                                            <span className={styles.actionsDropdownChecklist}>
                                                {isAteReturned ? '✓' : isAteSkipped ? '–' : '○'} ATE returned
                                                {isAteSkipped && !isAteReturned ? ' (skipped)' : ''}
                                                {' · '}
                                                {isProcessedComplete ? '✓' : '○'} Batch processed (Complete)
                                            </span>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleResetApiTransfer}
                                        disabled={resettingTransfer}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaSyncAlt />
                                            {resettingTransfer ? 'Resetting...' : 'Reset API Transfer'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Reset all transfer flags to allow a fresh API transfer attempt (e.g. after audit changes).
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.actionsDropdownItem}
                                        onClick={handleOpenResetMembershipModal}
                                        disabled={resetMembershipPreviewLoading || isResettingMembership || !batchDetails}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaUndo />
                                            {resetMembershipPreviewLoading ? 'Checking…' : 'Reset Batch Membership'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Put every claim that belongs to this batch back in: clear all audit flags and
                                            reinstate claims removed by skip repair or the minimum-value check. Bundles
                                            already built are kept; claims without one are queued for repair.
                                        </span>
                                    </button>
                                    {batchDetails.failed_transfer_csv_url && (
                                        <button
                                            type="button"
                                            className={styles.actionsDropdownItem}
                                            onClick={() =>
                                                window.open(
                                                    batchDetails.failed_transfer_csv_url!,
                                                    '_blank',
                                                    'noopener,noreferrer'
                                                )
                                            }
                                        >
                                            <span className={styles.actionsDropdownItemLabel}>
                                                <FaFileDownload />
                                                Download Trinity failure report
                                            </span>
                                            <span className={styles.actionsDropdownItemDesc}>
                                                Download the CSV of failed API transfers for this batch (if any failures
                                                occurred).
                                            </span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className={`${styles.actionsDropdownItem} ${styles.actionsDropdownItemDanger}`}
                                        onClick={handleOpenDeleteModal}
                                        disabled={isDeleting}
                                    >
                                        <span className={styles.actionsDropdownItemLabel}>
                                            <FaTrashAlt />
                                            {isDeleting ? 'Deleting...' : 'Delete Batch'}
                                        </span>
                                        <span className={styles.actionsDropdownItemDesc}>
                                            Permanently remove this batch and all associated client and claim records.
                                            Cannot be undone.
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>}
                </div>

                {/* Batch steps timeline – under title, spread out with titles, descriptions and action under current step */}
                {(() => {
                    const ateRequestedDone = ateRequested || !!batchDetails.ate_requested
                    // ATE deferred and not yet caught up: the two ATE steps are
                    // shown as skipped so the timeline reads through to Processed.
                    const ateSkippedPending = isAteSkipped && !isAteReturned
                    const processedState = batchDiagnostics?.processed
                    const processedComplete =
                        processedState === 'Complete' || processedState === 'Complete with errors'
                    const processedStarted = processedState === 'Started'
                    const processedError = processedState === 'Complete with errors'
                    const processedStale = processedStarted && !processing
                    const submissionDone =
                        batchDiagnostics?.processed === 'Complete' &&
                        !!(batchDiagnostics.submissionExcelUrl || batchDiagnostics.submissionCsvUrl)
                    const steps: Array<{
                        key: string
                        label: string
                        description: string
                        complete: boolean
                        inProgress?: boolean
                        isCurrent?: boolean
                        actionLabel?: string
                        onAction?: () => void
                        actionDisabled?: boolean
                        secondaryActionLabel?: string
                        onSecondaryAction?: () => void
                        secondaryActionDisabled?: boolean
                        nodeState?: 'complete' | 'inProgress' | 'error'
                        downloadUrl?: string | null
                        downloadLabel?: string
                        downloads?: Array<{ url: string; label: string }>
                    }> = [
                        {
                            key: 'initial',
                            label: 'Initial',
                            description: 'Batch created and ready for ATE.',
                            complete: true,
                        },
                        {
                            key: 'ate_requested',
                            label: ateSkippedPending ? 'ATE Requested (skipped)' : 'ATE Requested',
                            description: ateSkippedPending
                                ? 'ATE is skipped for this batch. Request it whenever you are ready — the batch does not wait for it.'
                                : 'Request ATE from Temple. Once requested, collect certificates when returned.',
                            complete: ateRequestedDone,
                            isCurrent: !ateRequestedDone && !isAteSkipped,
                            actionLabel: requestingATE ? 'Requesting…' : 'Request ATE',
                            onAction: handleRequestATE,
                            actionDisabled: requestingATE || !batchDetails,
                            secondaryActionLabel: updatingAteSkip ? 'Skipping…' : 'Skip ATE for now',
                            onSecondaryAction: () => handleSetAteSkipped(true),
                            secondaryActionDisabled: updatingAteSkip || !batchDetails,
                            downloadUrl: ateRequestedDone ? batchDetails.config?.ate_requested_csv_link ?? null : null,
                            downloadLabel: 'Download CSV',
                        },
                        {
                            key: 'ate_returned',
                            label: ateSkippedPending ? 'ATE Returned (skipped)' : 'ATE Returned',
                            description: ateSkippedPending
                                ? 'Skipped: the batch is processed without ATE certificates. Collect them later, then use Add ATE to Existing Bundles.'
                                : 'ATE certificates collected and linked to this batch.',
                            complete: isAteReturned,
                            isCurrent: ateRequestedDone && !isAteReturned && !isAteSkipped,
                            actionLabel: collectingAteCertificates
                                ? 'Starting…'
                                : isAteReturned
                                ? 'Collect certificates'
                                : 'Collect ATE certificates',
                            onAction: handleCollectAteCertificates,
                            actionDisabled: collectingAteCertificates || !batchDetails,
                            secondaryActionLabel: updatingAteSkip ? 'Skipping…' : 'Skip ATE for now',
                            onSecondaryAction: () => handleSetAteSkipped(true),
                            secondaryActionDisabled: updatingAteSkip || !batchDetails,
                            downloadUrl: isAteReturned ? batchDetails.config?.ate_returned_csv_link ?? null : null,
                            downloadLabel: 'Download CSV',
                        },
                        {
                            key: 'processed',
                            label:
                                processedError
                                    ? 'Complete with errors'
                                    : processedState === 'Complete'
                                    ? 'Complete'
                                    : processedStale
                                    ? 'Failed'
                                    : processedStarted
                                    ? 'Started'
                                    : 'Processed',
                            description: processedError
                                ? 'Some records failed. Download the error report and run Repair batch.'
                                : processedStale
                                ? 'Processing was interrupted. Repair the batch to fix any incomplete records, or re-run the allocation.'
                                : processedStarted
                                ? 'Document bundles and submission files are being generated.'
                                : processedState === 'Complete'
                                ? needsAteReprocess
                                    ? 'All records processed. ATE certificates arrived after processing — add them to the existing bundles without regenerating any other document.'
                                    : 'All records processed. Submission files ready to download.'
                                : ateSkippedPending
                                ? 'Run funding allocation to generate document packs and submission files. ATE is skipped, so bundles are built without ATE certificates.'
                                : 'Run funding allocation to generate document packs and submission files.',
                            complete: processedComplete,
                            inProgress: processedStarted && !processedStale,
                            isCurrent: processedStale || (ateStageCleared && !processedComplete && !processedStarted),
                            actionLabel: processedError || processedStale
                                ? repairingBatch
                                    ? 'Repairing…'
                                    : 'Repair batch'
                                : processing
                                ? 'Processing…'
                                : 'Process Allocation',
                            onAction: processedError || processedStale ? handleRepairBatch : handleProcessAllocation,
                            actionDisabled: processedError || processedStale
                                ? repairingBatch || !batchDetails
                                : processing || !batchId,
                            nodeState: processedStarted && !processedStale
                                ? 'inProgress'
                                : processedError || processedStale
                                ? 'error'
                                : processedState === 'Complete'
                                ? 'complete'
                                : undefined,
                        },
                        {
                            key: 'submission',
                            label: 'Submission Excel',
                            description: 'Download submission CSV/Excel for the funder.',
                            complete: submissionDone,
                            isCurrent: processedComplete && !submissionDone && batchDiagnostics?.processed === 'Complete',
                            downloads:
                                submissionDone && (batchDiagnostics?.submissionCsvUrl || batchDiagnostics?.submissionExcelUrl)
                                    ? [
                                          ...(batchDiagnostics.submissionCsvUrl
                                              ? [{ url: batchDiagnostics.submissionCsvUrl, label: 'CSV' }]
                                              : []),
                                          ...(batchDiagnostics.submissionExcelUrl
                                              ? [{ url: batchDiagnostics.submissionExcelUrl, label: 'Excel' }]
                                              : []),
                                      ]
                                    : undefined,
                        },
                        {
                            key: 'api_transferred',
                            label: 'API Transferred',
                            description: 'Send clients to Trinity via API. Optional; transfer a percentage or all.',
                            complete: apiTransferredProcessed,
                            isCurrent: isProcessedComplete && ateStageCleared && !apiTransferredProcessed,
                            actionLabel: isTransferring ? 'Transferring…' : 'Transfer by API',
                            onAction: handleOpenTransferModal,
                            actionDisabled: isTransferring || !canTransferByApi || !batchDetails,
                        },
                    ]
                    const stepCount = steps.length
                    const isCarousel = timelineVisibleCount < 6
                    const stripWidthPercent = isCarousel ? (stepCount / timelineVisibleCount) * 100 : 100
                    /* translateX % is relative to the strip width; one step = 100/stepCount % of strip */
                    const transformPercent = isCarousel && stepCount > 0
                        ? (timelineCarouselIndex * 100) / stepCount
                        : 0
                    return (
                        <div
                            className={`${styles.batchStepsTimeline} ${isCarousel ? styles.batchStepsTimelineCarousel : ''}`}
                        >
                            {isCarousel && (
                                <button
                                    type="button"
                                    className={styles.batchStepsCarouselArrow}
                                    aria-label="Previous step"
                                    onClick={() => setTimelineCarouselIndex((i) => Math.max(0, i - 1))}
                                    disabled={timelineCarouselIndex <= 0}
                                >
                                    <FaChevronLeft aria-hidden />
                                </button>
                            )}
                            <div className={styles.batchStepsCarouselTrack}>
                                <div
                                    className={styles.batchStepsCarouselStrip}
                                    style={
                                        {
                                            '--timeline-steps': stepCount,
                                            '--timeline-visible': timelineVisibleCount,
                                            width: `${stripWidthPercent}%`,
                                            transform: isCarousel
                                                ? `translateX(-${transformPercent}%)`
                                                : 'none',
                                        } as React.CSSProperties
                                    }
                                >
                                    {steps.map((step, index) => {
                                const nodeComplete = step.nodeState === 'complete' || (step.complete && !step.nodeState)
                                const nodeInProgress = step.nodeState === 'inProgress' || (step.inProgress && !step.nodeState)
                                const nodeError = step.nodeState === 'error'
                                const nodeClassName = [
                                    styles.batchStepNode,
                                    nodeError
                                        ? styles.batchStepNodeError
                                        : nodeComplete
                                        ? styles.batchStepNodeComplete
                                        : nodeInProgress
                                        ? styles.batchStepNodeWarning
                                        : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')
                                const showIcon = nodeComplete || nodeInProgress || nodeError
                                return (
                                    <div key={step.key} className={styles.batchStepItem}>
                                        {index > 0 && <div className={styles.batchStepConnector} />}
                                        <div
                                            className={nodeClassName}
                                            title={
                                                step.complete
                                                    ? `${step.label} – done`
                                                    : step.inProgress
                                                    ? `${step.label} – in progress`
                                                    : nodeError
                                                    ? `${step.label} – repair required`
                                                    : step.label
                                            }
                                        >
                                            {nodeComplete ? (
                                                <FaCheckCircle className={styles.batchStepIcon} aria-hidden />
                                            ) : nodeInProgress ? (
                                                <FaCircleNotch
                                                    className={`${styles.batchStepIcon} ${styles.batchStepIconSpin}`}
                                                    aria-hidden
                                                />
                                            ) : nodeError ? (
                                                <FaExclamationTriangle className={styles.batchStepIcon} aria-hidden />
                                            ) : (
                                                <span className={styles.batchStepDot} aria-hidden />
                                            )}
                                        </div>
                                        <div className={styles.batchStepContent}>
                                            <h5 className={styles.batchStepTitle}>{step.label}</h5>
                                            <p className={styles.batchStepDescription}>{step.description}</p>
                                            {!isScoreUser && (step.isCurrent || step.inProgress || nodeError) &&
                                                step.onAction &&
                                                step.actionLabel &&
                                                !(step.key === 'processed' && nodeError) && (
                                                    <div className={styles.batchStepActions}>
                                                        <button
                                                            type="button"
                                                            className={styles.batchStepActionButton}
                                                            onClick={step.onAction}
                                                            disabled={step.actionDisabled}
                                                        >
                                                            {step.actionLabel}
                                                        </button>
                                                        {step.onSecondaryAction && step.secondaryActionLabel && (
                                                            <button
                                                                type="button"
                                                                className={styles.batchStepActionButtonSecondary}
                                                                onClick={step.onSecondaryAction}
                                                                disabled={step.secondaryActionDisabled}
                                                            >
                                                                {step.secondaryActionLabel}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                        </div>
                                        <div className={styles.batchStepDownloadSlot}>
                                            {step.key === 'processed' && nodeError ? (
                                                !isScoreUser && <div className={styles.batchStepErrorActions}>
                                                    <button
                                                        type="button"
                                                        className={styles.batchStepActionButton}
                                                        onClick={step.onAction}
                                                        disabled={step.actionDisabled}
                                                    >
                                                        {step.actionLabel}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.batchStepActionButtonSecondary}
                                                        onClick={handleSkipRepair}
                                                        disabled={repairingBatch || skippingRepair || !batchDetails}
                                                    >
                                                        {skippingRepair ? 'Skipping…' : 'Skip repair'}
                                                    </button>
                                                </div>
                                            ) : step.downloadUrl ? (
                                                <a
                                                    href={step.downloadUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.batchStepDownloadLink}
                                                >
                                                    <FaFileDownload aria-hidden />
                                                    {step.downloadLabel ?? 'Download'}
                                                </a>
                                            ) : step.downloads && step.downloads.length > 0 ? (
                                                <div className={styles.batchStepDownloadRow}>
                                                    {step.downloads.map((d) => (
                                                        <a
                                                            key={d.label}
                                                            href={d.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.batchStepDownloadLink}
                                                        >
                                                            <FaFileDownload aria-hidden />
                                                            {d.label}
                                                        </a>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                )
                            })}
                                </div>
                            </div>
                            {isCarousel && (
                                <button
                                    type="button"
                                    className={styles.batchStepsCarouselArrow}
                                    aria-label="Next step"
                                    onClick={() =>
                                        setTimelineCarouselIndex((i) => Math.min(stepCount - 1, i + 1))
                                    }
                                    disabled={timelineCarouselIndex >= stepCount - 1}
                                >
                                    <FaChevronRight aria-hidden />
                                </button>
                            )}
                        </div>
                    )
                })()}

                {/* Batch Summary */}
                {batchDetails.batch_summary && (
                    <div className={styles.collapsibleSection}>
                        <button
                            type="button"
                            className={styles.collapsibleSectionHeader}
                            onClick={() => setBatchSummaryOpen((o) => !o)}
                            aria-expanded={batchSummaryOpen}
                        >
                            {batchSummaryOpen ? (
                                <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                            ) : (
                                <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                            )}
                            <h4 className={styles.collapsibleSectionTitle}>Batch Summary</h4>
                        </button>
                        {batchSummaryOpen && (
                    <div className={styles.summarySection}>
                        <div className={styles.summaryGrid}>
                            {/* Overview Card - Key Metrics */}
                            <div className={styles.summaryCard}>
                                <div className={styles.summaryCardHeader}>
                                    <FaUsers className={styles.summaryCardIcon} />
                                    <h5 className={styles.summaryCardTitle}>Overview</h5>
                                </div>
                                <div className={styles.summaryCardContent}>
                                    <div className={styles.summaryMetric}>
                                        <div className={styles.summaryMetricIconWrapper}>
                                            <FaUsers className={styles.summaryMetricIcon} />
                                        </div>
                                        <div className={styles.summaryMetricContent}>
                                            <span className={styles.summaryMetricLabel}>Total Clients</span>
                                            <span className={styles.summaryMetricValue}>
                                                {batchDetails.batch_summary.total_clients.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.summaryMetric}>
                                        <div className={styles.summaryMetricIconWrapper}>
                                            <FaFileContract className={styles.summaryMetricIcon} />
                                        </div>
                                        <div className={styles.summaryMetricContent}>
                                            <span className={styles.summaryMetricLabel}>Total Claims</span>
                                            <span className={styles.summaryMetricValue}>
                                                {batchDetails.batch_summary.total_claims.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                    {batchDetails.config &&
                                        batchDetails.config.max_clients !== null &&
                                        batchDetails.config.max_clients !== undefined &&
                                        batchDetails.config.max_clients !== '' && (
                                            <div className={styles.summaryMetric}>
                                                <div className={styles.summaryMetricIconWrapper}>
                                                    <FaUserCheck className={styles.summaryMetricIcon} />
                                                </div>
                                                <div className={styles.summaryMetricContent}>
                                                    <span className={styles.summaryMetricLabel}>Max Clients</span>
                                                    <span className={styles.summaryMetricValue}>
                                                        {batchDetails.config.max_clients}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                </div>
                            </div>

                            {/* API Transfer Card */}
                            {(batchDetails.batch_summary.total_clients > 0 ||
                                (batchDetails.batch_summary.transferred_claims ?? 0) > 0) && (
                                <div className={styles.summaryCard}>
                                    <div className={styles.summaryCardHeader}>
                                        <FaPaperPlane className={styles.summaryCardIcon} />
                                        <h5 className={styles.summaryCardTitle}>API Transfer</h5>
                                    </div>
                                    <div className={styles.summaryCardContent}>
                                        <div className={styles.summaryMetric}>
                                            <div className={styles.summaryMetricIconWrapper}>
                                                <FaUsers className={styles.summaryMetricIcon} />
                                            </div>
                                            <div className={styles.summaryMetricContent}>
                                                <span className={styles.summaryMetricLabel}>Clients transferred</span>
                                                <span className={styles.summaryMetricValue}>
                                                    {(
                                                        batchDetails.batch_summary.transferred_clients ?? 0
                                                    ).toLocaleString()}{' '}
                                                    of {batchDetails.batch_summary.total_clients.toLocaleString()} (
                                                    {batchDetails.batch_summary.total_clients > 0
                                                        ? (
                                                              ((batchDetails.batch_summary.transferred_clients ?? 0) /
                                                                  batchDetails.batch_summary.total_clients) *
                                                              100
                                                          ).toFixed(1)
                                                        : '0'}
                                                    %)
                                                </span>
                                            </div>
                                        </div>
                                        <div className={styles.summaryMetric}>
                                            <div className={styles.summaryMetricIconWrapper}>
                                                <FaFileContract className={styles.summaryMetricIcon} />
                                            </div>
                                            <div className={styles.summaryMetricContent}>
                                                <span className={styles.summaryMetricLabel}>Claims transferred</span>
                                                <span className={styles.summaryMetricValue}>
                                                    {(
                                                        batchDetails.batch_summary.transferred_claims ?? 0
                                                    ).toLocaleString()}{' '}
                                                    of {batchDetails.batch_summary.total_claims.toLocaleString()} (
                                                    {batchDetails.batch_summary.total_claims > 0
                                                        ? (
                                                              ((batchDetails.batch_summary.transferred_claims ?? 0) /
                                                                  batchDetails.batch_summary.total_claims) *
                                                              100
                                                          ).toFixed(1)
                                                        : '0'}
                                                    %)
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* AML Identity Documents Card (fairclaim batches) */}
                            {(amlSummaryLoading || amlSummary?.applicable) && (
                                <div className={styles.summaryCard}>
                                    <div className={styles.summaryCardHeader}>
                                        <FaIdCard className={styles.summaryCardIcon} />
                                        <h5 className={styles.summaryCardTitle}>AML Identity Documents</h5>
                                    </div>
                                    <div className={styles.summaryCardContent}>
                                        {amlSummaryLoading ? (
                                            <div className={styles.summaryMetric}>
                                                <div className={styles.summaryMetricIconWrapper}>
                                                    <FaCircleNotch className={`${styles.summaryMetricIcon} ${styles.spinIcon}`} />
                                                </div>
                                                <div className={styles.summaryMetricContent}>
                                                    <span className={styles.summaryMetricLabel}>
                                                        Checking AML documents...
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className={styles.summaryMetric}>
                                                    <div className={styles.summaryMetricIconWrapper}>
                                                        <FaIdCard className={styles.summaryMetricIcon} />
                                                    </div>
                                                    <div className={styles.summaryMetricContent}>
                                                        <span className={styles.summaryMetricLabel}>
                                                            Cases with AML document
                                                        </span>
                                                        <span className={styles.summaryMetricValue}>
                                                            {(amlSummary?.amlCount ?? 0).toLocaleString()} of{' '}
                                                            {(amlSummary?.eligibleCount ?? 0).toLocaleString()} (
                                                            {(amlSummary?.eligibleCount ?? 0) > 0
                                                                ? (
                                                                      ((amlSummary?.amlCount ?? 0) /
                                                                          (amlSummary?.eligibleCount ?? 1)) *
                                                                      100
                                                                  ).toFixed(1)
                                                                : '0'}
                                                            %)
                                                        </span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className={styles.amlDownloadButton}
                                                    onClick={handleDownloadAmlReport}
                                                    disabled={downloadingAmlReport || (amlSummary?.amlCount ?? 0) === 0}
                                                >
                                                    {downloadingAmlReport ? (
                                                        <>
                                                            <FaCircleNotch className={styles.spinIcon} /> Preparing
                                                            report...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaFileDownload /> Download AML claims list
                                                        </>
                                                    )}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Financial Card */}
                            <div className={styles.summaryCard}>
                                <div className={styles.summaryCardHeader}>
                                    <FaPoundSign className={styles.summaryCardIcon} />
                                    <h5 className={styles.summaryCardTitle}>Financial</h5>
                                </div>
                                <div className={styles.summaryCardContent}>
                                    <div className={styles.summaryMetric}>
                                        <div className={styles.summaryMetricIconWrapper}>
                                            <FaPoundSign className={styles.summaryMetricIcon} />
                                        </div>
                                        <div className={styles.summaryMetricContent}>
                                            <span className={styles.summaryMetricLabel}>Total Book Value</span>
                                            <span
                                                className={`${styles.summaryMetricValue} ${styles.summaryMetricValueLarge}`}
                                            >
                                                {formatCurrency(batchDetails.batch_summary.total_book_value)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.summaryMetric}>
                                        <div className={styles.summaryMetricIconWrapper}>
                                            <FaMoneyBillWave className={styles.summaryMetricIcon} />
                                        </div>
                                        <div className={styles.summaryMetricContent}>
                                            <span className={styles.summaryMetricLabel}>Drawdown</span>
                                            <span className={styles.summaryMetricValue}>
                                                {formatCurrency((batchDetails.batch_summary?.total_clients ?? 0) * 300)}
                                            </span>
                                        </div>
                                    </div>
                                    {batchDetails.config &&
                                        batchDetails.config.min_client_value !== null &&
                                        batchDetails.config.min_client_value !== undefined &&
                                        batchDetails.config.min_client_value !== '' && (
                                            <div className={styles.summaryMetric}>
                                                <div className={styles.summaryMetricIconWrapper}>
                                                    <FaMoneyBillWave className={styles.summaryMetricIcon} />
                                                </div>
                                                <div className={styles.summaryMetricContent}>
                                                    <span className={styles.summaryMetricLabel}>Min Client Value</span>
                                                    <span className={styles.summaryMetricValue}>
                                                        {typeof batchDetails.config.min_client_value === 'number'
                                                            ? `£${batchDetails.config.min_client_value.toLocaleString(
                                                                  'en-GB',
                                                                  {
                                                                      minimumFractionDigits: 2,
                                                                      maximumFractionDigits: 2,
                                                                  }
                                                              )}`
                                                            : String(batchDetails.config.min_client_value).startsWith(
                                                                  '£'
                                                              )
                                                            ? batchDetails.config.min_client_value
                                                            : `£${batchDetails.config.min_client_value}`}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                </div>
                            </div>

                            {/* Configuration Card */}
                            {(batchDetails.config?.commission_rate ||
                                batchDetails.config?.stat_interest ||
                                batchDetails.config?.calculation_date) && (
                                <div className={styles.summaryCard}>
                                    <div className={styles.summaryCardHeader}>
                                        <FaChartLine className={styles.summaryCardIcon} />
                                        <h5 className={styles.summaryCardTitle}>Configuration</h5>
                                    </div>
                                    <div className={styles.summaryCardContent}>
                                        {batchDetails.config.commission_rate !== null &&
                                            batchDetails.config.commission_rate !== undefined &&
                                            batchDetails.config.commission_rate !== '' && (
                                                <div className={styles.summaryMetric}>
                                                    <div className={styles.summaryMetricIconWrapper}>
                                                        <FaHandHoldingUsd className={styles.summaryMetricIcon} />
                                                    </div>
                                                    <div className={styles.summaryMetricContent}>
                                                        <span className={styles.summaryMetricLabel}>
                                                            Commission Rate
                                                        </span>
                                                        <span className={styles.summaryMetricValue}>
                                                            {typeof batchDetails.config.commission_rate === 'number'
                                                                ? `${batchDetails.config.commission_rate}%`
                                                                : String(batchDetails.config.commission_rate).includes(
                                                                      '%'
                                                                  )
                                                                ? batchDetails.config.commission_rate
                                                                : `${batchDetails.config.commission_rate}%`}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        {batchDetails.config.stat_interest !== null &&
                                            batchDetails.config.stat_interest !== undefined &&
                                            batchDetails.config.stat_interest !== '' && (
                                                <div className={styles.summaryMetric}>
                                                    <div className={styles.summaryMetricIconWrapper}>
                                                        <FaChartLine className={styles.summaryMetricIcon} />
                                                    </div>
                                                    <div className={styles.summaryMetricContent}>
                                                        <span className={styles.summaryMetricLabel}>Stat Interest</span>
                                                        <span className={styles.summaryMetricValue}>
                                                            {typeof batchDetails.config.stat_interest === 'number'
                                                                ? `${batchDetails.config.stat_interest}%`
                                                                : String(batchDetails.config.stat_interest).includes(
                                                                      '%'
                                                                  )
                                                                ? batchDetails.config.stat_interest
                                                                : `${batchDetails.config.stat_interest}%`}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        {batchDetails.config.calculation_date && (
                                            <div className={styles.summaryMetric}>
                                                <div className={styles.summaryMetricIconWrapper}>
                                                    <FaCalendarAlt className={styles.summaryMetricIcon} />
                                                </div>
                                                <div className={styles.summaryMetricContent}>
                                                    <span className={styles.summaryMetricLabel}>Calculation Date</span>
                                                    <span className={styles.summaryMetricValue}>
                                                        {new Date(
                                                            batchDetails.config.calculation_date
                                                        ).toLocaleDateString('en-GB', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                        })}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                        )}
                    </div>
                )}

                {/* Funded claims by approved-lender category (funder_defendants.category) */}
                {/* ATE Status */}
                <div className={styles.collapsibleSection}>
                    <button
                        type="button"
                        className={styles.collapsibleSectionHeader}
                        onClick={() => setAteStatusOpen((o) => !o)}
                        aria-expanded={ateStatusOpen}
                    >
                        {ateStatusOpen ? (
                            <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                        ) : (
                            <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                        )}
                        <h4 className={styles.collapsibleSectionTitle}>ATE Status</h4>
                    </button>
                    {ateStatusOpen && (
                    <>
                    <div className={styles.ateStatusSection}>
                        <div className={styles.ateStatusCard}>
                            <div className={styles.ateStatusCardHeader}>
                                <FaCheckCircle className={styles.ateStatusCardIcon} />
                                <h5 className={styles.ateStatusCardTitle}>Status</h5>
                            </div>
                            <div className={styles.ateStatusCardContent}>
                                <div className={styles.ateStatusMetric}>
                                    <div
                                        className={`${styles.ateStatusMetricIconWrapper} ${
                                            batchDetails.ate_requested === undefined
                                                ? styles.ateStatusIconUndefined
                                                : batchDetails.ate_requested
                                                ? styles.ateStatusIconSuccess
                                                : styles.ateStatusIconError
                                        }`}
                                    >
                                        {batchDetails.ate_requested === undefined ? (
                                            <FaClock className={styles.ateStatusMetricIcon} />
                                        ) : batchDetails.ate_requested ? (
                                            <FaCheckCircle className={styles.ateStatusMetricIcon} />
                                        ) : (
                                            <FaTimesCircle className={styles.ateStatusMetricIcon} />
                                        )}
                                    </div>
                                    <div className={styles.ateStatusMetricContent}>
                                        <span className={styles.ateStatusMetricLabel}>ATE Requested</span>
                                        <span
                                            className={`${styles.ateStatusBadge} ${
                                                batchDetails.ate_requested === undefined
                                                    ? styles.ateUndefined
                                                    : batchDetails.ate_requested
                                                    ? styles.ateRequested
                                                    : styles.ateNotRequested
                                            }`}
                                        >
                                            {batchDetails.ate_requested === undefined
                                                ? 'Undefined'
                                                : batchDetails.ate_requested
                                                ? 'Yes'
                                                : 'No'}
                                        </span>
                                    </div>
                                </div>
                                {batchDetails.ate_requested_date && (
                                    <div className={styles.ateStatusMetric}>
                                        <div className={styles.ateStatusMetricIconWrapper}>
                                            <FaCalendarAlt className={styles.ateStatusMetricIcon} />
                                        </div>
                                        <div className={styles.ateStatusMetricContent}>
                                            <span className={styles.ateStatusMetricLabel}>ATE Requested Date</span>
                                            <span className={styles.ateStatusMetricValue}>
                                                {new Date(batchDetails.ate_requested_date).toLocaleDateString('en-GB', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <div className={styles.ateStatusMetric}>
                                    <div
                                        className={`${styles.ateStatusMetricIconWrapper} ${
                                            batchDetails.ate_returned === undefined
                                                ? styles.ateStatusIconUndefined
                                                : batchDetails.ate_returned
                                                ? styles.ateStatusIconSuccess
                                                : styles.ateStatusIconError
                                        }`}
                                    >
                                        {batchDetails.ate_returned === undefined ? (
                                            <FaClock className={styles.ateStatusMetricIcon} />
                                        ) : batchDetails.ate_returned ? (
                                            <FaCheckCircle className={styles.ateStatusMetricIcon} />
                                        ) : (
                                            <FaTimesCircle className={styles.ateStatusMetricIcon} />
                                        )}
                                    </div>
                                    <div className={styles.ateStatusMetricContent}>
                                        <span className={styles.ateStatusMetricLabel}>ATE Returned</span>
                                        <span
                                            className={`${styles.ateStatusBadge} ${
                                                batchDetails.ate_returned ? styles.ateReturned : styles.ateNotReturned
                                            }`}
                                        >
                                            {batchDetails.ate_returned === undefined
                                                ? 'Undefined'
                                                : batchDetails.ate_returned
                                                ? 'Yes'
                                                : 'No'}
                                        </span>
                                    </div>
                                </div>
                                {isAteSkipped && (
                                    <div className={styles.ateStatusMetric}>
                                        <div
                                            className={`${styles.ateStatusMetricIconWrapper} ${styles.ateStatusIconUndefined}`}
                                        >
                                            <FaClock className={styles.ateStatusMetricIcon} />
                                        </div>
                                        <div className={styles.ateStatusMetricContent}>
                                            <span className={styles.ateStatusMetricLabel}>ATE Stage</span>
                                            <span className={`${styles.ateStatusBadge} ${styles.ateSkipped}`}>
                                                {isAteReturned ? 'Skipped – certificates now collected' : 'Skipped'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {needsAteReprocess && (
                                    <div className={styles.ateReprocessNotice}>
                                        <p className={styles.ateReprocessNoticeText}>
                                            This batch was processed without ATE. The certificates have now been
                                            collected — add them to the existing bundles without regenerating any
                                            other document. Existing download links stay valid.
                                        </p>
                                        {!isScoreUser && (
                                            <button
                                                type="button"
                                                className={styles.ateActionButton}
                                                onClick={handleInsertAteIntoExistingBundles}
                                                disabled={processing}
                                            >
                                                {processing ? 'Adding ATE…' : 'Add ATE to existing bundles'}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* ATE actions: Request ATE + Collect certificates (only when requested; collect allowed even if returned, shown subtly) */}
                                <div className={styles.ateActionsRow}>
                                    {!isScoreUser && (
                                        <>
                                    {!ateRequested && !isAteReturned && (
                                        <button
                                            type="button"
                                            className={styles.ateActionButton}
                                            onClick={handleRequestATE}
                                            disabled={requestingATE || !batchDetails}
                                        >
                                            {requestingATE ? 'Requesting…' : 'Request ATE'}
                                        </button>
                                    )}
                                    {(ateRequested || batchDetails.ate_requested) && (
                                        <button
                                            type="button"
                                            className={
                                                isAteReturned
                                                    ? styles.ateActionButtonSubtle
                                                    : styles.ateActionButton
                                            }
                                            onClick={handleCollectAteCertificates}
                                            disabled={collectingAteCertificates || !batchDetails}
                                            title={
                                                isAteReturned
                                                    ? 'Collect ATE certificates from Temple FTP (can be run again if needed)'
                                                    : 'Collect ATE certificates from Temple FTP. Runs in background.'
                                            }
                                        >
                                            {collectingAteCertificates
                                                ? 'Starting…'
                                                : isAteReturned
                                                ? 'Collect certificates'
                                                : 'Collect ATE certificates'}
                                        </button>
                                    )}
                                    {(ateRequested || batchDetails.ate_requested) && (
                                        <button
                                            type="button"
                                            className={styles.ateActionButtonSubtle}
                                            onClick={handleReRequestATE}
                                            disabled={reRequestingATE || !batchDetails}
                                            title="Clear all ATE data and re-create the ATE submission sheet"
                                        >
                                            {reRequestingATE ? 'Re-requesting…' : 'Re-request ATE'}
                                        </button>
                                    )}
                                    {!isAteReturned && !isAteSkipped && (
                                        <button
                                            type="button"
                                            className={styles.ateActionButtonSubtle}
                                            onClick={() => handleSetAteSkipped(true)}
                                            disabled={updatingAteSkip || !batchDetails}
                                            title="Process this batch without ATE certificates. ATE can be requested later and added to the bundles by re-processing."
                                        >
                                            {updatingAteSkip ? 'Updating…' : 'Skip ATE for now'}
                                        </button>
                                    )}
                                    {isAteSkipped && !isAteReturned && (
                                        <button
                                            type="button"
                                            className={styles.ateActionButtonSubtle}
                                            onClick={() => handleSetAteSkipped(false)}
                                            disabled={updatingAteSkip || !batchDetails}
                                            title="Require ATE certificates before this batch can be processed"
                                        >
                                            {updatingAteSkip ? 'Updating…' : 'Require ATE again'}
                                        </button>
                                    )}
                                    {(ateRequested || batchDetails.ate_requested) && !isAteReturned && (
                                        <button
                                            type="button"
                                            className={styles.ateActionButtonSubtle}
                                            onClick={handleMarkAteReturned}
                                            disabled={markingAteReturned || !batchDetails}
                                            title="Accept an incomplete Temple return: mark the ATE stage as returned even though some clients have no certificate. You will see how many are missing before confirming."
                                        >
                                            {markingAteReturned ? 'Checking…' : 'Mark ATE as returned'}
                                        </button>
                                    )}
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.ateActionButtonSubtle}
                                        onClick={handleDownloadAtePolicies}
                                        disabled={downloadingAtePolicies || !batchDetails}
                                        title="Download a CSV of active claim references with ATE policy numbers and inception dates"
                                    >
                                        <FaFileDownload />
                                        {downloadingAtePolicies ? 'Downloading…' : 'Download ATE policies CSV'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.ateActionButtonSubtle}
                                        onClick={handleDownloadTempleCsv}
                                        disabled={downloadingTempleCsv || !batchDetails}
                                        title="Download the actual import-policies CSV Temple returned (S3 copy; recovered from the Temple FTP server and saved to S3 if missing)"
                                    >
                                        <FaFileDownload />
                                        {downloadingTempleCsv ? 'Locating…' : 'Download Temple import CSV'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.ateActionButtonSubtle}
                                        onClick={handleVerifyAteImport}
                                        disabled={verifyingAteImport || !batchDetails}
                                        title="Check every policy ID, policy number and inception date on the Temple return sheet is in funder_references / funder_claims for this batch"
                                    >
                                        {verifyingAteImport ? <FaCircleNotch className={styles.spinIcon} /> : <FaClipboardCheck />}
                                        {verifyingAteImport ? 'Verifying…' : 'Verify imported policies'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* CSV Downloads Card */}
                        {(batchDetails.config?.ate_requested_csv_link ||
                            batchDetails.config?.ate_returned_csv_link ||
                            ateReturnFiles.length > 0 ||
                            ateSupplementaryRequests.length > 0) && (
                            <div className={styles.ateStatusCard}>
                                <div className={styles.ateStatusCardHeader}>
                                    <FaFileDownload className={styles.ateStatusCardIcon} />
                                    <h5 className={styles.ateStatusCardTitle}>Downloads</h5>
                                </div>
                                <div className={styles.ateStatusCardContent}>
                                    {batchDetails.config?.ate_requested_csv_link && (
                                        <div className={styles.ateStatusMetric}>
                                            <div className={styles.ateStatusMetricIconWrapper}>
                                                <FaFileDownload className={styles.ateStatusMetricIcon} />
                                            </div>
                                            <div className={styles.ateStatusMetricContent}>
                                                <span className={styles.ateStatusMetricLabel}>ATE Requested CSV</span>
                                                <a
                                                    href={batchDetails.config.ate_requested_csv_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.ateCsvLink}
                                                >
                                                    Download CSV
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                    {batchDetails.config?.ate_returned_csv_link && (
                                        <div className={styles.ateStatusMetric}>
                                            <div className={styles.ateStatusMetricIconWrapper}>
                                                <FaFileDownload className={styles.ateStatusMetricIcon} />
                                            </div>
                                            <div className={styles.ateStatusMetricContent}>
                                                <span className={styles.ateStatusMetricLabel}>ATE Returned CSV</span>
                                                <a
                                                    href={batchDetails.config.ate_returned_csv_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.ateCsvLink}
                                                >
                                                    Download CSV
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                    {/* Every Temple return file stored in S3 for this batch (original + later returns) */}
                                    {ateReturnFiles.length > 0 && (
                                        <div className={styles.ateStatusMetric}>
                                            <div className={styles.ateStatusMetricIconWrapper}>
                                                <FaFileDownload className={styles.ateStatusMetricIcon} />
                                            </div>
                                            <div className={styles.ateStatusMetricContent}>
                                                <span className={styles.ateStatusMetricLabel}>
                                                    Temple import CSV{ateReturnFiles.length === 1 ? '' : 's'} (S3)
                                                </span>
                                                <ul className={styles.ateFileList}>
                                                    {ateReturnFiles.map((file) => (
                                                        <li key={file.key}>
                                                            <a
                                                                href={file.fileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={styles.ateCsvLink}
                                                            >
                                                                {file.canonical ? 'Original return' : file.key.split('/').pop()}
                                                            </a>
                                                            <span className={styles.ateFileMeta}>
                                                                {file.uploadedAt
                                                                    ? new Date(file.uploadedAt).toLocaleDateString('en-GB', {
                                                                          year: 'numeric',
                                                                          month: 'short',
                                                                          day: 'numeric',
                                                                      })
                                                                    : ''}
                                                                {file.rowCount != null ? ` · ${file.rowCount} rows` : ''}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                    {ateSupplementaryRequests.length > 0 && (
                                        <div className={styles.ateStatusMetric}>
                                            <div className={styles.ateStatusMetricIconWrapper}>
                                                <FaPaperPlane className={styles.ateStatusMetricIcon} />
                                            </div>
                                            <div className={styles.ateStatusMetricContent}>
                                                <span className={styles.ateStatusMetricLabel}>Supplementary ATE requests</span>
                                                <ul className={styles.ateFileList}>
                                                    {ateSupplementaryRequests.map((req) => (
                                                        <li key={req.key}>
                                                            <a
                                                                href={req.fileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={styles.ateCsvLink}
                                                            >
                                                                {req.requestedAt
                                                                    ? new Date(req.requestedAt).toLocaleDateString('en-GB', {
                                                                          year: 'numeric',
                                                                          month: 'short',
                                                                          day: 'numeric',
                                                                          hour: '2-digit',
                                                                          minute: '2-digit',
                                                                      })
                                                                    : req.fileName ?? req.key.split('/').pop()}
                                                            </a>
                                                            <span className={styles.ateFileMeta}>
                                                                {req.clientCount != null
                                                                    ? `${req.clientCount} client${req.clientCount === 1 ? '' : 's'}`
                                                                    : ''}
                                                                {req.claimCount != null ? ` · ${req.claimCount} claims` : ''}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Verification result */}
                    {ateVerifyReport && (
                        <div
                            className={`${styles.ateVerifyResult} ${
                                ateVerifyReport.ok ? styles.ateVerifyResultOk : styles.ateVerifyResultIssues
                            }`}
                        >
                            <div className={styles.ateVerifyHeader}>
                                {ateVerifyReport.ok ? (
                                    <FaCheckCircle className={styles.ateVerifyHeaderIcon} aria-hidden />
                                ) : (
                                    <FaExclamationTriangle className={styles.ateVerifyHeaderIcon} aria-hidden />
                                )}
                                <strong>
                                    {ateVerifyReport.ok
                                        ? 'Every value on the Temple sheet is in the database'
                                        : `${ateVerifyReport.issueCount} issue${ateVerifyReport.issueCount === 1 ? '' : 's'} found`}
                                </strong>
                                {ateVerifyReport.source === 'ftp' && (
                                    <span className={`${styles.ateStatusBadge} ${styles.ateSkipped}`}>
                                        Sheet recovered from FTP and saved to S3
                                    </span>
                                )}
                            </div>
                            <div className={styles.ateVerifyStats}>
                                <span>
                                    <strong>{ateVerifyReport.sheetRowCount}</strong> sheet row
                                    {ateVerifyReport.sheetRowCount === 1 ? '' : 's'} in {ateVerifyReport.filesFound} file
                                    {ateVerifyReport.filesFound === 1 ? '' : 's'}
                                </span>
                                <span>
                                    <strong>{ateVerifyReport.matchedCount}</strong> / {ateVerifyReport.batchRowCount} claim
                                    {ateVerifyReport.batchRowCount === 1 ? '' : 's'} match
                                </span>
                                <span>
                                    <strong>{ateVerifyReport.batchClientCount}</strong> client
                                    {ateVerifyReport.batchClientCount === 1 ? '' : 's'} in batch
                                </span>
                                {ateVerifyReport.otherBatchRowCount > 0 && (
                                    <span title="Rows on the sheet whose case reference is not a client in this batch">
                                        <strong>{ateVerifyReport.otherBatchRowCount}</strong> row
                                        {ateVerifyReport.otherBatchRowCount === 1 ? '' : 's'} for other batches
                                    </span>
                                )}
                                {ateVerifyReport.repaired && (
                                    <span>
                                        Repaired <strong>{ateVerifyReport.repaired.references}</strong> ref
                                        {ateVerifyReport.repaired.references === 1 ? '' : 's'},{' '}
                                        <strong>{ateVerifyReport.repaired.claims}</strong> claim
                                        {ateVerifyReport.repaired.claims === 1 ? '' : 's'},{' '}
                                        <strong>{ateVerifyReport.repaired.clients}</strong> client
                                        {ateVerifyReport.repaired.clients === 1 ? '' : 's'}
                                    </span>
                                )}
                            </div>

                            {!ateVerifyReport.ok && (
                                <>
                                    <div className={styles.ateVerifySummaryRow}>
                                        {ateVerifyReport.repairable.issueCount > 0 && (
                                            <span className={`${styles.ateStatusBadge} ${styles.ateReturned}`}>
                                                {ateVerifyReport.repairable.issueCount} fixable from sheet (
                                                {ateVerifyReport.repairable.clientCount} client
                                                {ateVerifyReport.repairable.clientCount === 1 ? '' : 's'})
                                            </span>
                                        )}
                                        {ateVerifyReport.unrepairable.clientCount > 0 && (
                                            <span className={`${styles.ateStatusBadge} ${styles.ateNotReturned}`}>
                                                {ateVerifyReport.unrepairable.clientCount} client
                                                {ateVerifyReport.unrepairable.clientCount === 1 ? '' : 's'} /{' '}
                                                {ateVerifyReport.unrepairable.claimCount} claim
                                                {ateVerifyReport.unrepairable.claimCount === 1 ? '' : 's'} not on sheet
                                            </span>
                                        )}
                                    </div>

                                    {!isScoreUser && (
                                        <div className={styles.ateVerifyActions}>
                                            {ateVerifyReport.repairable.issueCount > 0 && (
                                                <button
                                                    type="button"
                                                    className={styles.ateActionButton}
                                                    onClick={handleRepairAteImport}
                                                    disabled={repairingAteImport || verifyingAteImport}
                                                    title="Write the policy ID / policy number / inception date from the Temple sheet onto the mismatched rows in this batch"
                                                >
                                                    {repairingAteImport ? <FaCircleNotch className={styles.spinIcon} /> : <FaWrench />}
                                                    {repairingAteImport ? 'Repairing…' : 'Repair from Temple sheet'}
                                                </button>
                                            )}
                                            {ateVerifyReport.unrepairable.clientCount > 0 && (
                                                <button
                                                    type="button"
                                                    className={styles.ateActionButtonSubtle}
                                                    onClick={handleReRequestMissingAte}
                                                    disabled={reRequestingMissingAte || verifyingAteImport}
                                                    title="Build a supplementary ATE request sheet containing only the clients Temple never returned"
                                                >
                                                    {reRequestingMissingAte ? <FaCircleNotch className={styles.spinIcon} /> : <FaPaperPlane />}
                                                    {reRequestingMissingAte ? 'Building sheet…' : 'Re-request missing ATE'}
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        className={styles.ateVerifyToggle}
                                        onClick={() => setAteIssuesExpanded((o) => !o)}
                                        aria-expanded={ateIssuesExpanded}
                                    >
                                        {ateIssuesExpanded ? <FaChevronDown aria-hidden /> : <FaChevronRight aria-hidden />}
                                        {ateIssuesExpanded ? 'Hide' : 'Show'} issues
                                    </button>

                                    {ateIssuesExpanded && (
                                        <div className={styles.ateVerifyTableWrap}>
                                            <table className={styles.ateVerifyTable}>
                                                <thead>
                                                    <tr>
                                                        <th>Client</th>
                                                        <th>Claim</th>
                                                        <th>Issue</th>
                                                        <th>Sheet</th>
                                                        <th>Database</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {ateVerifyReport.issues.slice(0, 200).map((issue, idx) => (
                                                        <tr key={`${issue.funderClientId}-${issue.claimRef ?? 'client'}-${issue.kind}-${idx}`}>
                                                            <td>{issue.funderClientId}</td>
                                                            <td>
                                                                {issue.claimRef ??
                                                                    (issue.claimRefs && issue.claimRefs.length > 0
                                                                        ? issue.claimRefs.join(', ')
                                                                        : '—')}
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={`${styles.ateStatusBadge} ${
                                                                        issue.repairable ? styles.ateReturned : styles.ateNotReturned
                                                                    }`}
                                                                >
                                                                    {ATE_ISSUE_LABELS[issue.kind] ?? issue.kind}
                                                                </span>
                                                            </td>
                                                            <td>{issue.expected ?? <em>blank</em>}</td>
                                                            <td>{issue.actual ?? <em>empty</em>}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {ateVerifyReport.issues.length > 200 && (
                                                <p className={styles.ateVerifyMore}>
                                                    {ateVerifyReport.issues.length - 200} more not shown
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    </>
                    )}
                </div>

                {/* Missing client data warning */}
                {batchDiagnostics && batchDiagnostics.orphanedClients > 0 && (
                    <div className={styles.orphanedClientsWarning}>
                        <FaExclamationTriangle className={styles.orphanedClientsWarningIcon} aria-hidden />
                        <div>
                            <strong>{batchDiagnostics.orphanedClients} client{batchDiagnostics.orphanedClients === 1 ? '' : 's'} missing data</strong>
                            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', opacity: 0.9 }}>
                                Client records (email, name, address, phone) are missing from the database. This batch needs to be re-imported to restore the missing data.
                            </p>
                        </div>
                    </div>
                )}

                {/* Batch processing status (from batch-diagnostics) */}
                {batchDetails && (
                    <div className={styles.collapsibleSection}>
                        <button
                            type="button"
                            className={styles.collapsibleSectionHeader}
                            onClick={() => setBatchProcessingOpen((o) => !o)}
                            aria-expanded={batchProcessingOpen}
                        >
                            {batchProcessingOpen ? (
                                <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                            ) : (
                                <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                            )}
                            <h4 className={styles.collapsibleSectionTitle}>Batch processing</h4>
                        </button>
                        {batchProcessingOpen && (
                            <div className={styles.batchProcessingStatusSection}>
                        {diagnosticsLoading && (
                            <div className={styles.diagnosticsLoadingCard}>
                                <div className={styles.diagnosticsLoadingSpinner} aria-hidden />
                                <p className={styles.diagnosticsLoadingText}>Checking batch processing status…</p>
                                <p className={styles.diagnosticsLoadingSubtext}>
                                    Loading diagnostics from the server to see if submission files are ready.
                                </p>
                            </div>
                        )}

                        {!diagnosticsLoading && (() => {
                            const p = batchDiagnostics?.processed
                            const initialDone = p !== undefined && p !== 'Initial'
                            const startedDone = p === 'Complete' || p === 'Complete with errors'
                            const completeDone = p === 'Complete with errors'
                            const initialCurrent = p === 'Initial'
                            const startedCurrent = p === 'Started'
                            const startedStale = startedCurrent && !processing
                            const completeCurrent = p === 'Complete'
                            const errorsCurrent = p === 'Complete with errors'
                            return (
                            <div className={styles.batchProcessingGrid}>
                                {/* Initial */}
                                <div
                                    className={`${styles.batchProcessingCard} ${
                                        initialCurrent ? styles.batchProcessingCardActive : ''
                                    } ${initialDone ? styles.batchProcessingCardDone : ''}`}
                                >
                                    <div className={styles.batchProcessingCardHeader}>
                                        {initialDone ? (
                                            <FaCheckCircle className={styles.batchProcessingCardIconDone} aria-hidden />
                                        ) : (
                                            <FaClock className={styles.batchProcessingCardIcon} />
                                        )}
                                        <h5 className={styles.batchProcessingCardTitle}>Initial</h5>
                                        {initialCurrent && (
                                            <span className={styles.batchProcessingBadge}>Current</span>
                                        )}
                                    </div>
                                    {initialCurrent && (
                                        <div className={styles.batchProcessingCardContent}>
                                            <p className={styles.batchProcessingCardDesc}>
                                                Batch has not been processed yet. Run <strong>Process Allocation</strong> from
                                                the Actions menu once ATE has been returned.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Started */}
                                <div
                                    className={`${styles.batchProcessingCard} ${
                                        startedCurrent ? styles.batchProcessingCardActive : ''
                                    } ${startedStale ? styles.batchProcessingCardDoneWithErrors : ''} ${startedDone ? styles.batchProcessingCardDone : ''}`}
                                >
                                    <div className={styles.batchProcessingCardHeader}>
                                        {startedDone ? (
                                            <FaCheckCircle className={styles.batchProcessingCardIconDone} aria-hidden />
                                        ) : startedStale ? (
                                            <FaExclamationTriangle className={styles.batchProcessingCardIconDoneWithErrors} aria-hidden />
                                        ) : startedCurrent ? (
                                            <FaCircleNotch
                                                className={`${styles.batchProcessingCardIcon} ${styles.batchProcessingCardIconSpin}`}
                                                aria-hidden
                                            />
                                        ) : (
                                            <FaClock className={styles.batchProcessingCardIcon} />
                                        )}
                                        <h5 className={styles.batchProcessingCardTitle}>
                                            {startedStale ? 'Failed' : 'Started'}
                                        </h5>
                                        {startedCurrent && (
                                            <span className={styles.batchProcessingBadge}>Current</span>
                                        )}
                                    </div>
                                    {startedStale ? (
                                        <div className={styles.batchProcessingCardContent}>
                                            <p className={styles.batchProcessingCardDesc}>
                                                Processing was interrupted before completing. Run <strong>Repair batch</strong> to
                                                fix any incomplete records and regenerate submission files.
                                            </p>
                                            {!isScoreUser && <div className={styles.batchProcessingErrorActions}>
                                                <button
                                                    type="button"
                                                    className={styles.repairBatchButton}
                                                    onClick={handleRepairBatch}
                                                    disabled={repairingBatch || !batchDetails}
                                                >
                                                    <FaWrench />
                                                    <span>{repairingBatch ? (repairRemaining != null ? `Repairing… ${repairRemaining} left` : 'Repairing…') : 'Repair batch'}</span>
                                                </button>
                                                {repairingBatch && (
                                                    <button
                                                        type="button"
                                                        className={styles.stopRepairButton}
                                                        onClick={handleStopRepair}
                                                        disabled={stoppingRepair}
                                                    >
                                                        <FaStopCircle />
                                                        <span>{stoppingRepair ? 'Stopping…' : 'Stop repair'}</span>
                                                    </button>
                                                )}
                                            </div>}
                                        </div>
                                    ) : startedCurrent && (
                                        <div className={styles.batchProcessingCardContent}>
                                            <p className={styles.batchProcessingCardDesc}>
                                                Document bundles and submission files are being generated. This may take
                                                several minutes depending on batch size.
                                            </p>
                                            <div className={styles.batchProcessingCardLoader}>
                                                <div className={styles.diagnosticsLoadingSpinner} aria-hidden />
                                                <span>Processing in progress…</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Final stage: only one card – Complete OR Complete with errors */}
                                {completeCurrent && (
                                    <div
                                        className={`${styles.batchProcessingCard} ${styles.batchProcessingCardActive}`}
                                    >
                                        <div className={styles.batchProcessingCardHeader}>
                                            <FaCheckCircle className={styles.batchProcessingCardIconDone} aria-hidden />
                                            <h5 className={styles.batchProcessingCardTitle}>Complete</h5>
                                        </div>
                                        <div className={styles.batchProcessingCardContent}>
                                            <p className={styles.batchProcessingCardDesc}>
                                                All records processed successfully. Submission CSV and Excel are ready to
                                                download.
                                            </p>
                                            {(batchDiagnostics?.submissionCsvUrl || batchDiagnostics?.submissionExcelUrl) && (
                                                <div className={styles.batchProcessingDownloads}>
                                                    {batchDiagnostics?.submissionCsvUrl && (
                                                        <a
                                                            href={batchDiagnostics.submissionCsvUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.ateCsvLink}
                                                        >
                                                            <FaFileDownload /> Download CSV
                                                        </a>
                                                    )}
                                                    {batchDiagnostics?.submissionExcelUrl && (
                                                        <a
                                                            href={batchDiagnostics.submissionExcelUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.ateCsvLink}
                                                        >
                                                            <FaFileDownload /> Download Excel
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {!isScoreUser && errorsCurrent && (
                                    <div
                                        className={`${styles.batchProcessingCard} ${styles.batchProcessingCardActive} ${styles.batchProcessingCardDoneWithErrors} ${styles.batchProcessingCardFullWidth}`}
                                    >
                                        <div className={styles.batchProcessingCardHeader}>
                                            <FaExclamationTriangle className={styles.batchProcessingCardIconDoneWithErrors} aria-hidden />
                                            <h5 className={styles.batchProcessingCardTitle}>Complete with errors</h5>
                                        </div>
                                        <div className={styles.batchProcessingCardContent}>
                                            <p className={styles.batchProcessingCardDesc}>
                                                Some records failed during processing. Download the error report, then run
                                                Repair batch to fix failed records, or Skip repair to remove claims
                                                without ATE certificate and complete the batch.
                                            </p>
                                            {failureBreakdownLoading && !failureBreakdown && (
                                                <div className={styles.failureBreakdownLoading}>
                                                    <div className={styles.diagnosticsLoadingSpinner} aria-hidden />
                                                    <span>Loading failure breakdown…</span>
                                                </div>
                                            )}
                                            {failureBreakdown && (
                                                <div className={styles.failureBreakdown}>
                                                    <div className={styles.failureBreakdownStats}>
                                                        <div className={styles.failureBreakdownStat}>
                                                            <span className={styles.failureBreakdownStatValue}>
                                                                {failureBreakdown.successCount.toLocaleString()}
                                                            </span>
                                                            <span className={styles.failureBreakdownStatLabel}>
                                                                Bundles generated
                                                            </span>
                                                        </div>
                                                        <div className={`${styles.failureBreakdownStat} ${styles.failureBreakdownStatFailed}`}>
                                                            <span className={styles.failureBreakdownStatValue}>
                                                                {failureBreakdown.failedCount.toLocaleString()}
                                                            </span>
                                                            <span className={styles.failureBreakdownStatLabel}>
                                                                Failed to generate
                                                            </span>
                                                        </div>
                                                        <div className={styles.failureBreakdownStat}>
                                                            <span className={styles.failureBreakdownStatValue}>
                                                                {failureBreakdown.totalRecords.toLocaleString()}
                                                            </span>
                                                            <span className={styles.failureBreakdownStatLabel}>
                                                                Total claims
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {failureBreakdown.categories.length > 0 && (
                                                        <div className={styles.failureCategoryList}>
                                                            <p className={styles.failureCategoryListTitle}>
                                                                Failures by category (click to see cases)
                                                            </p>
                                                            {failureBreakdown.categories.map((cat) => {
                                                                const isExpanded = expandedFailureCategories.has(cat.category)
                                                                return (
                                                                    <div key={cat.category} className={styles.failureCategory}>
                                                                        <button
                                                                            type="button"
                                                                            className={styles.failureCategoryHeader}
                                                                            onClick={() => toggleFailureCategory(cat.category)}
                                                                            aria-expanded={isExpanded}
                                                                        >
                                                                            {isExpanded ? (
                                                                                <FaChevronDown className={styles.failureCategoryChevron} aria-hidden />
                                                                            ) : (
                                                                                <FaChevronRight className={styles.failureCategoryChevron} aria-hidden />
                                                                            )}
                                                                            <span className={styles.failureCategoryName}>{cat.category}</span>
                                                                            <span className={styles.failureCategoryCount}>
                                                                                {cat.count.toLocaleString()} case{cat.count === 1 ? '' : 's'}
                                                                            </span>
                                                                        </button>
                                                                        {isExpanded && (
                                                                            <ul className={styles.failureCategoryCases}>
                                                                                {cat.cases.map((c, i) => (
                                                                                    <li key={`${c.case_ref}-${c.docName ?? ''}-${i}`}>
                                                                                        <span className={styles.failureCaseRef}>{c.case_ref}</span>
                                                                                        {c.docName && (
                                                                                            <span className={styles.failureCaseDoc}>{c.docName}</span>
                                                                                        )}
                                                                                        {c.error && (
                                                                                            <span className={styles.failureCaseError}>{c.error}</span>
                                                                                        )}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        )}
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    )}
                                                    {!failureBreakdown.reportAvailable && failureBreakdown.failedCount > 0 && (
                                                        <p className={styles.failureBreakdownNote}>
                                                            No failure report was found for this batch, so a category
                                                            breakdown is not available. Counts are based on claims still
                                                            missing a document pack.
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                            <div className={styles.batchProcessingErrorActions}>
                                                {batchDiagnostics?.failureReportUrl && (
                                                    <a
                                                        href={batchDiagnostics.failureReportUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={styles.ateCsvLink}
                                                    >
                                                        <FaFileDownload /> Download error report
                                                    </a>
                                                )}
                                                {!isScoreUser && <>
                                                <button
                                                    type="button"
                                                    className={styles.repairBatchButton}
                                                    onClick={handleRepairBatch}
                                                    disabled={repairingBatch || skippingRepair || !batchDetails}
                                                >
                                                    <FaWrench />
                                                    <span>{repairingBatch ? (repairRemaining != null ? `Repairing… ${repairRemaining} left` : 'Repairing…') : 'Repair batch'}</span>
                                                </button>
                                                {repairingBatch && (
                                                    <button
                                                        type="button"
                                                        className={styles.stopRepairButton}
                                                        onClick={handleStopRepair}
                                                        disabled={stoppingRepair}
                                                    >
                                                        <FaStopCircle />
                                                        <span>{stoppingRepair ? 'Stopping…' : 'Stop repair'}</span>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className={styles.skipRepairButton}
                                                    onClick={handleSkipRepair}
                                                    disabled={repairingBatch || skippingRepair || !batchDetails}
                                                >
                                                    <span>{skippingRepair ? 'Skipping…' : 'Skip repair'}</span>
                                                </button>
                                                </>}
                                            </div>
                                            <p className={styles.batchProcessingMessage}>
                                                After repairing, the batch will move to Complete and submission files
                                                will be available. Skip repair removes claims missing ATE certificate
                                                and regenerates the submission sheet.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            )
                        })()}
                            </div>
                        )}
                    </div>
                )}

                {/* Audit Check */}
                {!isScoreUser && <div className={styles.collapsibleSection}>
                    <button
                        type="button"
                        className={styles.collapsibleSectionHeader}
                        onClick={() => setAuditSectionOpen((o) => !o)}
                        aria-expanded={auditSectionOpen}
                    >
                        {auditSectionOpen ? (
                            <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                        ) : (
                            <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                        )}
                        <h4 className={styles.collapsibleSectionTitle}>Audit Check</h4>
                    </button>
                    {auditSectionOpen && (
                        <div className={styles.sectionContainer}>
                            <p className={styles.sectionDescription}>
                                Upload the funder's audited claims file to cross-reference against the batch. Claims not present in the audit file will be flagged for removal. Review the preview before confirming.
                            </p>
                        <div className={styles.auditSection}>
                            <div className={styles.auditUploadArea}>
                                <div className={styles.auditUploadRow}>
                                    <label className={styles.auditFileLabel}>
                                        <FaFileExcel className={styles.auditFileIcon} />
                                        <span>Upload audited claims file (.xlsx)</span>
                                        <input
                                            ref={auditFileInputRef}
                                            type="file"
                                            accept=".xlsx"
                                            className={styles.auditFileInput}
                                            onChange={handleAuditFileChange}
                                        />
                                    </label>
                                    {auditFile && (
                                        <span className={styles.auditFileName}>{auditFile.name}</span>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.auditCheckButton}
                                        onClick={handleAuditCheck}
                                        disabled={!auditFile || auditChecking}
                                    >
                                        {auditChecking ? (
                                            <><FaCircleNotch className={styles.spinIcon} /> Checking...</>
                                        ) : (
                                            <><FaClipboardCheck /> Check Audit File</>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {auditResult && (
                                <div className={auditResult.success ? styles.auditResultSuccess : styles.auditResultError}>
                                    {auditResult.success ? <FaCheckCircle /> : <FaTimesCircle />}
                                    <span>{auditResult.message}</span>
                                </div>
                            )}

                            {auditPreview && (
                                <div className={styles.auditPreview}>
                                    <div className={styles.auditSummaryGrid}>
                                        <div className={styles.auditSummaryCard}>
                                            <span className={styles.auditSummaryValue}>{auditPreview.summary.totalActiveClaims}</span>
                                            <span className={styles.auditSummaryLabel}>Active Claims</span>
                                        </div>
                                        <div className={styles.auditSummaryCard}>
                                            <span className={styles.auditSummaryValue}>{auditPreview.summary.auditFileReferences}</span>
                                            <span className={styles.auditSummaryLabel}>Audit File Refs</span>
                                        </div>
                                        <div className={styles.auditSummaryCard}>
                                            <span className={styles.auditSummaryValue}>{auditPreview.summary.auditedClaims}</span>
                                            <span className={styles.auditSummaryLabel}>Matched (Audited)</span>
                                        </div>
                                        <div className={`${styles.auditSummaryCard} ${styles.auditSummaryCardWarning}`}>
                                            <span className={styles.auditSummaryValue}>{auditPreview.summary.unauditedClaims}</span>
                                            <span className={styles.auditSummaryLabel}>To Remove</span>
                                        </div>
                                        <div className={`${styles.auditSummaryCard} ${styles.auditSummaryCardWarning}`}>
                                            <span className={styles.auditSummaryValue}>{auditPreview.summary.clientsToFullyRemove}</span>
                                            <span className={styles.auditSummaryLabel}>Clients to Remove</span>
                                        </div>
                                        {auditPreview.summary.alreadyRemoved > 0 && (
                                            <div className={styles.auditSummaryCard}>
                                                <span className={styles.auditSummaryValue}>{auditPreview.summary.alreadyRemoved}</span>
                                                <span className={styles.auditSummaryLabel}>Already Removed</span>
                                            </div>
                                        )}
                                        {(auditPreview.summary.addressColumnsDetected?.length || 0) > 0 && (
                                            <div className={`${styles.auditSummaryCard} ${auditAddressUpdateCount > 0 ? styles.auditSummaryCardInfo : ''}`}>
                                                <span className={styles.auditSummaryValue}>{auditAddressUpdateCount}</span>
                                                <span className={styles.auditSummaryLabel}>Address Changes</span>
                                            </div>
                                        )}
                                    </div>

                                    {auditPreview.clientsToFullyRemove.length > 0 && (
                                        <div className={styles.auditPreviewSection}>
                                            <button
                                                type="button"
                                                className={styles.auditPreviewSectionToggle}
                                                onClick={() => setAuditClientsListOpen((o) => !o)}
                                                aria-expanded={auditClientsListOpen}
                                            >
                                                {auditClientsListOpen ? <FaChevronDown aria-hidden /> : <FaChevronRight aria-hidden />}
                                                <h5 className={styles.auditPreviewSectionTitle}>
                                                    Clients to fully remove ({auditPreview.clientsToFullyRemove.length})
                                                </h5>
                                            </button>
                                            {auditClientsListOpen && <div className={styles.auditPreviewTableWrapper}>
                                                <table className={styles.auditPreviewTable}>
                                                    <thead>
                                                        <tr>
                                                            <th></th>
                                                            <th>Client ID</th>
                                                            <th>Name</th>
                                                            <th>Email</th>
                                                            <th>Claims</th>
                                                            <th>Total Value</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {auditPreview.clientsToFullyRemove.map((client) => (
                                                            <>
                                                                <tr key={`audit-c-${client.funder_client_id}`} className={styles.auditPreviewRow}>
                                                                    <td className={styles.expandCell}>
                                                                        <button
                                                                            type="button"
                                                                            className={styles.expandButton}
                                                                            onClick={() => toggleAuditClient(client.funder_client_id)}
                                                                        >
                                                                            {expandedAuditClients.has(client.funder_client_id)
                                                                                ? <FaChevronDown />
                                                                                : <FaChevronRight />}
                                                                        </button>
                                                                    </td>
                                                                    <td>{client.funder_client_id}</td>
                                                                    <td>{`${client.title || ''} ${client.first_name || ''} ${client.last_name || ''}`.trim() || '—'}</td>
                                                                    <td>{client.email_address || '—'}</td>
                                                                    <td>{client.claimsCount}</td>
                                                                    <td>£{client.totalValue.toFixed(2)}</td>
                                                                </tr>
                                                                {expandedAuditClients.has(client.funder_client_id) && client.claims.map((claim) => (
                                                                    <tr key={`audit-cl-${claim.funder_case_ref}`} className={styles.auditClaimRow}>
                                                                        <td></td>
                                                                        <td colSpan={2}>{claim.funder_case_ref}</td>
                                                                        <td>{claim.defendant || '—'}</td>
                                                                        <td>{claim.law_firm_secondary_reference || '—'}</td>
                                                                        <td>£{(parseFloat(String(claim.quantum_individual)) || 0).toFixed(2)}</td>
                                                                    </tr>
                                                                ))}
                                                            </>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>}
                                        </div>
                                    )}

                                    {auditPreview.partialClaimsToRemove.length > 0 && (
                                        <div className={styles.auditPreviewSection}>
                                            <button
                                                type="button"
                                                className={styles.auditPreviewSectionToggle}
                                                onClick={() => setAuditPartialListOpen((o) => !o)}
                                                aria-expanded={auditPartialListOpen}
                                            >
                                                {auditPartialListOpen ? <FaChevronDown aria-hidden /> : <FaChevronRight aria-hidden />}
                                                <h5 className={styles.auditPreviewSectionTitle}>
                                                    Individual claims to remove ({auditPreview.partialClaimsToRemove.length})
                                                </h5>
                                            </button>
                                            {auditPartialListOpen && <div className={styles.auditPreviewTableWrapper}>
                                                <table className={styles.auditPreviewTable}>
                                                    <thead>
                                                        <tr>
                                                            <th>Case Ref</th>
                                                            <th>Client</th>
                                                            <th>Secondary Ref</th>
                                                            <th>Defendant</th>
                                                            <th>Value</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {auditPreview.partialClaimsToRemove.map((claim) => (
                                                            <tr key={`audit-p-${claim.funder_case_ref}`} className={styles.auditPreviewRow}>
                                                                <td>{claim.funder_case_ref}</td>
                                                                <td>{claim.clientName || '—'}</td>
                                                                <td>{claim.law_firm_secondary_reference || '—'}</td>
                                                                <td>{claim.defendant || '—'}</td>
                                                                <td>£{(parseFloat(String(claim.quantum_individual)) || 0).toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>}
                                        </div>
                                    )}

                                    {(auditPreview.addressChanges?.length || 0) > 0 && (
                                        <div className={styles.auditPreviewSection}>
                                            <button
                                                type="button"
                                                className={styles.auditPreviewSectionToggle}
                                                onClick={() => setAuditAddressListOpen((o) => !o)}
                                                aria-expanded={auditAddressListOpen}
                                            >
                                                {auditAddressListOpen ? <FaChevronDown aria-hidden /> : <FaChevronRight aria-hidden />}
                                                <h5 className={styles.auditPreviewSectionTitle}>
                                                    Address changes from audit file ({auditPreview.addressChanges!.length} client{auditPreview.addressChanges!.length === 1 ? '' : 's'})
                                                </h5>
                                            </button>
                                            <p className={styles.auditAddressNote}>
                                                Address values on the sheet differ from the stored client record. Blank sheet values are ignored and never clear existing data.
                                                {(auditPreview.summary.addressUnmatchedRows || 0) > 0 && (
                                                    <> {auditPreview.summary.addressUnmatchedRows} row(s) with address data could not be matched to a claim in this batch and were skipped.</>
                                                )}
                                            </p>
                                            {auditAddressListOpen && <div className={styles.auditPreviewTableWrapper}>
                                                <table className={styles.auditPreviewTable}>
                                                    <thead>
                                                        <tr>
                                                            <th>Client ID</th>
                                                            <th>Client</th>
                                                            <th>Field</th>
                                                            <th>Current</th>
                                                            <th>New (from sheet)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {auditPreview.addressChanges!.map((client) => (
                                                            client.changes.map((change, idx) => (
                                                                <tr key={`audit-addr-${client.funder_client_id}-${change.field}`} className={styles.auditAddressRow}>
                                                                    {idx === 0 && (
                                                                        <>
                                                                            <td rowSpan={client.changes.length}>{client.funder_client_id}</td>
                                                                            <td rowSpan={client.changes.length}>
                                                                                <div>{client.clientName || '—'}</div>
                                                                                {client.email_address && <div className={styles.auditAddressSubtle}>{client.email_address}</div>}
                                                                                <div className={styles.auditAddressSubtle}>Ref: {client.sourceRef}</div>
                                                                            </td>
                                                                        </>
                                                                    )}
                                                                    <td>{change.label}</td>
                                                                    <td className={styles.auditAddressCurrent}>{change.current || <em>empty</em>}</td>
                                                                    <td className={styles.auditAddressProposed}>
                                                                        {change.proposed}
                                                                        {change.conflictingValues.length > 0 && (
                                                                            <div className={styles.auditAddressConflict}>
                                                                                Other rows for this client had: {change.conflictingValues.join(' / ')}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>}
                                            <label className={styles.auditAddressApplyToggle}>
                                                <input
                                                    type="checkbox"
                                                    checked={auditApplyAddressUpdates}
                                                    onChange={(e) => setAuditApplyAddressUpdates(e.target.checked)}
                                                    disabled={auditRemoving}
                                                />
                                                <span>Update {auditAddressUpdateCount} client address{auditAddressUpdateCount === 1 ? '' : 'es'} in the database on confirm</span>
                                            </label>
                                        </div>
                                    )}

                                    {auditHasChangesToConfirm ? (
                                        <div className={styles.auditPreviewActions}>
                                            <button
                                                type="button"
                                                className={styles.cancelButton}
                                                onClick={handleAuditCancelPreview}
                                                disabled={auditRemoving}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.confirmDeleteButton}
                                                onClick={handleAuditConfirmRemoval}
                                                disabled={auditRemoving}
                                            >
                                                {auditRemoving ? (
                                                    <><FaCircleNotch className={styles.spinIcon} /> Applying...</>
                                                ) : auditPreview.allCaseRefsToRemove.length > 0 ? (
                                                    <>
                                                        Confirm Removal ({auditPreview.allCaseRefsToRemove.length} claims)
                                                        {auditWillApplyAddressUpdates && ` + ${auditAddressUpdateCount} address update${auditAddressUpdateCount === 1 ? '' : 's'}`}
                                                    </>
                                                ) : (
                                                    <>Confirm {auditAddressUpdateCount} Address Update{auditAddressUpdateCount === 1 ? '' : 's'}</>
                                                )}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className={styles.auditPreviewActions}>
                                            <p className={styles.auditNoChanges}>
                                                {auditPreview.allCaseRefsToRemove.length === 0 && auditAddressUpdateCount === 0
                                                    ? 'All claims in this batch match the audit file. No changes needed.'
                                                    : 'No claims to remove. Tick the address update option above to apply the address changes.'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        </div>
                    )}
                </div>}

                {/* Removed Records (Audit) */}
                {!isScoreUser && <div className={styles.collapsibleSection}>
                    <button
                        type="button"
                        className={styles.collapsibleSectionHeader}
                        onClick={() => setRemovedSectionOpen((o) => !o)}
                        aria-expanded={removedSectionOpen}
                    >
                        {removedSectionOpen ? (
                            <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                        ) : (
                            <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                        )}
                        <h4 className={styles.collapsibleSectionTitle}>
                            Removed Records (Audit)
                            {removedTotalClaims > 0 && (
                                <span className={styles.removedBadge}>{removedTotalClaims}</span>
                            )}
                        </h4>
                    </button>
                    {removedSectionOpen && (
                        <div className={styles.sectionContainer}>
                            <p className={styles.sectionDescription}>
                                Records that were removed during the audit process. These claims were not present in the funder's audited file and have been excluded from the batch. Use the search and filters to review removed records and their removal reasons.
                            </p>
                        <div className={styles.removedSection}>
                            <div className={styles.removedSectionHeader}>
                                <div className={styles.removedSearchWrap}>
                                    <FaSearch className={styles.removedSearchIcon} />
                                    <input
                                        type="text"
                                        className={styles.removedSearchInput}
                                        placeholder="Search removed records..."
                                        value={removedSearchQuery}
                                        onChange={(e) => setRemovedSearchQuery(e.target.value)}
                                    />
                                </div>
                                {removedTotalClaims > 0 && (
                                    <button
                                        type="button"
                                        className={styles.restoreAllButton}
                                        onClick={handleDownloadRemovedExcel}
                                        disabled={downloadingRemovedExcel}
                                    >
                                        {downloadingRemovedExcel ? (
                                            <><FaCircleNotch className={styles.spinIcon} /> Downloading...</>
                                        ) : (
                                            <><FaFileExcel /> Download Excel</>
                                        )}
                                    </button>
                                )}
                                {removedClients.length > 0 && (
                                    <div className={styles.restoreActions}>
                                        {removalReasons.length > 0 && (
                                            <div className={styles.revertByType}>
                                                <select
                                                    className={styles.revertSelect}
                                                    value={selectedRevertReason}
                                                    onChange={(e) => setSelectedRevertReason(e.target.value)}
                                                    disabled={revertingByType || restoringRefs.size > 0}
                                                >
                                                    <option value="">Revert by type...</option>
                                                    {removalReasons.map((r) => (
                                                        <option key={r.reason} value={r.reason}>
                                                            {REASON_LABELS[r.reason] || r.reason} ({r.count})
                                                        </option>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button"
                                                    className={styles.revertButton}
                                                    onClick={handleRevertByType}
                                                    disabled={!selectedRevertReason || revertingByType || restoringRefs.size > 0}
                                                >
                                                    {revertingByType ? (
                                                        <><FaCircleNotch className={styles.spinIcon} /> Reverting...</>
                                                    ) : (
                                                        <><FaUndo /> Revert</>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className={styles.restoreAllButton}
                                            onClick={handleRestoreAll}
                                            disabled={restoringRefs.size > 0}
                                        >
                                            {restoringRefs.has('__all__') ? (
                                                <><FaCircleNotch className={styles.spinIcon} /> Restoring...</>
                                            ) : (
                                                <><FaUndo /> Restore All</>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.purgeRemovedButton}
                                            onClick={handleOpenPurgeModal}
                                            disabled={purgePreviewLoading || restoringRefs.size > 0 || revertingByType}
                                        >
                                            {purgePreviewLoading ? (
                                                <><FaCircleNotch className={styles.spinIcon} /> Loading...</>
                                            ) : (
                                                <><FaTrashAlt /> Delete Permanently</>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {removedLoading ? (
                                <div className={styles.removedLoading}>
                                    <FaCircleNotch className={styles.spinIcon} />
                                    <span>Loading removed records...</span>
                                </div>
                            ) : removedClients.length === 0 ? (
                                <div className={styles.removedEmpty}>
                                    No audit-removed records found for this batch.
                                </div>
                            ) : (
                                <>
                                <div className={styles.removedTableWrapper}>
                                    <table className={styles.removedTable}>
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Client ID</th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Claims</th>
                                                <th>Total Value</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {removedClients.map((client) => {
                                                const clientRefs = client.claims.map((c) => c.funder_case_ref)
                                                const isRestoring = clientRefs.some((r) => restoringRefs.has(r)) || restoringRefs.has('__all__')
                                                return (
                                                    <>
                                                        <tr key={`rem-c-${client.funder_client_id}`} className={styles.removedClientRow}>
                                                            <td className={styles.expandCell}>
                                                                <button
                                                                    type="button"
                                                                    className={styles.expandButton}
                                                                    onClick={() => toggleRemovedClient(client.funder_client_id)}
                                                                >
                                                                    {expandedRemovedClients.has(client.funder_client_id)
                                                                        ? <FaChevronDown />
                                                                        : <FaChevronRight />}
                                                                </button>
                                                            </td>
                                                            <td>{client.funder_client_id}</td>
                                                            <td>{`${client.title || ''} ${client.first_name || ''} ${client.last_name || ''}`.trim() || '—'}</td>
                                                            <td>{client.email_address || '—'}</td>
                                                            <td>{client.claimsCount}</td>
                                                            <td>£{client.totalValue.toFixed(2)}</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className={styles.restoreButton}
                                                                    onClick={() => handleRestoreClients(clientRefs)}
                                                                    disabled={isRestoring}
                                                                >
                                                                    {isRestoring ? <FaCircleNotch className={styles.spinIcon} /> : <FaUndo />}
                                                                    <span>{isRestoring ? 'Restoring' : 'Restore'}</span>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        {expandedRemovedClients.has(client.funder_client_id) && client.claims.map((claim) => (
                                                            <tr key={`rem-cl-${claim.funder_case_ref}`} className={styles.removedClaimRow}>
                                                                <td></td>
                                                                <td colSpan={2}>{claim.funder_case_ref}</td>
                                                                <td>{claim.defendant || '—'}</td>
                                                                <td>{claim.law_firm_secondary_reference || '—'}</td>
                                                                <td>£{(parseFloat(String(claim.quantum_individual)) || 0).toFixed(2)}</td>
                                                                <td className={styles.removedDateCell}>
                                                                    {claim.audit_removed_date ? new Date(claim.audit_removed_date).toLocaleDateString() : '—'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                {removedPagination && removedPagination.totalPages > 1 && (
                                    <div className={styles.removedPagination}>
                                        <span className={styles.removedPaginationInfo}>
                                            Page {removedPagination.page} of {removedPagination.totalPages}
                                            {' · '}{removedPagination.totalClients} client{removedPagination.totalClients !== 1 ? 's' : ''}
                                        </span>
                                        <div className={styles.removedPaginationButtons}>
                                            <button
                                                type="button"
                                                className={styles.removedPaginationButton}
                                                onClick={() => setRemovedPage((p) => Math.max(1, p - 1))}
                                                disabled={!removedPagination.hasPrev || removedLoading}
                                            >
                                                <FaChevronLeft /> Previous
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.removedPaginationButton}
                                                onClick={() => setRemovedPage((p) => p + 1)}
                                                disabled={!removedPagination.hasNext || removedLoading}
                                            >
                                                Next <FaChevronRight />
                                            </button>
                                        </div>
                                    </div>
                                )}
                                </>
                            )}
                        </div>
                        </div>
                    )}
                </div>}

                {/* Batch maintenance tools (merged): email CSV tools + post audit alterations */}
                {!isScoreUser && <div className={styles.collapsibleSection}>
                    <button
                        type="button"
                        className={styles.collapsibleSectionHeader}
                        onClick={() => setMaintenanceToolsOpen((o) => !o)}
                        aria-expanded={maintenanceToolsOpen}
                    >
                        {maintenanceToolsOpen ? (
                            <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                        ) : (
                            <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                        )}
                        <h4 className={styles.collapsibleSectionTitle}>Batch Maintenance Tools</h4>
                    </button>
                    {maintenanceToolsOpen && (
                    <div className={styles.sectionContainer}>
                        <p className={styles.sectionDescription}>
                            Corrective tools for this batch: regenerate document bundles, hard-remove clients, relink introducer client IDs (all by email CSV), and post-audit alterations. Expand a tool to use it.
                        </p>
                {/* Reprocess document bundles by email CSV */}
                <div className={styles.collapsibleSubSection}>
                    <button
                        type="button"
                        className={styles.collapsibleSubSectionHeader}
                        onClick={() => setBundleEmailSectionOpen((o) => !o)}
                        aria-expanded={bundleEmailSectionOpen}
                    >
                        {bundleEmailSectionOpen ? (
                            <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                        ) : (
                            <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                        )}
                        <h5 className={styles.collapsibleSubSectionTitle}>Reprocess Document Bundles (by email CSV)</h5>
                    </button>
                    {bundleEmailSectionOpen && (
                        <div className={styles.subSectionContainer}>
                            <p className={styles.sectionDescription}>
                                Upload a CSV of client emails to regenerate the document bundles for those clients' claims in this batch. Each bundle is rebuilt and uploaded to its existing S3 location, so the stored document bundle URLs are left unchanged — no "Repair batch" step is needed.
                            </p>
                            <div className={styles.reprocessDocActions}>
                                <button
                                    type="button"
                                    className={styles.reprocessLinkButton}
                                    onClick={() => setBundleEmailDocKeys([...ALL_REPROCESS_DOC_KEYS])}
                                    disabled={regeneratingBundles}
                                >
                                    Select all
                                </button>
                                <button
                                    type="button"
                                    className={styles.reprocessLinkButton}
                                    onClick={() => setBundleEmailDocKeys([])}
                                    disabled={regeneratingBundles}
                                >
                                    Select none
                                </button>
                            </div>
                            <div className={styles.reprocessDocList}>
                                {REPROCESS_DOCUMENTS.map((doc) => (
                                    <div key={doc.key}>
                                        <label className={styles.reprocessDocRow}>
                                            <input
                                                type="checkbox"
                                                checked={bundleEmailDocKeys.includes(doc.key)}
                                                onChange={() => toggleBundleEmailDoc(doc.key)}
                                                disabled={regeneratingBundles}
                                            />
                                            <span>{doc.label}</span>
                                            <span className={styles.reprocessDocHint}>
                                                {bundleEmailDocKeys.includes(doc.key)
                                                    ? 'Regenerate'
                                                    : 'Collect from S3'}
                                            </span>
                                        </label>
                                        {doc.key === 'aof' && bundleEmailDocKeys.includes('aof') && (
                                            <div className={styles.reprocessAofVariant} role="radiogroup" aria-label="Assignment of Funds version">
                                                <label className={styles.reprocessAofVariantLabel}>
                                                    <input
                                                        type="radio"
                                                        name="bundleEmailAofVariant"
                                                        value="standard"
                                                        checked={bundleEmailAofVariant === 'standard'}
                                                        onChange={() => setBundleEmailAofVariant('standard')}
                                                        disabled={regeneratingBundles}
                                                    />
                                                    <span>Standard (ORION CAPITAL FUNDING DAC)</span>
                                                </label>
                                                <label className={styles.reprocessAofVariantLabel}>
                                                    <input
                                                        type="radio"
                                                        name="bundleEmailAofVariant"
                                                        value="neradac"
                                                        checked={bundleEmailAofVariant === 'neradac'}
                                                        onChange={() => setBundleEmailAofVariant('neradac')}
                                                        disabled={regeneratingBundles}
                                                    />
                                                    <span>NERADAC (Nera Capital Funding 8 DAC)</span>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <p className={styles.reprocessS3OnlyNote}>
                                Always collected from S3: Credit Report, AML Document. ATE Certificate is taken from
                                the client S3 path first, then the Temple FTP processed backup for this batch; if it
                                is missing from both, the bundle is rebuilt without it.
                            </p>
                            <div className={styles.auditSection}>
                                <div className={styles.auditUploadArea}>
                                    <div className={styles.auditUploadRow}>
                                        <label className={styles.auditFileLabel}>
                                            <FaUpload className={styles.auditFileIcon} />
                                            <span>Upload client emails file (.csv)</span>
                                            <input
                                                ref={bundleEmailFileInputRef}
                                                type="file"
                                                accept=".csv,text/csv"
                                                className={styles.auditFileInput}
                                                onChange={handleBundleEmailFileChange}
                                            />
                                        </label>
                                        {bundleEmailFile && (
                                            <span className={styles.auditFileName}>{bundleEmailFile.name}</span>
                                        )}
                                        <button
                                            type="button"
                                            className={styles.auditCheckButton}
                                            onClick={handleRegenerateBundlesByEmail}
                                            disabled={!bundleEmailFile || regeneratingBundles}
                                        >
                                            {regeneratingBundles ? (
                                                <><FaCircleNotch className={styles.spinIcon} /> Regenerating...</>
                                            ) : (
                                                <><FaSyncAlt /> Regenerate bundles</>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {regenBundlesResult && (
                                    <div className={regenBundlesResult.success ? styles.auditResultSuccess : styles.auditResultError}>
                                        {regenBundlesResult.success ? <FaCheckCircle /> : <FaTimesCircle />}
                                        <span>
                                            {regenBundlesResult.message}
                                            {typeof regenBundlesResult.bundlesRegenerated === 'number' && (
                                                <> ({regenBundlesResult.bundlesRegenerated} regenerated{regenBundlesResult.bundlesFailed ? `, ${regenBundlesResult.bundlesFailed} failed` : ''}; {regenBundlesResult.emailsMatched} of {regenBundlesResult.emailsProvided} email(s) matched)</>
                                            )}
                                        </span>
                                    </div>
                                )}

                                {regenBundlesResult?.emailsNotFound && regenBundlesResult.emailsNotFound.length > 0 && (
                                    <div className={styles.sectionDescription}>
                                        <strong>{regenBundlesResult.emailsNotFound.length}</strong> email(s) had no claims in this batch:
                                        <div style={{ marginTop: 4, fontSize: '0.85em', wordBreak: 'break-all' }}>
                                            {regenBundlesResult.emailsNotFound.slice(0, 50).join(', ')}
                                            {regenBundlesResult.emailsNotFound.length > 50 ? ', …' : ''}
                                        </div>
                                    </div>
                                )}

                                {regenBundlesResult?.failures && regenBundlesResult.failures.length > 0 && (
                                    <div className={styles.sectionDescription}>
                                        <strong>{regenBundlesResult.failures.length}</strong> bundle(s) failed to regenerate:
                                        <div style={{ marginTop: 4, fontSize: '0.85em' }}>
                                            {regenBundlesResult.failures.slice(0, 50).map((f, i) => (
                                                <div key={i} style={{ wordBreak: 'break-all' }}>
                                                    {f.funderCaseRef || f.caseRef}{f.email ? ` (${f.email})` : ''}: {f.error}
                                                </div>
                                            ))}
                                            {regenBundlesResult.failures.length > 50 ? <div>…</div> : null}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Hard-remove clients from this batch by email CSV */}
                <div className={styles.collapsibleSubSection}>
                    <button
                        type="button"
                        className={styles.collapsibleSubSectionHeader}
                        onClick={() => setEmailPurgeSectionOpen((o) => !o)}
                        aria-expanded={emailPurgeSectionOpen}
                    >
                        {emailPurgeSectionOpen ? (
                            <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                        ) : (
                            <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                        )}
                        <h5 className={styles.collapsibleSubSectionTitle}>Remove clients from batch (by email CSV)</h5>
                    </button>
                    {emailPurgeSectionOpen && (
                        <div className={styles.subSectionContainer}>
                            <p className={styles.sectionDescription}>
                                Upload a CSV with an email column to permanently delete those clients from this batch —
                                including audit-removed and API-transferred records. Generated document bundles are
                                deleted from S3. The batch value is then recalculated. Trinity is not changed, and
                                source introducer records are left in place.
                            </p>
                            <div className={styles.auditSection}>
                                <div className={styles.auditUploadArea}>
                                    <div className={styles.auditUploadRow}>
                                        <label className={styles.auditFileLabel}>
                                            <FaUpload className={styles.auditFileIcon} />
                                            <span>Upload client emails file (.csv)</span>
                                            <input
                                                ref={emailPurgeFileInputRef}
                                                type="file"
                                                accept=".csv,text/csv"
                                                className={styles.auditFileInput}
                                                onChange={handleEmailPurgeFileChange}
                                            />
                                        </label>
                                        {emailPurgeFile && (
                                            <span className={styles.auditFileName}>{emailPurgeFile.name}</span>
                                        )}
                                        <button
                                            type="button"
                                            className={styles.purgeRemovedButton}
                                            onClick={handleEmailPurgePreview}
                                            disabled={!emailPurgeFile || emailPurgePreviewLoading || isEmailPurging}
                                        >
                                            {emailPurgePreviewLoading ? (
                                                <><FaCircleNotch className={styles.spinIcon} /> Previewing...</>
                                            ) : (
                                                <><FaTrashAlt /> Preview removal</>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {emailPurgeResult && (
                                    <div className={emailPurgeResult.success ? styles.auditResultSuccess : styles.auditResultError}>
                                        {emailPurgeResult.success ? <FaCheckCircle /> : <FaTimesCircle />}
                                        <span>
                                            {emailPurgeResult.message}
                                            {typeof emailPurgeResult.matchedClients === 'number' && (
                                                <> ({emailPurgeResult.matchedClients} client(s), {emailPurgeResult.claimCount ?? 0} claim(s); Trinity unchanged)</>
                                            )}
                                        </span>
                                    </div>
                                )}

                                {emailPurgeResult?.unmatchedEmails && emailPurgeResult.unmatchedEmails.length > 0 && (
                                    <div className={styles.sectionDescription}>
                                        <strong>{emailPurgeResult.unmatchedEmails.length}</strong> email(s) were not in this batch:
                                        <div className={styles.emailPurgeUnmatched}>
                                            {emailPurgeResult.unmatchedEmails.slice(0, 50).join(', ')}
                                            {emailPurgeResult.unmatchedEmails.length > 50 ? ', …' : ''}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Restore introducer clients.id from funding-monitor introducer_client_id */}
                <div className={styles.collapsibleSubSection}>
                    <button
                        type="button"
                        className={styles.collapsibleSubSectionHeader}
                        onClick={() => setRelinkIdsSectionOpen((o) => !o)}
                        aria-expanded={relinkIdsSectionOpen}
                    >
                        {relinkIdsSectionOpen ? (
                            <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                        ) : (
                            <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                        )}
                        <h5 className={styles.collapsibleSubSectionTitle}>Relink introducer Client IDs (by email CSV)</h5>
                    </button>
                    {relinkIdsSectionOpen && (
                        <div className={styles.subSectionContainer}>
                            <p className={styles.sectionDescription}>
                                Upload a CSV of client emails to restore each Fairclaim or FFJ introducer{' '}
                                <strong>Client ID</strong> to the original <strong>introducer_client_id</strong> stored
                                on the funding-monitor record for this batch. Child rows (leads, lead states, signatures,
                                webhook deliveries) are retargeted with the same ID. Funding-monitor is not modified,
                                and FairClaim/FFJ <code>client_ref</code> values are left unchanged.
                            </p>
                            <div className={styles.auditSection}>
                                <div className={styles.auditUploadArea}>
                                    <div className={styles.auditUploadRow}>
                                        <label className={styles.auditFileLabel}>
                                            <FaUpload className={styles.auditFileIcon} />
                                            <span>Upload client emails file (.csv)</span>
                                            <input
                                                ref={relinkIdsFileInputRef}
                                                type="file"
                                                accept=".csv,text/csv"
                                                className={styles.auditFileInput}
                                                onChange={handleRelinkIdsFileChange}
                                            />
                                        </label>
                                        {relinkIdsFile && (
                                            <span className={styles.auditFileName}>{relinkIdsFile.name}</span>
                                        )}
                                        <button
                                            type="button"
                                            className={styles.auditCheckButton}
                                            onClick={handleRelinkIdsPreview}
                                            disabled={!relinkIdsFile || relinkIdsPreviewLoading || isRelinkingIds}
                                        >
                                            {relinkIdsPreviewLoading ? (
                                                <><FaCircleNotch className={styles.spinIcon} /> Previewing...</>
                                            ) : (
                                                <><FaLink /> Preview relink</>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {relinkIdsResult && (
                                    <div className={relinkIdsResult.success ? styles.auditResultSuccess : styles.auditResultError}>
                                        {relinkIdsResult.success ? <FaCheckCircle /> : <FaTimesCircle />}
                                        <span>
                                            {relinkIdsResult.message}
                                            {typeof relinkIdsResult.relinkedCount === 'number' && (
                                                <> ({relinkIdsResult.relinkedCount} relinked, {relinkIdsResult.alreadyLinkedCount ?? 0} already linked, {relinkIdsResult.skippedCount ?? 0} skipped)</>
                                            )}
                                        </span>
                                    </div>
                                )}

                                {relinkIdsResult?.perEmail && relinkIdsResult.perEmail.filter((r) => r.status !== 'relinked' && r.status !== 'already_linked').length > 0 && (
                                    <div className={styles.sectionDescription}>
                                        <strong>
                                            {relinkIdsResult.perEmail.filter((r) => r.status !== 'relinked' && r.status !== 'already_linked').length}
                                        </strong>{' '}
                                        email(s) were skipped or failed:
                                        <div className={styles.emailPurgeUnmatched}>
                                            {relinkIdsResult.perEmail
                                                .filter((r) => r.status !== 'relinked' && r.status !== 'already_linked')
                                                .slice(0, 50)
                                                .map((r) => `${r.email} (${RELINK_STATUS_LABELS[r.status] || r.status})`)
                                                .join(', ')}
                                            {relinkIdsResult.perEmail.filter((r) => r.status !== 'relinked' && r.status !== 'already_linked').length > 50 ? ', …' : ''}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Post Audit Alterations */}
                <div className={styles.collapsibleSubSection}>
                    <button
                        type="button"
                        className={styles.collapsibleSubSectionHeader}
                        onClick={() => setPostAuditOpen((o) => !o)}
                        aria-expanded={postAuditOpen}
                    >
                        {postAuditOpen ? (
                            <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                        ) : (
                            <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                        )}
                        <h5 className={styles.collapsibleSubSectionTitle}>Post Audit Alterations</h5>
                    </button>
                    {postAuditOpen && (
                        <div className={styles.subSectionContainer}>
                            <p className={styles.sectionDescription}>
                                Apply corrections to the batch after the audit is complete. Select an alteration type, upload the relevant file, and preview changes before applying. Use this for tasks such as resolving credit report mismatches or removing records with loan start dates outside the qualifying period.
                            </p>
                        <div className={styles.postAuditSection}>
                            <div className={styles.postAuditControls}>
                                <div className={styles.postAuditRow}>
                                    <label className={styles.postAuditSelectLabel}>
                                        Alteration Type
                                        <select
                                            className={styles.postAuditSelect}
                                            value={alterationType}
                                            onChange={(e) => {
                                                setAlterationType(e.target.value)
                                                handleAlterationReset()
                                            }}
                                        >
                                            <option value="tr-credit-report-mismatch">
                                                TR Report / Credit Report Loan Amount Mismatch
                                            </option>
                                            <option value="tr-zero-loan-update">
                                                TR Report / Zero Loan Amount Update
                                            </option>
                                            <option value="loan-start-date-removal">
                                                Remove Claims - Loan Start Date After Oct 2024
                                            </option>
                                            <option value="various-issues">
                                                Various Issues
                                            </option>
                                            <option value="removal-by-client-email">
                                                Removal by Client Email
                                            </option>
                                            <option value="excessive-claims-removal">
                                                Remove Clients with More Than 9 Claims
                                            </option>
                                        </select>
                                    </label>
                                    <div className={styles.sectionDescription} style={{ marginTop: '10px', marginBottom: 0, padding: '10px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                        {alterationType === 'tr-credit-report-mismatch' && (
                                            <>
                                                <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#334155' }}>Loan Amount Mismatch Correction</p>
                                                <p style={{ margin: '0 0 6px' }}>
                                                    <strong>Upload:</strong> .xlsx with a <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>Law Firm Secondary Reference</code> column. Optionally include a <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>Credit Report Starting Balance</code> column (e.g. £11,457.00) to supply the balance directly instead of fetching from the credit report.
                                                </p>
                                                <p style={{ margin: '0 0 6px' }}>
                                                    <strong>What happens:</strong> If a <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>Credit Report Starting Balance</code> column is present, that value is used directly. Otherwise, the credit report is fetched from S3 and the starting balance is extracted from the CAIS account matching the agreement number. The lead&apos;s <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>starting_balance</code> is updated in the introducer DB, then <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>quantum_individual</code> is recalculated (statutory interest + commission) and <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>quantum_client_cumulative</code> is updated for the client. Claims with a £0.00 balance are skipped.
                                                </p>
                                                <p style={{ margin: 0, color: '#64748b', fontSize: '0.78rem' }}>
                                                    Without the balance column: each claim must have an agreement number and a credit report in S3.
                                                </p>
                                            </>
                                        )}
                                        {alterationType === 'tr-zero-loan-update' && (
                                            <>
                                                <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#334155' }}>Zero Loan Amount — Document Regeneration</p>
                                                <p style={{ margin: '0 0 6px' }}>
                                                    <strong>Upload:</strong> .xlsx with a <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>Law Firm Secondary Reference</code> column.
                                                </p>
                                                <p style={{ margin: '0 0 6px' }}>
                                                    <strong>What happens:</strong> For each matched claim, the TR Forensic Report document is regenerated so it picks up the corrected loan amount logic. No quantum values or funding-monitor records are changed — this only affects the generated document.
                                                </p>
                                            </>
                                        )}
                                        {alterationType === 'loan-start-date-removal' && (
                                            <>
                                                <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#334155' }}>Remove Claims by Loan Start Date</p>
                                                <p style={{ margin: '0 0 6px' }}>
                                                    <strong>Upload:</strong> .xlsx with a <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>Law Firm Secondary Reference</code> column.
                                                </p>
                                                <p style={{ margin: '0 0 6px' }}>
                                                    <strong>What happens:</strong> Each matched claim&apos;s loan start date (incident date) is checked. If it falls after 31st October 2024, the claim is soft-removed from the batch. If a client has no remaining active claims or their cumulative quantum drops below £1,700, all of that client&apos;s claims are removed. <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>quantum_client_cumulative</code> is recalculated for remaining clients.
                                                </p>
                                            </>
                                        )}
                                        {alterationType === 'various-issues' && (
                                            <>
                                                <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#b45309' }}>Full Client Removal</p>
                                                <p style={{ margin: '0 0 6px' }}>
                                                    <strong>Upload:</strong> .xlsx with a <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>Law Firm Secondary Reference</code> column.
                                                </p>
                                                <p style={{ margin: '0 0 6px', color: '#b45309', fontWeight: 500 }}>
                                                    <strong>What happens:</strong> Each claim reference is used to identify the client. The entire client and all of their claims are soft-removed from the batch — not just the referenced claim. Clients already removed will be skipped. <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>quantum_client_cumulative</code> is recalculated for remaining clients.
                                                </p>
                                            </>
                                        )}
                                        {alterationType === 'removal-by-client-email' && (
                                            <>
                                                <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#b45309' }}>Full Client Removal by Email</p>
                                                <p style={{ margin: '0 0 6px' }}>
                                                    <strong>Upload:</strong> .xlsx with an <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>Email</code> or <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>Email Address</code> column. Matching is case-insensitive and emails are de-duplicated.
                                                </p>
                                                <p style={{ margin: '0 0 6px', color: '#b45309', fontWeight: 500 }}>
                                                    <strong>What happens:</strong> Every client in this batch whose email address appears in the file is identified, and the entire client and all of their claims are soft-removed from the batch — not just one claim. Clients already removed will be skipped. <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>quantum_client_cumulative</code> is recalculated for remaining clients.
                                                </p>
                                                <p style={{ margin: 0, color: '#64748b', fontSize: '0.78rem' }}>
                                                    Emails in the file that don&apos;t match any client in this batch will be listed as &quot;Not Found&quot;.
                                                </p>
                                            </>
                                        )}
                                        {alterationType === 'excessive-claims-removal' && (
                                            <>
                                                <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#b45309' }}>Full Client Removal — Excessive Claim Count</p>
                                                <p style={{ margin: '0 0 6px' }}>
                                                    <strong>No file required.</strong> The batch is scanned automatically — press <strong>Scan Batch</strong> below to identify any client with more than 9 active claims (i.e. 10 or more).
                                                </p>
                                                <p style={{ margin: '0 0 6px', color: '#b45309', fontWeight: 500 }}>
                                                    <strong>What happens:</strong> Every client flagged is soft-removed along with ALL of their claims in the batch — not just the excess. Clients already removed will be skipped. <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.8rem' }}>quantum_client_cumulative</code> is recalculated for remaining clients.
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.postAuditRow}>
                                    {alterationType !== 'excessive-claims-removal' && (
                                        <>
                                            <label className={styles.postAuditFileLabel}>
                                                <FaFileExcel className={styles.postAuditFileIcon} />
                                                <span>Upload claims file (.xlsx)</span>
                                                <input
                                                    ref={alterationFileInputRef}
                                                    type="file"
                                                    accept=".xlsx"
                                                    className={styles.postAuditFileInput}
                                                    onChange={handleAlterationFileChange}
                                                />
                                            </label>
                                            {alterationFile && (
                                                <span className={styles.postAuditFileName}>{alterationFile.name}</span>
                                            )}
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.postAuditPreviewButton}
                                        onClick={handleAlterationPreview}
                                        disabled={
                                            alterationPreviewing ||
                                            (alterationType !== 'excessive-claims-removal' && !alterationFile)
                                        }
                                    >
                                        {alterationPreviewing ? (
                                            <><FaCircleNotch className={styles.spinIcon} /> {alterationType === 'excessive-claims-removal' ? 'Scanning...' : 'Previewing...'}</>
                                        ) : (
                                            <><FaClipboardCheck /> {alterationType === 'excessive-claims-removal' ? 'Scan Batch' : 'Preview'}</>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {alterationPreview && (
                                <div className={styles.postAuditPreview}>
                                    <div className={styles.postAuditSummaryGrid}>
                                        <div className={styles.postAuditSummaryCard}>
                                            <span className={styles.postAuditSummaryValue}>
                                                {alterationPreview.summary.totalRefsInFile}
                                            </span>
                                            <span className={styles.postAuditSummaryLabel}>
                                                {alterationPreview.matchBy === 'auto'
                                                    ? 'Clients Flagged'
                                                    : alterationPreview.matchBy === 'email_address'
                                                        ? 'Emails in File'
                                                        : 'References in File'}
                                            </span>
                                        </div>
                                        <div className={styles.postAuditSummaryCard}>
                                            <span className={styles.postAuditSummaryValue}>
                                                {alterationPreview.summary.matchedCount}
                                            </span>
                                            <span className={styles.postAuditSummaryLabel}>
                                                {alterationPreview.matchBy === 'auto'
                                                    ? 'Claims to Remove'
                                                    : alterationPreview.matchBy === 'email_address'
                                                        ? 'Claims Matched'
                                                        : 'Matched in Batch'}
                                            </span>
                                        </div>
                                        {alterationPreview.summary.unmatchedCount > 0 && (
                                            <div className={`${styles.postAuditSummaryCard} ${styles.postAuditSummaryCardWarning}`}>
                                                <span className={styles.postAuditSummaryValue}>
                                                    {alterationPreview.summary.unmatchedCount}
                                                </span>
                                                <span className={styles.postAuditSummaryLabel}>
                                                    Not Found
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {alterationPreview.unmatchedRefs && alterationPreview.unmatchedRefs.length > 0 && (
                                        <div className={styles.postAuditUnmatched}>
                                            <h5>
                                                {alterationPreview.matchBy === 'email_address'
                                                    ? `Unmatched Emails (${alterationPreview.unmatchedRefs.length})`
                                                    : `Unmatched References (${alterationPreview.unmatchedRefs.length})`}
                                            </h5>
                                            <div className={styles.postAuditUnmatchedList}>
                                                {alterationPreview.unmatchedRefs.map((ref: string) => (
                                                    <span key={ref} className={styles.postAuditUnmatchedRef}>{ref}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {!alterationResult && (
                                        <div className={styles.postAuditActions}>
                                            <button
                                                type="button"
                                                className={styles.postAuditProcessButton}
                                                onClick={handleAlterationProcess}
                                                disabled={alterationProcessing || !alterationPreview.matched?.length}
                                            >
                                                {alterationProcessing ? (
                                                    <><FaCircleNotch className={styles.spinIcon} /> Processing...</>
                                                ) : (
                                                    <><FaSyncAlt /> Process Alterations ({alterationPreview.matched?.length || 0} claims)</>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.postAuditCancelButton}
                                                onClick={handleAlterationReset}
                                                disabled={alterationProcessing}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {alterationResult && (
                                <div className={styles.postAuditResult}>
                                    <h5 className={styles.postAuditResultTitle}>Alteration Results</h5>
                                    <div className={styles.postAuditSummaryGrid}>
                                        <div className={styles.postAuditSummaryCard}>
                                            <span className={styles.postAuditSummaryValue}>{alterationResult.summary?.total || 0}</span>
                                            <span className={styles.postAuditSummaryLabel}>Total</span>
                                        </div>
                                        <div className={`${styles.postAuditSummaryCard} ${styles.postAuditSummaryCardSuccess}`}>
                                            <span className={styles.postAuditSummaryValue}>{alterationResult.summary?.updated || alterationResult.summary?.removed || 0}</span>
                                            <span className={styles.postAuditSummaryLabel}>{alterationResult.summary?.removed !== undefined ? 'Removed' : 'Updated'}</span>
                                        </div>
                                        {(alterationResult.summary?.clientsRemoved || 0) > 0 && (
                                            <div className={`${styles.postAuditSummaryCard} ${styles.postAuditSummaryCardError}`}>
                                                <span className={styles.postAuditSummaryValue}>{alterationResult.summary.clientsRemoved}</span>
                                                <span className={styles.postAuditSummaryLabel}>Clients Removed</span>
                                            </div>
                                        )}
                                        {(alterationResult.summary?.belowThreshold || 0) > 0 && (
                                            <div className={`${styles.postAuditSummaryCard} ${styles.postAuditSummaryCardWarning}`}>
                                                <span className={styles.postAuditSummaryValue}>{alterationResult.summary.belowThreshold}</span>
                                                <span className={styles.postAuditSummaryLabel}>Below Threshold</span>
                                            </div>
                                        )}
                                        {(alterationResult.summary?.skipped || 0) > 0 && (
                                            <div className={`${styles.postAuditSummaryCard} ${styles.postAuditSummaryCardWarning}`}>
                                                <span className={styles.postAuditSummaryValue}>{alterationResult.summary.skipped}</span>
                                                <span className={styles.postAuditSummaryLabel}>Skipped</span>
                                            </div>
                                        )}
                                        {(alterationResult.summary?.failed || 0) > 0 && (
                                            <div className={`${styles.postAuditSummaryCard} ${styles.postAuditSummaryCardError}`}>
                                                <span className={styles.postAuditSummaryValue}>{alterationResult.summary.failed}</span>
                                                <span className={styles.postAuditSummaryLabel}>Failed</span>
                                            </div>
                                        )}
                                    </div>

                                    {alterationResult.results && alterationResult.results.length > 0 && (
                                        <div className={styles.postAuditTableWrapper}>
                                            <table className={styles.postAuditTable}>
                                                <thead>
                                                    <tr>
                                                        <th>Reference</th>
                                                        <th>Status</th>
                                                        <th>Details</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {alterationResult.results.map((r: any, idx: number) => (
                                                        <tr key={idx} className={
                                                            (r.status === 'updated' || r.status === 'removed') ? styles.postAuditRowSuccess :
                                                            r.status === 'skipped' ? styles.postAuditRowWarning :
                                                            styles.postAuditRowError
                                                        }>
                                                            <td>{r.ref}</td>
                                                            <td>
                                                                <span className={
                                                                    (r.status === 'updated' || r.status === 'removed') ? styles.postAuditBadgeSuccess :
                                                                    r.status === 'skipped' ? styles.postAuditBadgeWarning :
                                                                    styles.postAuditBadgeError
                                                                }>
                                                                    {r.status}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {r.status === 'updated' && `Starting balance set to £${r.startingBalance}`}
                                                                {r.status === 'removed' && r.reason}
                                                                {r.status === 'skipped' && r.reason}
                                                                {r.status === 'failed' && r.reason}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {(alterationResult.summary?.removed || 0) > 0 && (
                                        <div className={styles.postAuditReminder}>
                                            <strong>Reminder:</strong> You must re-process the batch (Repair Batch) after this alteration has been applied so the submission sheet reflects the updated client/claim list.
                                        </div>
                                    )}

                                    {(alterationResult.summary?.updated || 0) > 0 && (
                                        <div className={styles.postAuditActions}>
                                            {!alterationReprocessResult && (
                                                <button
                                                    type="button"
                                                    className={styles.postAuditReprocessButton}
                                                    onClick={handleAlterationReprocess}
                                                    disabled={alterationReprocessing}
                                                >
                                                    {alterationReprocessing ? (
                                                        <><FaCircleNotch className={styles.spinIcon} /> Re-processing document bundles...</>
                                                    ) : (
                                                        <><FaSyncAlt /> Re-process Document Bundles ({alterationResult.summary.updated} cases)</>
                                                    )}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className={styles.postAuditProcessButton}
                                                onClick={handleAlterationExport}
                                                disabled={alterationExporting}
                                            >
                                                {alterationExporting ? (
                                                    <><FaCircleNotch className={styles.spinIcon} /> Exporting...</>
                                                ) : (
                                                    <><FaFileDownload /> Download Updated Claims</>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.postAuditProcessButton}
                                                onClick={handleQuantumCsvDownload}
                                            >
                                                <FaFileDownload /> Download Quantum CSV
                                            </button>
                                        </div>
                                    )}

                                    {alterationReprocessResult && (
                                        <div className={styles.postAuditReprocessResult}>
                                            <h5 className={styles.postAuditResultTitle}>Document Bundle Re-processing Results</h5>
                                            <div className={styles.postAuditSummaryGrid}>
                                                <div className={`${styles.postAuditSummaryCard} ${styles.postAuditSummaryCardSuccess}`}>
                                                    <span className={styles.postAuditSummaryValue}>{alterationReprocessResult.summary?.success || 0}</span>
                                                    <span className={styles.postAuditSummaryLabel}>Regenerated</span>
                                                </div>
                                                {(alterationReprocessResult.summary?.failed || 0) > 0 && (
                                                    <div className={`${styles.postAuditSummaryCard} ${styles.postAuditSummaryCardError}`}>
                                                        <span className={styles.postAuditSummaryValue}>{alterationReprocessResult.summary.failed}</span>
                                                        <span className={styles.postAuditSummaryLabel}>Failed</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className={styles.postAuditActions}>
                                        <button
                                            type="button"
                                            className={styles.postAuditCancelButton}
                                            onClick={handleAlterationReset}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        </div>
                    )}
                </div>
                    </div>
                    )}
                </div>}

                {/* Clients Table */}
                {batchDetails.clients && batchDetails.clients.length > 0 ? (
                    <div className={styles.collapsibleSection}>
                        <button
                            type="button"
                            className={styles.collapsibleSectionHeader}
                            onClick={() => setClientsSectionOpen((o) => !o)}
                            aria-expanded={clientsSectionOpen}
                        >
                            {clientsSectionOpen ? (
                                <FaChevronDown className={styles.collapsibleSectionChevron} aria-hidden />
                            ) : (
                                <FaChevronRight className={styles.collapsibleSectionChevron} aria-hidden />
                            )}
                            <h4 className={styles.collapsibleSectionTitle}>Clients</h4>
                        </button>
                        {clientsSectionOpen && (
                    <div className={styles.clientsSection}>
                        <div className={styles.clientsSectionHeader}>
                            <div className={styles.clientSearchDescription}>
                                <span className={styles.clientSearchDescriptionTitle}>Search clients</span>
                                <span className={styles.clientSearchDescriptionText}>
                                    Find any client by name, email, claim ref, secondary ref, funder client ID or funder claim ID. Opens in a new tab.
                                </span>
                            </div>
                            <div className={styles.clientSearchWrap} ref={clientSearchRef}>
                                <span className={styles.clientSearchIcon}>
                                    <FaSearch />
                                </span>
                                <input
                                    type="text"
                                    className={styles.clientSearchInput}
                                    placeholder="Search by name, email, claim ref, secondary ref, funder client ID, funder claim ID..."
                                    value={clientSearchQuery}
                                    onChange={(e) => setClientSearchQuery(e.target.value)}
                                    onFocus={() => clientSearchResults.length > 0 && setClientSearchDropdownOpen(true)}
                                />
                                {clientSearchLoading && (
                                    <span className={styles.clientSearchLoading}>Searching...</span>
                                )}
                                {clientSearchDropdownOpen &&
                                    clientSearchQuery.trim().length >= 3 &&
                                    !clientSearchLoading && (
                                        <>
                                            {clientSearchResults.length > 0 ? (
                                                <ul className={styles.clientSearchDropdown}>
                                                    {clientSearchResults.map((c) => (
                                                        <li
                                                            key={c.client_id}
                                                            className={styles.clientSearchResultItem}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    setClientSearchDropdownOpen(false)
                                                                    setClientSearchQuery('')
                                                                    window.open(
                                                                        `/funding-client/${c.client_id}`,
                                                                        '_blank',
                                                                        'noopener,noreferrer'
                                                                    )
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                setClientSearchDropdownOpen(false)
                                                                setClientSearchQuery('')
                                                                window.open(
                                                                    `/funding-client/${c.client_id}`,
                                                                    '_blank',
                                                                    'noopener,noreferrer'
                                                                )
                                                            }}
                                                        >
                                                            <span className={styles.clientSearchResultName}>
                                                                {c.name || 'Unnamed'}
                                                            </span>
                                                            <span className={styles.clientSearchResultMeta}>
                                                                {[
                                                                    c.email_address,
                                                                    c.batch_group,
                                                                    c.funder_case_refs,
                                                                    c.law_firm_secondary_refs,
                                                                    c.client_id != null ? `Client ID: ${c.client_id}` : null,
                                                                    c.funder_claim_ids ? `Claim ID: ${c.funder_claim_ids}` : null
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(' · ') || '—'}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <div className={styles.clientSearchEmpty}>No clients found</div>
                                            )}
                                        </>
                                    )}
                            </div>
                        </div>
                        <div className={styles.clientsTableWrapper}>
                            <table className={styles.clientsTable}>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Client ID</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>DOB</th>
                                        <th>Introducer</th>
                                        <th>Total Value</th>
                                        <th>Claims</th>
                                        <th>API Transferred</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {batchDetails.clients.map((client) => (
                                        <>
                                            <tr key={client.funder_client_id} className={styles.clientRow}>
                                                <td className={styles.expandCell}>
                                                    {client.claims && client.claims.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className={styles.expandButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                toggleClientClaims(client.funder_client_id)
                                                            }}
                                                        >
                                                            <span
                                                                className={`${styles.expandIcon} ${
                                                                    expandedClients.has(client.funder_client_id)
                                                                        ? styles.expanded
                                                                        : ''
                                                                }`}
                                                            >
                                                                ▼
                                                            </span>
                                                        </button>
                                                    )}
                                                </td>
                                                <td
                                                    onClick={() =>
                                                        navigate(`/funding-client/${client.funder_client_id}`)
                                                    }
                                                    className={styles.clickableCell}
                                                >
                                                    {client.funder_client_id}
                                                </td>
                                                <td
                                                    onClick={() =>
                                                        navigate(`/funding-client/${client.funder_client_id}`)
                                                    }
                                                    className={styles.clickableCell}
                                                >
                                                    {client.full_name ||
                                                        `${client.title} ${client.first_name} ${client.last_name}`}
                                                </td>
                                                <td
                                                    onClick={() =>
                                                        navigate(`/funding-client/${client.funder_client_id}`)
                                                    }
                                                    className={styles.clickableCell}
                                                >
                                                    {client.email_address || 'N/A'}
                                                </td>
                                                <td
                                                    onClick={() =>
                                                        navigate(`/funding-client/${client.funder_client_id}`)
                                                    }
                                                    className={styles.clickableCell}
                                                >
                                                    {formatDate(client.dob)}
                                                </td>
                                                <td
                                                    onClick={() =>
                                                        navigate(`/funding-client/${client.funder_client_id}`)
                                                    }
                                                    className={styles.clickableCell}
                                                >
                                                    {client.introducer || 'N/A'}
                                                </td>
                                                <td
                                                    onClick={() =>
                                                        navigate(`/funding-client/${client.funder_client_id}`)
                                                    }
                                                    className={`${styles.valueCell} ${styles.clickableCell}`}
                                                >
                                                    {formatCurrency(client.total_value)}
                                                </td>
                                                <td
                                                    onClick={() =>
                                                        navigate(`/funding-client/${client.funder_client_id}`)
                                                    }
                                                    className={styles.clickableCell}
                                                >
                                                    {client.claims_count || 0}
                                                </td>
                                                <td
                                                    onClick={() =>
                                                        navigate(`/funding-client/${client.funder_client_id}`)
                                                    }
                                                    className={styles.clickableCell}
                                                >
                                                    {(() => {
                                                        const claims = client.claims || []
                                                        if (claims.length === 0) return '—'
                                                        const yesCount = claims.filter(
                                                            (c) =>
                                                                c.api_transferred &&
                                                                String(c.api_transferred).toLowerCase() === 'yes'
                                                        ).length
                                                        if (yesCount === claims.length)
                                                            return (
                                                                <span
                                                                    className={`${styles.statusBadge} ${styles.active}`}
                                                                >
                                                                    Yes
                                                                </span>
                                                            )
                                                        if (yesCount === 0)
                                                            return (
                                                                <span
                                                                    className={`${styles.statusBadge} ${styles.inactive}`}
                                                                >
                                                                    No
                                                                </span>
                                                            )
                                                        return (
                                                            <span className={`${styles.statusBadge} ${styles.partial}`}>
                                                                Partial ({yesCount}/{claims.length})
                                                            </span>
                                                        )
                                                    })()}
                                                </td>
                                            </tr>
                                            {expandedClients.has(client.funder_client_id) &&
                                                client.claims &&
                                                client.claims.length > 0 && (
                                                    <tr key={`${client.funder_client_id}-claims`}>
                                                        <td colSpan={9} className={styles.claimsCell}>
                                                            <div className={styles.claimsTableContainer}>
                                                                <h6 className={styles.claimsTitle}>
                                                                    Claims for{' '}
                                                                    {client.full_name ||
                                                                        `${client.first_name} ${client.last_name}`}
                                                                </h6>
                                                                <div className={styles.claimsTable}>
                                                                    <table>
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Funder Ref</th>
                                                                                <th>Introducer Ref</th>
                                                                                <th>Loan Value</th>
                                                                                <th>Quantum</th>
                                                                                <th>Defendant</th>
                                                                                <th>CFA Date</th>
                                                                                <th>Status</th>
                                                                                <th>Allocated</th>
                                                                                <th>Submitted</th>
                                                                                <th>API Transferred</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {client.claims.map((claim, idx) => (
                                                                                <tr key={idx}>
                                                                                    <td>
                                                                                        {claim.funder_case_ref || 'N/A'}
                                                                                    </td>
                                                                                    <td>
                                                                                        {claim.introducer_case_ref ||
                                                                                            'N/A'}
                                                                                    </td>
                                                                                    <td>
                                                                                        {formatCurrency(
                                                                                            claim.loan_value || 0
                                                                                        )}
                                                                                    </td>
                                                                                    <td>
                                                                                        {formatCurrency(
                                                                                            claim.quantum_individual ||
                                                                                                0
                                                                                        )}
                                                                                    </td>
                                                                                    <td>{claim.defendant || 'N/A'}</td>
                                                                                    <td>
                                                                                        {formatDate(claim.cfa_date)}
                                                                                    </td>
                                                                                    <td>
                                                                                        {claim.client_status || 'N/A'}
                                                                                    </td>
                                                                                    <td>
                                                                                        <span
                                                                                            className={`${
                                                                                                styles.statusBadge
                                                                                            } ${
                                                                                                String(
                                                                                                    claim.allocated ??
                                                                                                        ''
                                                                                                ).toLowerCase() ===
                                                                                                'yes'
                                                                                                    ? styles.active
                                                                                                    : styles.inactive
                                                                                            }`}
                                                                                        >
                                                                                            {String(
                                                                                                claim.allocated ?? ''
                                                                                            ).toLowerCase() === 'yes'
                                                                                                ? 'Yes'
                                                                                                : 'No'}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td>
                                                                                        <span
                                                                                            className={`${
                                                                                                styles.statusBadge
                                                                                            } ${
                                                                                                String(
                                                                                                    claim.submitted ??
                                                                                                        ''
                                                                                                ).toLowerCase() ===
                                                                                                'yes'
                                                                                                    ? styles.active
                                                                                                    : styles.inactive
                                                                                            }`}
                                                                                        >
                                                                                            {String(
                                                                                                claim.submitted ?? ''
                                                                                            ).toLowerCase() === 'yes'
                                                                                                ? 'Yes'
                                                                                                : 'No'}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td>
                                                                                        <span
                                                                                            className={`${
                                                                                                styles.statusBadge
                                                                                            } ${
                                                                                                String(
                                                                                                    claim.api_transferred ??
                                                                                                        ''
                                                                                                ).toLowerCase() ===
                                                                                                'yes'
                                                                                                    ? styles.active
                                                                                                    : styles.inactive
                                                                                            }`}
                                                                                        >
                                                                                            {String(
                                                                                                claim.api_transferred ??
                                                                                                    ''
                                                                                            ).toLowerCase() === 'yes'
                                                                                                ? 'Yes'
                                                                                                : 'No'}
                                                                                        </span>
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                        </>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination */}
                        {batchDetails.pagination && batchDetails.pagination.total_pages > 1 && (
                            <div className={styles.paginationSection}>
                                <div className={styles.paginationInfo}>
                                    <span>
                                        Page {batchDetails.pagination.page} of {batchDetails.pagination.total_pages}
                                    </span>
                                    <span>
                                        Showing{' '}
                                        {(
                                            (batchDetails.pagination.page - 1) * batchDetails.pagination.limit +
                                            1
                                        ).toLocaleString()}{' '}
                                        -{' '}
                                        {Math.min(
                                            batchDetails.pagination.page * batchDetails.pagination.limit,
                                            batchDetails.pagination.total_clients
                                        ).toLocaleString()}{' '}
                                        of {batchDetails.pagination.total_clients.toLocaleString()} clients
                                    </span>
                                </div>
                                <div className={styles.paginationControls}>
                                    <button
                                        type="button"
                                        className={styles.paginationButton}
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={!batchDetails.pagination.has_prev}
                                    >
                                        Previous
                                    </button>
                                    {Array.from({ length: batchDetails.pagination.total_pages }, (_, i) => i + 1)
                                        .filter((page) => {
                                            const current = batchDetails.pagination.page
                                            const total = batchDetails.pagination.total_pages
                                            return page === 1 || page === total || (page >= current - 2 && page <= current + 2)
                                        })
                                        .map((page, idx, arr) => (
                                            <div key={page} className={styles.paginationPages}>
                                                {idx > 0 && arr[idx - 1] !== page - 1 && (
                                                    <span className={styles.paginationEllipsis}>...</span>
                                                )}
                                                <button
                                                    type="button"
                                                    className={`${styles.paginationButton} ${
                                                        page === batchDetails.pagination.page ? styles.active : ''
                                                    }`}
                                                    onClick={() => handlePageChange(page)}
                                                >
                                                    {page}
                                                </button>
                                            </div>
                                        ))}
                                    <button
                                        type="button"
                                        className={styles.paginationButton}
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={!batchDetails.pagination.has_next}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                        )}
                    </div>
                ) : (
                    <div className={styles.noResults}>
                        <p>No clients found for this batch.</p>
                    </div>
                )}

            </div>

            {/* Transfer by API Modal */}
            {!isScoreUser && showTransferModal && (
                <div className={styles.modalOverlay} onClick={handleCloseTransferModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Transfer cases by API</h3>
                            <button
                                type="button"
                                className={styles.modalCloseButton}
                                onClick={handleCloseTransferModal}
                                aria-label="Close modal"
                                disabled={isTransferring}
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <p>
                                Send <strong>clients</strong> in this batch (and all their claims) to Trinity via the
                                API. Choose how many to send:
                            </p>
                            <div className={styles.confirmInput}>
                                <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <input
                                            type="radio"
                                            name="transferMode"
                                            checked={transferMode === 'percentage'}
                                            onChange={() => setTransferMode('percentage')}
                                            disabled={isTransferring}
                                        />
                                        <span>Percentage of clients (1–100)</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <input
                                            type="radio"
                                            name="transferMode"
                                            checked={transferMode === 'count'}
                                            onChange={() => setTransferMode('count')}
                                            disabled={isTransferring}
                                        />
                                        <span>Specific number of clients</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <input
                                            type="radio"
                                            name="transferMode"
                                            checked={transferMode === 'all'}
                                            onChange={() => setTransferMode('all')}
                                            disabled={isTransferring}
                                        />
                                        <span>Send all clients in the batch (100%)</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <input
                                            type="radio"
                                            name="transferMode"
                                            checked={transferMode === 'remaining'}
                                            onChange={() => setTransferMode('remaining')}
                                            disabled={isTransferring}
                                        />
                                        <span>Send all remaining (only clients not yet transferred)</span>
                                    </label>
                                </fieldset>
                                {transferMode === 'percentage' && (
                                    <div style={{ marginTop: 12 }}>
                                        <label htmlFor="transferPercentage">
                                            Percentage of clients to transfer (1–100)
                                        </label>
                                        <input
                                            id="transferPercentage"
                                            type="number"
                                            min={1}
                                            max={100}
                                            value={transferPercentage}
                                            onChange={(e) =>
                                                setTransferPercentage(
                                                    Math.min(100, Math.max(1, Number(e.target.value) || 1))
                                                )
                                            }
                                            disabled={isTransferring}
                                        />
                                    </div>
                                )}
                                {transferMode === 'count' && (
                                    <div style={{ marginTop: 12 }}>
                                        <label htmlFor="transferClientCount">
                                            Number of clients to transfer
                                        </label>
                                        <input
                                            id="transferClientCount"
                                            type="number"
                                            min={1}
                                            value={transferClientCount}
                                            onChange={(e) =>
                                                setTransferClientCount(
                                                    Math.max(1, Math.floor(Number(e.target.value) || 1))
                                                )
                                            }
                                            disabled={isTransferring}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCloseTransferModal}
                                disabled={isTransferring}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.transferConfirmButton}
                                onClick={handleTransferByApi}
                                disabled={isTransferring}
                            >
                                {isTransferring
                                    ? 'Transferring...'
                                    : transferMode === 'percentage'
                                    ? `Transfer ${transferPercentage}%`
                                    : transferMode === 'count'
                                    ? `Transfer ${transferClientCount} client${transferClientCount === 1 ? '' : 's'}`
                                    : transferMode === 'all'
                                    ? 'Transfer all'
                                    : 'Transfer all remaining'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Split Batch Modal */}
            {!isScoreUser && showSplitModal && (
                <div className={styles.modalOverlay} onClick={handleCloseSplitModal}>
                    <div
                        className={`${styles.modalContent} ${styles.splitModalContent}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <h3>Split Batch</h3>
                            <button
                                type="button"
                                className={styles.modalCloseButton}
                                onClick={handleCloseSplitModal}
                                aria-label="Close modal"
                                disabled={isSplitting}
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            {splitPreviewLoading && <p>Loading clients…</p>}
                            {!splitPreviewLoading && splitPreview && !splitPreview.canSplit && (
                                <div className={styles.warningBanner}>
                                    <FaExclamationTriangle />
                                    <span>{splitPreview.reason || 'This batch cannot be split.'}</span>
                                </div>
                            )}
                            {!splitPreviewLoading && splitPreview?.canSplit && (
                                <>
                                    <p>
                                        Move clients from this batch into a new funding batch. Document bundles stay as-is;
                                        only the batch reference is updated. Config (calculation date, creation date, rates,
                                        etc.) is cloned exactly — except <strong>max clients</strong> and an
                                        incremented <strong>nera id</strong> (e.g. ur12 → ur13) on the new batch.
                                    </p>

                                    <fieldset className={styles.splitModeFieldset}>
                                        <label className={styles.splitModeLabel}>
                                            <input
                                                type="radio"
                                                name="splitMode"
                                                checked={splitMode === 'auto'}
                                                onChange={() => setSplitMode('auto')}
                                                disabled={isSplitting}
                                            />
                                            <span>Auto-select N clients</span>
                                        </label>
                                        <label className={styles.splitModeLabel}>
                                            <input
                                                type="radio"
                                                name="splitMode"
                                                checked={splitMode === 'manual'}
                                                onChange={() => setSplitMode('manual')}
                                                disabled={isSplitting}
                                            />
                                            <span>Select specific clients</span>
                                        </label>
                                    </fieldset>

                                    <div className={styles.splitFilters}>
                                        <label>
                                            Lender
                                            <select
                                                value={splitLenderFilter}
                                                onChange={(e) => setSplitLenderFilter(e.target.value)}
                                                disabled={isSplitting}
                                            >
                                                <option value="">All lenders</option>
                                                {(splitPreview.lenders || []).map((l) => (
                                                    <option key={l} value={l}>
                                                        {l}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label>
                                            Min value (£)
                                            <input
                                                type="number"
                                                value={splitMinValue}
                                                onChange={(e) => setSplitMinValue(e.target.value)}
                                                disabled={isSplitting}
                                                min={0}
                                                step="0.01"
                                            />
                                        </label>
                                        <label>
                                            Max value (£)
                                            <input
                                                type="number"
                                                value={splitMaxValue}
                                                onChange={(e) => setSplitMaxValue(e.target.value)}
                                                disabled={isSplitting}
                                                min={0}
                                                step="0.01"
                                            />
                                        </label>
                                        <label>
                                            Min claims
                                            <input
                                                type="number"
                                                value={splitMinClaims}
                                                onChange={(e) => setSplitMinClaims(e.target.value)}
                                                disabled={isSplitting}
                                                min={0}
                                                step={1}
                                            />
                                        </label>
                                        <label>
                                            Max claims
                                            <input
                                                type="number"
                                                value={splitMaxClaims}
                                                onChange={(e) => setSplitMaxClaims(e.target.value)}
                                                disabled={isSplitting}
                                                min={0}
                                                step={1}
                                            />
                                        </label>
                                        <label className={styles.splitSearchLabel}>
                                            Search
                                            <input
                                                type="text"
                                                value={splitSearch}
                                                onChange={(e) => setSplitSearch(e.target.value)}
                                                disabled={isSplitting}
                                                placeholder="Name or email"
                                            />
                                        </label>
                                    </div>

                                    {splitMode === 'auto' && (
                                        <div className={styles.confirmInput}>
                                            <label htmlFor="splitClientCount">
                                                Clients to move to new batch (of {filteredSplitClients.length} matching)
                                            </label>
                                            <input
                                                id="splitClientCount"
                                                type="number"
                                                min={1}
                                                max={Math.max(1, filteredSplitClients.length - 1)}
                                                value={splitClientCount}
                                                onChange={(e) =>
                                                    setSplitClientCount(
                                                        Math.max(1, parseInt(e.target.value, 10) || 1)
                                                    )
                                                }
                                                disabled={isSplitting}
                                            />
                                        </div>
                                    )}

                                    {splitMode === 'manual' && (
                                        <div className={styles.splitClientTableWrap}>
                                            <div className={styles.splitClientTableActions}>
                                                <button
                                                    type="button"
                                                    className={styles.cancelButton}
                                                    disabled={isSplitting || filteredSplitClients.length === 0}
                                                    onClick={() =>
                                                        setSplitSelectedIds(
                                                            new Set(
                                                                filteredSplitClients.map((c) => c.funder_client_id)
                                                            )
                                                        )
                                                    }
                                                >
                                                    Select filtered
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.cancelButton}
                                                    disabled={isSplitting || splitSelectedIds.size === 0}
                                                    onClick={() => setSplitSelectedIds(new Set())}
                                                >
                                                    Clear
                                                </button>
                                                <span>
                                                    {splitSelectedIds.size} selected · {filteredSplitClients.length}{' '}
                                                    shown
                                                </span>
                                            </div>
                                            <table className={styles.splitClientTable}>
                                                <thead>
                                                    <tr>
                                                        <th></th>
                                                        <th>Client</th>
                                                        <th>Claims</th>
                                                        <th>Value</th>
                                                        <th>Lenders</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredSplitClients.map((c) => (
                                                        <tr key={c.funder_client_id}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={splitSelectedIds.has(
                                                                        c.funder_client_id
                                                                    )}
                                                                    disabled={isSplitting}
                                                                    onChange={() => {
                                                                        setSplitSelectedIds((prev) => {
                                                                            const next = new Set(prev)
                                                                            if (next.has(c.funder_client_id)) {
                                                                                next.delete(c.funder_client_id)
                                                                            } else {
                                                                                next.add(c.funder_client_id)
                                                                            }
                                                                            return next
                                                                        })
                                                                    }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div>{c.full_name || '—'}</div>
                                                                <div className={styles.splitClientEmail}>
                                                                    {c.email_address || '—'}
                                                                </div>
                                                            </td>
                                                            <td>{c.claims_count}</td>
                                                            <td>£{Number(c.total_value || 0).toFixed(2)}</td>
                                                            <td>{(c.lenders || []).join(', ') || '—'}</td>
                                                        </tr>
                                                    ))}
                                                    {filteredSplitClients.length === 0 && (
                                                        <tr>
                                                            <td colSpan={5}>No clients match the current filters.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <div className={styles.splitSummary}>
                                        <div>
                                            <strong>Moving:</strong> {splitMovePreview.movedClients} clients ·{' '}
                                            {splitMovePreview.movedClaims} claims · £
                                            {splitMovePreview.movedValue.toFixed(2)}
                                        </div>
                                        <div>
                                            <strong>Remaining:</strong> {splitMovePreview.remainingClients} clients ·{' '}
                                            {splitMovePreview.remainingClaims} claims · £
                                            {splitMovePreview.remainingValue.toFixed(2)}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCloseSplitModal}
                                disabled={isSplitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.transferConfirmButton}
                                onClick={handleConfirmSplit}
                                disabled={
                                    isSplitting ||
                                    splitPreviewLoading ||
                                    !splitPreview?.canSplit ||
                                    splitMovePreview.movedClients < 1 ||
                                    splitMovePreview.remainingClients < 1
                                }
                            >
                                {isSplitting
                                    ? 'Splitting...'
                                    : `Split ${splitMovePreview.movedClients} client${
                                          splitMovePreview.movedClients === 1 ? '' : 's'
                                      }`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transfer result / failure modal */}
            {!isScoreUser && transferResultModal.show && (
                <div className={styles.modalOverlay} onClick={handleCloseTransferResultModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={transferResultModal.success ? '' : styles.transferResultModalTitleError}>
                                {transferResultModal.success ? 'Transfer completed with issues' : 'Transfer failed'}
                            </h3>
                            <button
                                type="button"
                                className={styles.modalCloseButton}
                                onClick={handleCloseTransferResultModal}
                                aria-label="Close modal"
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <p className={styles.transferResultMessage}>{transferResultModal.message}</p>
                            {(transferResultModal.transferredCount !== undefined ||
                                transferResultModal.failedCount !== undefined) && (
                                <div className={styles.transferResultBreakdown}>
                                    <h4>Summary</h4>
                                    <ul className={styles.transferResultList}>
                                        {transferResultModal.transferredCount !== undefined && (
                                            <li>
                                                <strong>Transferred:</strong>{' '}
                                                {transferResultModal.transferredCount.toLocaleString()} claims
                                            </li>
                                        )}
                                        {transferResultModal.failedCount !== undefined &&
                                            transferResultModal.failedCount > 0 && (
                                                <li>
                                                    <strong>Failed:</strong>{' '}
                                                    {transferResultModal.failedCount.toLocaleString()} claims
                                                </li>
                                            )}
                                    </ul>
                                </div>
                            )}
                            {transferResultModal.failedCsvPresignedUrl && (
                                <div className={styles.transferResultCsvSection}>
                                    <p>
                                        Download the failure report to see which cases failed and the reason for each
                                        failure.
                                    </p>
                                    <a
                                        href={transferResultModal.failedCsvPresignedUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.transferResultCsvLink}
                                    >
                                        <FaFileDownload /> Download failure report (CSV)
                                    </a>
                                </div>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCloseTransferResultModal}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Process Allocation Modal */}
            {!isScoreUser && showAllocateModal && (
                <div className={styles.modalOverlay} onClick={handleCloseAllocateModal}>
                    <div
                        className={`${styles.modalContent} ${styles.reprocessModalContent}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <h3>{allocateResume ? 'Resume Processing' : 'Process Allocation'}</h3>
                            <button
                                type="button"
                                className={styles.modalCloseButton}
                                onClick={handleCloseAllocateModal}
                                aria-label="Close modal"
                                disabled={processing}
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <p>
                                Choose which documents to <strong>regenerate</strong>. Unchecked documents are
                                collected from S3 and inserted into the bundle as-is.
                                {allocateResume
                                    ? ' Completed records are skipped; these choices apply to the remainder.'
                                    : ''}
                            </p>

                            <div className={styles.reprocessDocActions}>
                                <button
                                    type="button"
                                    className={styles.reprocessLinkButton}
                                    onClick={() => setAllocateDocKeys([...ALL_REPROCESS_DOC_KEYS])}
                                >
                                    Select all
                                </button>
                                <button
                                    type="button"
                                    className={styles.reprocessLinkButton}
                                    onClick={() => setAllocateDocKeys([])}
                                >
                                    Select none
                                </button>
                            </div>

                            <div className={styles.reprocessDocList}>
                                {REPROCESS_DOCUMENTS.map((doc) => (
                                    <label key={doc.key} className={styles.reprocessDocRow}>
                                        <input
                                            type="checkbox"
                                            checked={allocateDocKeys.includes(doc.key)}
                                            onChange={() => toggleAllocateDoc(doc.key)}
                                        />
                                        <span>
                                            <strong>{doc.label}</strong>
                                            <span className={styles.reprocessDocHint}>
                                                {allocateDocKeys.includes(doc.key)
                                                    ? 'Regenerate'
                                                    : 'Collect from S3'}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>

                            <p className={styles.reprocessS3OnlyNote}>
                                Always collected from S3: {S3_ONLY_DOC_LABELS.join(', ')}.
                            </p>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCloseAllocateModal}
                                disabled={processing}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.reprocessConfirmButton}
                                onClick={handleConfirmAllocateBatch}
                                disabled={processing}
                            >
                                {allocateResume ? (
                                    <>
                                        <FaPlay /> Resume processing
                                    </>
                                ) : (
                                    <>Process allocation</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Re-process Batch Modal */}
            {!isScoreUser && showReprocessModal && (
                <div className={styles.modalOverlay} onClick={handleCloseReprocessModal}>
                    <div
                        className={`${styles.modalContent} ${styles.reprocessModalContent}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <h3>Re-process Batch</h3>
                            <button
                                type="button"
                                className={styles.modalCloseButton}
                                onClick={handleCloseReprocessModal}
                                aria-label="Close modal"
                                disabled={sampleRunning}
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <p>
                                Choose which documents to <strong>regenerate</strong>. Unchecked documents are
                                collected from S3 and inserted into the new bundle as-is.
                            </p>

                            <div className={styles.reprocessDocActions}>
                                <button
                                    type="button"
                                    className={styles.reprocessLinkButton}
                                    onClick={() => {
                                        setReprocessDocKeys([...ALL_REPROCESS_DOC_KEYS])
                                        setSampleResult(null)
                                    }}
                                >
                                    Select all
                                </button>
                                <button
                                    type="button"
                                    className={styles.reprocessLinkButton}
                                    onClick={() => {
                                        setReprocessDocKeys([])
                                        setSampleResult(null)
                                    }}
                                >
                                    Select none
                                </button>
                            </div>

                            <div className={styles.reprocessDocList}>
                                {REPROCESS_DOCUMENTS.map((doc) => (
                                    <label key={doc.key} className={styles.reprocessDocRow}>
                                        <input
                                            type="checkbox"
                                            checked={reprocessDocKeys.includes(doc.key)}
                                            onChange={() => toggleReprocessDoc(doc.key)}
                                        />
                                        <span>
                                            <strong>{doc.label}</strong>
                                            <span className={styles.reprocessDocHint}>
                                                {reprocessDocKeys.includes(doc.key)
                                                    ? 'Regenerate'
                                                    : 'Collect from S3'}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>

                            <p className={styles.reprocessS3OnlyNote}>
                                Always collected from S3: {S3_ONLY_DOC_LABELS.join(', ')}.
                            </p>

                            <div className={styles.reprocessSampleSection}>
                                <h4>Sample preview (one claim)</h4>
                                <p>
                                    Runs a dry-run with the options above. Live production documents and
                                    bundles are not overwritten — only a preview pack is created.
                                </p>

                                <label className={styles.reprocessSearchLabel} htmlFor="sampleClaimSearch">
                                    Search client or case
                                </label>
                                <div className={styles.reprocessSearchWrap} ref={sampleSearchRef}>
                                    <span className={styles.reprocessSearchIcon}>
                                        <FaSearch />
                                    </span>
                                    <input
                                        id="sampleClaimSearch"
                                        type="text"
                                        className={styles.reprocessSearchInput}
                                        placeholder="Name, email, funder ref, or case ref (min 2 chars)"
                                        value={sampleSearchQuery}
                                        onChange={(e) => setSampleSearchQuery(e.target.value)}
                                        onFocus={() =>
                                            sampleSearchResults.length > 0 && setSampleSearchDropdownOpen(true)
                                        }
                                        disabled={sampleRunning}
                                        autoComplete="off"
                                    />
                                    {sampleSearchLoading && (
                                        <span className={styles.reprocessSearchLoading}>Searching...</span>
                                    )}
                                    {sampleSearchDropdownOpen &&
                                        sampleSearchQuery.trim().length >= 2 &&
                                        !sampleSearchLoading && (
                                            <>
                                                {sampleSearchResults.length > 0 ? (
                                                    <ul className={styles.reprocessSearchDropdown}>
                                                        {sampleSearchResults.map((opt) => (
                                                            <li
                                                                key={opt.key}
                                                                className={styles.reprocessSearchResultItem}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => selectSampleClaim(opt)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') selectSampleClaim(opt)
                                                                }}
                                                            >
                                                                {opt.label}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <div className={styles.reprocessSearchEmpty}>
                                                        No matching claims in this batch
                                                    </div>
                                                )}
                                            </>
                                        )}
                                </div>

                                {sampleSelectedClaim ? (
                                    <div className={styles.reprocessSelectedClaim}>
                                        <span>
                                            Selected: <strong>{sampleSelectedClaim.label}</strong>
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.reprocessLinkButton}
                                            onClick={() => {
                                                setSampleSelectedClaim(null)
                                                setSampleResult(null)
                                            }}
                                            disabled={sampleRunning}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                ) : sampleClaimOptions.length > 0 ? (
                                    <div className={styles.reprocessQuickPicks}>
                                        <span className={styles.reprocessS3OnlyNote}>
                                            Or pick from this page:
                                        </span>
                                        <select
                                            className={styles.reprocessClaimSelect}
                                            value=""
                                            onChange={(e) => {
                                                const opt = sampleClaimOptions.find((o) => o.key === e.target.value)
                                                if (opt) selectSampleClaim(opt)
                                            }}
                                            disabled={sampleRunning}
                                        >
                                            <option value="">Select a claim on this page...</option>
                                            {sampleClaimOptions.map((opt) => (
                                                <option key={opt.key} value={opt.key}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ) : (
                                    <p className={styles.reprocessS3OnlyNote}>
                                        Search above to find a claim in this batch.
                                    </p>
                                )}

                                <button
                                    type="button"
                                    className={styles.reprocessSampleButton}
                                    onClick={handleSampleReprocess}
                                    disabled={sampleRunning || !sampleSelectedClaim}
                                >
                                    {sampleRunning ? (
                                        <>
                                            <FaCircleNotch className={styles.spinIcon} /> Generating sample...
                                        </>
                                    ) : (
                                        <>
                                            <FaPlay /> Run sample
                                        </>
                                    )}
                                </button>

                                {sampleResult && (
                                    <div className={styles.reprocessSampleResult}>
                                        <div className={styles.reprocessSampleResultHeader}>
                                            Sample ready for {sampleResult.case_ref}
                                            {sampleResult.bundleUrl && (
                                                <a
                                                    href={sampleResult.bundleUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.reprocessBundleLink}
                                                >
                                                    <FaFileDownload /> Download preview bundle
                                                </a>
                                            )}
                                        </div>
                                        <ul className={styles.reprocessSourceList}>
                                            {(sampleResult.documentSources || [])
                                                .filter((d) => d.docName !== 'Cover Page')
                                                .map((d) => (
                                                    <li key={d.docName}>
                                                        <span>{d.docName}</span>
                                                        <span
                                                            className={
                                                                d.source === 'regenerated'
                                                                    ? styles.sourceRegen
                                                                    : styles.sourceS3
                                                            }
                                                        >
                                                            {d.source === 'regenerated'
                                                                ? 'Regenerated'
                                                                : 'Collected from S3'}
                                                        </span>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCloseReprocessModal}
                                disabled={sampleRunning || processing}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.reprocessConfirmButton}
                                onClick={handleConfirmReprocessBatch}
                                disabled={sampleRunning || processing}
                            >
                                <FaSyncAlt /> Re-process entire batch
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {!isScoreUser && showDeleteModal && (
                <div className={styles.modalOverlay} onClick={handleCloseDeleteModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Delete Funding Batch</h3>
                            <button
                                type="button"
                                className={styles.modalCloseButton}
                                onClick={handleCloseDeleteModal}
                                aria-label="Close modal"
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.warningBanner}>
                                <FaTrashAlt className={styles.warningIcon} />
                                <div>
                                    <strong>Warning:</strong> This action cannot be undone.
                                </div>
                            </div>
                            <p>
                                You are about to delete batch <strong>{batchDetails.batch_id}</strong> and all
                                associated data including:
                            </p>
                            <ul className={styles.deleteList}>
                                <li>{batchDetails.batch_summary?.total_clients || 0} client records</li>
                                <li>{batchDetails.batch_summary?.total_claims || 0} claim records</li>
                                <li>Batch configuration and metadata</li>
                            </ul>
                            <div className={styles.confirmInput}>
                                <label htmlFor="deleteConfirm">
                                    Type <strong>delete</strong> to confirm:
                                </label>
                                <input
                                    id="deleteConfirm"
                                    type="text"
                                    value={deleteConfirmText}
                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                    placeholder="Type 'delete' to confirm"
                                    autoComplete="off"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCloseDeleteModal}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.confirmDeleteButton}
                                onClick={handleDeleteBatch}
                                disabled={deleteConfirmText.toLowerCase() !== 'delete' || isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete Batch'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Purge Removed Clients Confirmation Modal */}
            {!isScoreUser && showResetMembershipModal && resetMembershipPreview && (() => {
                const p = resetMembershipPreview
                const totalChanges = p.flagged.count + p.missing.claims + p.orphaned.recoverableClaims
                return (
                    <div className={styles.modalOverlay} onClick={handleCloseResetMembershipModal}>
                        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <h3>Reset Batch Membership</h3>
                                <button
                                    type="button"
                                    className={styles.modalCloseButton}
                                    onClick={handleCloseResetMembershipModal}
                                    aria-label="Close modal"
                                >
                                    &times;
                                </button>
                            </div>
                            <div className={styles.modalBody}>
                                {totalChanges === 0 ? (
                                    <p>
                                        Nothing to reset. Every claim belonging to this batch is already active:{' '}
                                        <strong>{p.current.references} claim(s)</strong> across{' '}
                                        <strong>{p.current.clients} client(s)</strong>.
                                    </p>
                                ) : (
                                    <>
                                        <div className={styles.warningBanner}>
                                            <FaUndo className={styles.warningIcon} />
                                            <div>
                                                This rebuilds the batch to everything that was originally allocated to it.
                                                Existing bundles, ATE data and quantum values on the claims are untouched.
                                                The submission sheet is regenerated and the client cumulative totals are
                                                recalculated.
                                            </div>
                                        </div>
                                        <p>
                                            Batch <strong>{batchId}</strong> currently has{' '}
                                            <strong>{p.current.references} active claim(s)</strong> across{' '}
                                            <strong>{p.current.clients} client(s)</strong>. After the reset it will have{' '}
                                            <strong>{p.after.references} claim(s)</strong>,{' '}
                                            <strong>{p.after.withoutPack}</strong> of which still need a document bundle.
                                        </p>
                                        <ul className={styles.deleteList}>
                                            <li>
                                                <strong>{p.flagged.count}</strong> audit-removed claim(s) will have their audit flag cleared
                                                {p.flagged.byReason.length > 0 && (
                                                    <> ({p.flagged.byReason.map((r) => `${r.reason}: ${r.count}`).join(', ')})</>
                                                )}
                                            </li>
                                            <li>
                                                <strong>{p.missing.claims}</strong> claim(s) across {p.missing.clients} client(s) were deleted
                                                from the batch (skip repair / minimum value) and will be reinstated
                                                {p.missing.withoutPack > 0 && <> — {p.missing.withoutPack} without a bundle</>}
                                            </li>
                                            <li>
                                                <strong>{p.orphaned.recoverableClients}</strong> deleted client record(s) will be rebuilt from{' '}
                                                {p.introducer || 'the introducer'} along with their {p.orphaned.recoverableClaims} claim(s)
                                            </li>
                                        </ul>
                                        {p.orphaned.unrecoverable.length > 0 && (
                                            <p className={styles.purgeSkippedNote}>
                                                <strong>{p.orphaned.unrecoverable.length} deleted client(s) cannot be recovered</strong>{' '}
                                                and will be left out:{' '}
                                                {p.orphaned.unrecoverable
                                                    .slice(0, 5)
                                                    .map((u) => `client ${u.funderClientId} (${u.claims} claim(s): ${u.reason})`)
                                                    .join('; ')}
                                                {p.orphaned.unrecoverable.length > 5 && <> and {p.orphaned.unrecoverable.length - 5} more</>}
                                            </p>
                                        )}
                                        {p.current.transferred > 0 && (
                                            <p className={styles.purgeSkippedNote}>
                                                <strong>{p.current.transferred} claim(s) have already been transferred by API.</strong>{' '}
                                                Reinstated claims are added with transfer status "No"; re-run the transfer for them afterwards.
                                            </p>
                                        )}
                                        {p.after.withoutPack > 0 && (
                                            <p className={styles.purgeSkippedNote}>
                                                The batch will be marked <strong>Complete with errors</strong> so you can run{' '}
                                                <strong>Repair</strong> (choosing shards and document types) over the{' '}
                                                {p.after.withoutPack} claim(s) without a bundle.
                                            </p>
                                        )}
                                        <div className={styles.confirmInput}>
                                            <label htmlFor="resetMembershipConfirm">
                                                Type <strong>reset</strong> to confirm:
                                            </label>
                                            <input
                                                id="resetMembershipConfirm"
                                                type="text"
                                                value={resetMembershipConfirmText}
                                                onChange={(e) => setResetMembershipConfirmText(e.target.value)}
                                                placeholder="Type 'reset' to confirm"
                                                autoComplete="off"
                                                autoFocus
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className={styles.modalFooter}>
                                <button
                                    type="button"
                                    className={styles.cancelButton}
                                    onClick={handleCloseResetMembershipModal}
                                    disabled={isResettingMembership}
                                >
                                    {totalChanges === 0 ? 'Close' : 'Cancel'}
                                </button>
                                {totalChanges > 0 && (
                                    <button
                                        type="button"
                                        className={styles.confirmResetButton}
                                        onClick={handleResetMembership}
                                        disabled={resetMembershipConfirmText.trim().toLowerCase() !== 'reset' || isResettingMembership}
                                    >
                                        {isResettingMembership ? 'Resetting…' : `Reset batch (${totalChanges} change(s))`}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )
            })()}

            {!isScoreUser && showPurgeModal && purgePreview && (
                <div className={styles.modalOverlay} onClick={handleClosePurgeModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Permanently Delete Removed Clients</h3>
                            <button
                                type="button"
                                className={styles.modalCloseButton}
                                onClick={handleClosePurgeModal}
                                aria-label="Close modal"
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.warningBanner}>
                                <FaTrashAlt className={styles.warningIcon} />
                                <div>
                                    <strong>Warning:</strong> This action cannot be undone. These records will be
                                    deleted from both the funding batch and the original introducer database.
                                </div>
                            </div>
                            {purgePreview.clientCount === 0 ? (
                                <p>
                                    No clients can be permanently deleted right now. Only clients whose batch claims
                                    are ALL marked as removed are eligible.
                                </p>
                            ) : (
                                <>
                                    <p>
                                        You are about to permanently delete{' '}
                                        <strong>{purgePreview.clientCount} client(s)</strong> and{' '}
                                        <strong>{purgePreview.claimCount} claim(s)</strong> marked as removed in
                                        batch <strong>{batchId}</strong>:
                                    </p>
                                    <ul className={styles.deleteList}>
                                        <li>
                                            Funding batch: {purgePreview.funding.funder_clients} client record(s),{' '}
                                            {purgePreview.funding.funder_claims} claim record(s),{' '}
                                            {purgePreview.funding.funder_references} reference record(s)
                                        </li>
                                        {Object.entries(purgePreview.introducerTableCounts).map(([introducer, counts]) => (
                                            <li key={introducer}>
                                                Introducer ({introducer}): {counts.clients} client(s), {counts.leads} lead(s),{' '}
                                                {counts.lead_states} lead state(s), {counts.signature_links} signature link(s),{' '}
                                                {counts.documents} document record(s)
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                            {purgePreview.skippedPartialClients.length > 0 && (
                                <p className={styles.purgeSkippedNote}>
                                    <strong>{purgePreview.skippedPartialClients.length} client(s) will NOT be touched</strong>{' '}
                                    because they still have active claims in this batch.
                                </p>
                            )}
                            {purgePreview.skippedIntroducerClients.length > 0 && (
                                <p className={styles.purgeSkippedNote}>
                                    <strong>{purgePreview.skippedIntroducerClients.length} client(s) will only be deleted from the funding batch</strong>{' '}
                                    (introducer records kept — shared with other batches
                                    {purgePreview.unsupportedIntroducers.length > 0 && (
                                        <> or from an unsupported introducer: {purgePreview.unsupportedIntroducers.join(', ')}</>
                                    )}
                                    ).
                                </p>
                            )}
                            {purgePreview.clientCount > 0 && (
                                <div className={styles.confirmInput}>
                                    <label htmlFor="purgeConfirm">
                                        Type <strong>delete</strong> to confirm:
                                    </label>
                                    <input
                                        id="purgeConfirm"
                                        type="text"
                                        value={purgeConfirmText}
                                        onChange={(e) => setPurgeConfirmText(e.target.value)}
                                        placeholder="Type 'delete' to confirm"
                                        autoComplete="off"
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleClosePurgeModal}
                                disabled={isPurging}
                            >
                                Cancel
                            </button>
                            {purgePreview.clientCount > 0 && (
                                <button
                                    type="button"
                                    className={styles.confirmDeleteButton}
                                    onClick={handlePurgeRemoved}
                                    disabled={purgeConfirmText.toLowerCase() !== 'delete' || isPurging}
                                >
                                    {isPurging ? 'Deleting...' : 'Delete Permanently'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {!isScoreUser && showEmailPurgeModal && emailPurgePreview && (
                <div className={styles.modalOverlay} onClick={handleCloseEmailPurgeModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Remove clients from this batch</h3>
                            <button
                                type="button"
                                className={styles.modalCloseButton}
                                onClick={handleCloseEmailPurgeModal}
                                aria-label="Close modal"
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.warningBanner}>
                                <FaTrashAlt className={styles.warningIcon} />
                                <div>
                                    <strong>Warning:</strong> Matched clients will be deleted from this batch and their
                                    document bundles removed from S3. This cannot be undone. Trinity is not changed.
                                </div>
                            </div>
                            <p>
                                CSV emails: <strong>{emailPurgePreview.requestedEmails ?? 0}</strong>. Matched:{' '}
                                <strong>{emailPurgePreview.matchedClients ?? 0} client(s)</strong> /{' '}
                                <strong>{emailPurgePreview.claimCount ?? 0} claim(s)</strong>
                                {(emailPurgePreview.transferredClaimCount ?? 0) > 0 && (
                                    <>
                                        , including <strong>{emailPurgePreview.transferredClaimCount}</strong> already
                                        API-transferred locally
                                    </>
                                )}
                                .
                            </p>
                            {(emailPurgePreview.sharedFunderClients ?? 0) > 0 && (
                                <p className={styles.purgeSkippedNote}>
                                    {emailPurgePreview.sharedFunderClients} client record(s) also belong to other batches
                                    — those client rows will be kept; only this batch&apos;s claims are removed.
                                </p>
                            )}
                            {(emailPurgePreview.unmatchedCount ?? 0) > 0 && (
                                <p className={styles.purgeSkippedNote}>
                                    {emailPurgePreview.unmatchedCount} email(s) were not found in this batch
                                    {emailPurgePreview.unmatchedEmails?.length
                                        ? `: ${emailPurgePreview.unmatchedEmails.slice(0, 20).join(', ')}${
                                              emailPurgePreview.unmatchedEmails.length > 20 ? ', …' : ''
                                          }`
                                        : ''}
                                    .
                                </p>
                            )}
                            {(emailPurgePreview.matchedClients ?? 0) > 0 && (
                                <div className={styles.confirmInput}>
                                    <label htmlFor="emailPurgeConfirm">
                                        Type <strong>delete</strong> to confirm:
                                    </label>
                                    <input
                                        id="emailPurgeConfirm"
                                        type="text"
                                        value={emailPurgeConfirmText}
                                        onChange={(e) => setEmailPurgeConfirmText(e.target.value)}
                                        placeholder="Type 'delete' to confirm"
                                        autoComplete="off"
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCloseEmailPurgeModal}
                                disabled={isEmailPurging}
                            >
                                Cancel
                            </button>
                            {(emailPurgePreview.matchedClients ?? 0) > 0 && (
                                <button
                                    type="button"
                                    className={styles.confirmDeleteButton}
                                    onClick={handleEmailPurgeConfirm}
                                    disabled={emailPurgeConfirmText.toLowerCase() !== 'delete' || isEmailPurging}
                                >
                                    {isEmailPurging ? 'Deleting...' : 'Delete from batch'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {!isScoreUser && showRelinkIdsModal && relinkIdsPreview && (
                <div className={styles.modalOverlay} onClick={handleCloseRelinkIdsModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Relink introducer Client IDs</h3>
                            <button
                                type="button"
                                className={styles.modalCloseButton}
                                onClick={handleCloseRelinkIdsModal}
                                aria-label="Close modal"
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.warningBanner}>
                                <FaLink className={styles.warningIcon} />
                                <div>
                                    <strong>This rewrites introducer primary keys.</strong> Fairclaim/FFJ{' '}
                                    <code>clients.id</code> values (and related <code>client_id</code> rows) will be
                                    set to the original funding-monitor <code>introducer_client_id</code>. Funding-monitor
                                    is not changed.
                                </div>
                            </div>
                            <p>
                                CSV emails: <strong>{relinkIdsPreview.requestedEmails ?? 0}</strong>. Will relink:{' '}
                                <strong>{relinkIdsPreview.willRelinkCount ?? 0}</strong>. Already linked:{' '}
                                <strong>{relinkIdsPreview.alreadyLinkedCount ?? 0}</strong>. Skipped:{' '}
                                <strong>{relinkIdsPreview.skippedCount ?? 0}</strong>.
                            </p>
                            {(relinkIdsPreview.perEmail || []).filter((r) => r.status === 'will_relink').length > 0 && (
                                <div className={styles.sectionDescription}>
                                    {relinkIdsPreview.perEmail
                                        ?.filter((r) => r.status === 'will_relink')
                                        .slice(0, 25)
                                        .map((r) => (
                                            <div key={r.email} style={{ wordBreak: 'break-all' }}>
                                                {r.email}
                                                {r.name ? ` (${r.name})` : ''}: {r.currentIntroducerClientId} →{' '}
                                                {r.targetIntroducerClientId}
                                                {r.introducer ? ` [${r.introducer}]` : ''}
                                            </div>
                                        ))}
                                    {(relinkIdsPreview.willRelinkCount ?? 0) > 25 ? <div>…</div> : null}
                                </div>
                            )}
                            {(relinkIdsPreview.skippedCount ?? 0) > 0 && (
                                <p className={styles.purgeSkippedNote}>
                                    {(relinkIdsPreview.perEmail || [])
                                        .filter((r) => r.status !== 'will_relink' && r.status !== 'already_linked')
                                        .slice(0, 15)
                                        .map((r) => `${r.email} (${RELINK_STATUS_LABELS[r.status] || r.status})`)
                                        .join('; ')}
                                    {(relinkIdsPreview.skippedCount ?? 0) > 15 ? ' …' : ''}
                                </p>
                            )}
                            {(relinkIdsPreview.willRelinkCount ?? 0) > 0 && (
                                <div className={styles.confirmInput}>
                                    <label htmlFor="relinkIdsConfirm">
                                        Type <strong>RELINK</strong> to confirm:
                                    </label>
                                    <input
                                        id="relinkIdsConfirm"
                                        type="text"
                                        value={relinkIdsConfirmText}
                                        onChange={(e) => setRelinkIdsConfirmText(e.target.value)}
                                        placeholder="Type 'RELINK' to confirm"
                                        autoComplete="off"
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCloseRelinkIdsModal}
                                disabled={isRelinkingIds}
                            >
                                Cancel
                            </button>
                            {(relinkIdsPreview.willRelinkCount ?? 0) > 0 && (
                                <button
                                    type="button"
                                    className={styles.auditCheckButton}
                                    onClick={handleRelinkIdsConfirm}
                                    disabled={relinkIdsConfirmText.toLowerCase() !== 'relink' || isRelinkingIds}
                                >
                                    {isRelinkingIds ? 'Relinking...' : 'Relink Client IDs'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
